import React, { useState, useMemo, useRef, useEffect } from "react";
import appData from "../data.json";
import {
  ThemeProvider, createTheme, CssBaseline, Box, Paper, Typography, Button,
  IconButton, AppBar, Toolbar, Tabs, Tab, Grid, Card, CardContent, Chip,
  TextField, Dialog, DialogContent, Drawer, Badge, Avatar, Divider,
  InputAdornment, MenuItem, Select, FormControl, Table, TableHead, TableRow,
  TableCell, TableBody, LinearProgress, Stack, Fab, Collapse,
  ToggleButtonGroup, ToggleButton, Container, alpha, Zoom,
  Tooltip as MTooltip, Snackbar, Alert, Popover, Switch, FormControlLabel,
} from "@mui/material";
import {
  LocalFireDepartment, RestaurantMenu, ShoppingCart, Add, Remove,
  Close, Search, Phone, Star, CalendarMonth, TrendingUp, TrendingDown,
  Inventory2, WarningAmber, AttachMoney, Percent, Download, Send,
  ChevronRight, ChevronLeft, Logout, Settings, AdminPanelSettings,
  Storefront as StorefrontIcon, SoupKitchen, Groups, AutoAwesome,
  CheckCircle, BarChart as BarChartIcon, ShowChart, AccessTime, ArrowBack,
  SmartToy, Image as ImageIcon, LocationOn,
} from "@mui/icons-material";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

/* ============================================================================
   SERVER-BASED PERSISTENCE.
   ========================================================================== */

const DATA_SERVER_URL =
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname)
    ? "http://localhost:4000"
    : "/api";

function resolveImg(src) {
  if (!src) return src;
  if (src.startsWith("/images/")) return `${DATA_SERVER_URL}${src}`;
  return src;
}

async function apiGet(path) {
  const res = await fetch(`${DATA_SERVER_URL}${path}`);
  if (!res.ok) throw new Error(`GET ${path} failed (${res.status})`);
  return res.json();
}
async function apiPost(path, body) {
  let res;
  try {
    res = await fetch(`${DATA_SERVER_URL}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (networkErr) {
    throw new Error(`Couldn't reach data-server.cjs at "${DATA_SERVER_URL}" — is it running? (${networkErr.message})`);
  }
  if (!res.ok) {
    let detail = "";
    try { detail = (await res.json()).error || ""; } catch { /* body wasn't JSON */ }
    throw new Error(`Save failed (${res.status})${detail ? `: ${detail}` : ""}`);
  }
  return res.json();
}

/* ------------------------------- THEME ------------------------------------ */
const FONT_URL = "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap";

const PALETTE = {
  bg: "#FAF8F4", paper: "#FFFFFF", ink: "#241F1A", inkDim: "#756E63",
  border: "#E9E2D6", garnet: "#7E1F31", garnetLight: "#9E2C40",
  brass: "#B5813A", brassLight: "#D8B372", sage: "#4F7A5C", alert: "#C1440E",
};

const theme = createTheme({
  palette: {
    background: { default: PALETTE.bg, paper: PALETTE.paper },
    primary: { main: PALETTE.garnet, light: PALETTE.garnetLight },
    secondary: { main: PALETTE.brass, light: PALETTE.brassLight },
    success: { main: PALETTE.sage },
    error: { main: PALETTE.alert },
    text: { primary: PALETTE.ink, secondary: PALETTE.inkDim },
    divider: PALETTE.border,
  },
  typography: {
    fontFamily: "Inter, sans-serif",
    h1: { fontFamily: "Fraunces, serif" }, h2: { fontFamily: "Fraunces, serif" },
    h3: { fontFamily: "Fraunces, serif" }, h4: { fontFamily: "Fraunces, serif" },
    h5: { fontFamily: "Fraunces, serif" }, h6: { fontFamily: "Fraunces, serif" },
  },
  shape: { borderRadius: 14 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
    MuiButton: { styleOverrides: { root: { textTransform: "none", fontWeight: 600, borderRadius: 999 } } },
    MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
    MuiTabScrollButton: {
      styleOverrides: {
        root: {
  width: "40px",
  height: "40px",
  opacity: 0.7,
  color: "#fff",
  background: "#7e1f31",
  borderRadius: "24px",
}
  },}}
});

const PIE_COLORS = [PALETTE.brass, PALETTE.garnetLight, PALETTE.alert, PALETTE.sage, "#4C6B8A", PALETTE.brassLight];

const IMAGES = {
  banners: [
    { img: "https://static.vecteezy.com/system/resources/previews/015/763/393/large_2x/steak-gourmet-fresh-delicious-juicy-steak-selected-focus-in-poster-format-free-photo.jpg", title: "Fire-Seared Prime Cuts", subtitle: "Dry-aged, char-finished, plated to order" },
    { img: "https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=1600&q=80", title: "Fire. Marble. Minnesota.", subtitle: "A steakhouse built around the grill" },
    { img: "https://images.unsplash.com/photo-1600891964599-f61ba0e24092?auto=format&fit=crop&w=1600&q=80", title: "Every Cut, Perfected", subtitle: "From ribeye to tomahawk, cooked exactly to order" },
  ],
  interior: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1200&q=80",
  chef: "https://images.unsplash.com/photo-1577219491135-ce391730fb2c?auto=format&fit=crop&w=1200&q=80",
  wine: "https://picsum.photos/id/431/1200/800",
};

const lockedFeed = (keywords, count) =>
  Array.from({ length: count }, (_, i) => `https://loremflickr.com/800/600/${keywords}/all?lock=${i + 1}`);

const IMAGE_POOL_BY_CAT = {
  Steaks: [
    "https://static.vecteezy.com/system/resources/previews/015/763/393/large_2x/steak-gourmet-fresh-delicious-juicy-steak-selected-focus-in-poster-format-free-photo.jpg",
    "https://images.unsplash.com/photo-1600891964599-f61ba0e24092?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1546833999-b9f581a1996d?auto=format&fit=crop&w=800&q=80",
    ...lockedFeed("steak,grill,beef", 24),
  ],
  Starters: [
    "https://images.unsplash.com/photo-1533630654593-b73e5f8ee42e?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1541014741259-de529411b96a?auto=format&fit=crop&w=800&q=80",
    ...lockedFeed("appetizer,shrimp,oyster", 16),
  ],
  Sides: [
    "https://images.unsplash.com/photo-1518013431117-eb1465fa5752?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1476124369491-e7addf5db371?auto=format&fit=crop&w=800&q=80",
    ...lockedFeed("sidedish,potato,vegetable", 19),
  ],
  Drinks: [
    "https://images.unsplash.com/photo-1470337458703-46ad1756a187?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?auto=format&fit=crop&w=800&q=80",
    ...lockedFeed("cocktail,wine,whiskey", 22),
  ],
  Desserts: [
    "https://images.unsplash.com/photo-1606313564200-e75d5e30476c?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1571877227200-a0d98ea607e9?auto=format&fit=crop&w=800&q=80",
    ...lockedFeed("dessert,cake,chocolate", 13),
  ],
};

/* ------------------------------- HELPERS ---------------------------------- */
const fmtUSD = (n) => `$${n.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const fmtUSD2 = (n) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
function seeded(seed) {
  let s = seed % 2147483647; if (s <= 0) s += 2147483646;
  return () => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };
}

const CATS = ["Steaks", "Starters", "Sides", "Drinks", "Desserts"];

/* ============================================================================
   NEW — COMBO SYSTEM
   Overflow beyond 16 items per category is permanently pulled off the regular
   grid and bundled into named recurring combos (Wednesday Combo, Sunday Family
   Combo, Dinner-for-Two Combo, etc). Combos get a 15% bundle discount off the
   sum of individual prices, and are shown in their own showcase section.
   ========================================================================== */
const COMBO_NAMES = ["Wednesday Combo", "Sunday Family Combo", "Dinner-for-Two Combo", "Weekend Grill Combo", "Chef's Sampler Combo"];
const MENU_ITEMS_SHOWN_PER_CAT = 16;

function buildComboOverflow(menu, perCatShown = MENU_ITEMS_SHOWN_PER_CAT) {
  const shownMenu = [];
  const overflow = [];
  CATS.forEach((cat) => {
    const items = menu.filter((m) => m.cat === cat);
    shownMenu.push(...items.slice(0, perCatShown));
    overflow.push(...items.slice(perCatShown));
  });
  const combos = COMBO_NAMES.map((name) => ({ name, items: [] }));
  overflow.forEach((item, i) => combos[i % combos.length].items.push(item));
  return { shownMenu, combos: combos.filter((c) => c.items.length > 0) };
}
function comboFullPrice(items) { return items.reduce((s, i) => s + i.price, 0); }
function comboPrice(items) { return +(comboFullPrice(items) * 0.85).toFixed(2); } // 15% bundle discount

/* ============================================================================
   NEW — LOYALTY PROGRAM: monthly points tiers + couples' trip special
   ========================================================================== */

// Per-purchase point multiplier, based on that customer's cumulative spend
// THIS MONTH (including the order being placed).
function monthlyPointsTier(monthlySpendSoFar) {
  if (monthlySpendSoFar >= 100) return { label: "Gold", mult: 3 };
  if (monthlySpendSoFar >= 50) return { label: "Silver", mult: 2 };
  return { label: "Bronze", mult: 1 };
}
function pointsForPurchase(orderTotal, monthlySpendBeforeThisOrder = 0, rules = DEFAULT_LOYALTY_RULES, when = new Date()) {
  const tier = monthlyPointsTier(monthlySpendBeforeThisOrder + orderTotal);
  let points = orderTotal * tier.mult;
  if (rules.dayBasedBonus?.enabled && when.toLocaleDateString("en-US",{weekday:"long"}) === rules.dayBasedBonus.day) points *= rules.dayBasedBonus.mult;
  if (rules.seasonBonus?.enabled) points *= rules.seasonBonus.mult;
  if (rules.variableRewards?.enabled) {
    const { min, max } = rules.variableRewards;
    points = Math.round(min + Math.random() * (max - min));
  }
  return { points: Math.round(points), tier: tier.label, mult: tier.mult };
}

// Married-couple trip special: customers with an "anniversary" on file are the
// couples pool. Each calendar month a random ~15% of them are selected for a
// random 0-40% off a trip ticket. Deterministic per month (same seed for the
// whole month) so the selection doesn't reshuffle on every render/reload.
function coupleTripWinners(customers) {
  const monthKey = new Date().toISOString().slice(0, 7); // e.g. "2026-08"
  const rng = seeded(monthKey.split("").reduce((a, c) => a + c.charCodeAt(0), 0) + 17);
  return customers
    .filter((c) => Boolean(c.anniversary))
    .filter(() => rng() < 0.15)
    .map((c) => ({ ...c, discountPct: Math.floor(rng() * 41) })); // 0-40%
}

/* --------------------------------- MENU ------------------------------------ */
const MENU_SEED_RAW = [
  { id: "m1", cat: "Steaks", name: "Prime Ribeye 16oz", desc: "Dry-aged 28 days, char-seared over oak", cost: 14.2, margin: 68 },
  { id: "m2", cat: "Steaks", name: "Filet Mignon 8oz", desc: "Butter-basted, cracked black pepper", cost: 12.8, margin: 72 },
  { id: "m3", cat: "Steaks", name: "NY Strip 14oz", desc: "Hickory smoked finish", cost: 11.5, margin: 65 },
  { id: "m4", cat: "Steaks", name: "Tomahawk 32oz", desc: "For the table, bone-in", cost: 28.0, margin: 55 },
  { id: "m5", cat: "Steaks", name: "Porterhouse 24oz", desc: "Two cuts, one plate", cost: 21.4, margin: 58 },
  { id: "m16", cat: "Steaks", name: "Bone-In Ribeye 20oz", desc: "Cowboy cut, extra marbling", cost: 19.5, margin: 60 },
  { id: "m17", cat: "Steaks", name: "Chateaubriand for Two", desc: "Center-cut tenderloin, carved tableside", cost: 34.0, margin: 62 },
  { id: "m26", cat: "Steaks", name: "Wagyu Ribeye 10oz", desc: "A5 marbled, seared in butter", cost: 42.0, margin: 50 },
  { id: "m27", cat: "Steaks", name: "Skirt Steak Fajita Cut", desc: "Citrus marinade, char-grilled", cost: 9.8, margin: 78 },
  { id: "m9", cat: "Starters", name: "Shrimp Cocktail", desc: "Chilled gulf shrimp, spiced cocktail sauce", cost: 5.4, margin: 95 },
  { id: "m10", cat: "Starters", name: "Wagyu Sliders", desc: "Trio, brioche bun, smoked cheddar", cost: 6.8, margin: 88 },
  { id: "m18", cat: "Starters", name: "Oysters on the Half Shell", desc: "Half dozen, mignonette", cost: 8.2, margin: 90 },
  { id: "m19", cat: "Starters", name: "Burrata Caprese", desc: "Heirloom tomato, basil oil", cost: 4.6, margin: 105 },
  { id: "m28", cat: "Starters", name: "Seared Ahi Tuna", desc: "Sesame crust, wasabi aioli", cost: 7.4, margin: 92 },
  { id: "m29", cat: "Starters", name: "French Onion Soup", desc: "Gruyère crouton, sherry broth", cost: 3.3, margin: 120 },
  { id: "m6", cat: "Sides", name: "Truffle Mash", desc: "Yukon gold, shaved black truffle", cost: 3.1, margin: 140 },
  { id: "m7", cat: "Sides", name: "Grilled Asparagus", desc: "Lemon zest, flaked sea salt", cost: 2.4, margin: 150 },
  { id: "m8", cat: "Sides", name: "Lobster Mac & Cheese", desc: "Maine lobster, aged gruyère", cost: 6.2, margin: 110 },
  { id: "m20", cat: "Sides", name: "Creamed Spinach", desc: "Nutmeg, aged parmesan", cost: 2.7, margin: 135 },
  { id: "m21", cat: "Sides", name: "Loaded Baked Potato", desc: "Bacon, chives, sour cream", cost: 2.2, margin: 145 },
  { id: "m30", cat: "Sides", name: "Grilled Brussels Sprouts", desc: "Balsamic glaze, pancetta", cost: 2.9, margin: 130 },
  { id: "m31", cat: "Sides", name: "Onion Rings Tower", desc: "Buttermilk-battered, spiced ketchup", cost: 2.1, margin: 155 },
  { id: "m11", cat: "Drinks", name: "Cabernet Sauvignon", desc: "Glass pour, Napa Valley", cost: 4.0, margin: 175 },
  { id: "m12", cat: "Drinks", name: "Old Fashioned", desc: "Bourbon, bitters, orange twist", cost: 3.2, margin: 190 },
  { id: "m13", cat: "Drinks", name: "Single Malt Scotch", desc: "12-year Highland", cost: 6.5, margin: 160 },
  { id: "m14", cat: "Drinks", name: "House Lager", desc: "Local Minnesota brew", cost: 1.8, margin: 130 },
  { id: "m22", cat: "Drinks", name: "Manhattan", desc: "Rye whiskey, sweet vermouth, cherry", cost: 3.6, margin: 180 },
  { id: "m23", cat: "Drinks", name: "Espresso Martini", desc: "Vodka, cold brew, cacao", cost: 3.9, margin: 170 },
  { id: "m32", cat: "Drinks", name: "Chardonnay", desc: "Glass pour, buttery oak finish", cost: 3.8, margin: 172 },
  { id: "m33", cat: "Drinks", name: "Smoked Rosemary Negroni", desc: "Gin, campari, hickory smoke", cost: 4.2, margin: 185 },
  { id: "m15", cat: "Desserts", name: "Chocolate Lava Cake", desc: "Vanilla bean ice cream", cost: 2.9, margin: 145 },
  { id: "m24", cat: "Desserts", name: "New York Cheesecake", desc: "Graham crust, berry compote", cost: 3.1, margin: 140 },
  { id: "m25", cat: "Desserts", name: "Crème Brûlée", desc: "Madagascar vanilla, torched sugar", cost: 2.6, margin: 150 },
  { id: "m34", cat: "Desserts", name: "Bourbon Pecan Pie", desc: "Warm, with whipped cream", cost: 2.8, margin: 148 },
  { id: "m35", cat: "Desserts", name: "Tiramisu", desc: "Espresso-soaked, mascarpone", cost: 3.0, margin: 142 },
];
const __catCursor = {};
const MENU_SEED = MENU_SEED_RAW.map((m) => {
  __catCursor[m.cat] = __catCursor[m.cat] || 0;
  const img = IMAGE_POOL_BY_CAT[m.cat].slice(__catCursor[m.cat], __catCursor[m.cat] + 3);
  __catCursor[m.cat] += 3;
  return { ...m, price: +(m.cost * (1 + m.margin / 100)).toFixed(2), img };
});

/* ------------------------------ CUSTOMERS ---------------------------------- */
const FIRST = ["Olivia","Liam","Emma","Noah","Ava","Ethan","Sophia","Mason","Isabella","Logan","Mia","Lucas","Amelia","James","Harper","Benjamin","Evelyn","Henry","Charlotte","Alexander","Grace","Sebastian","Chloe","Jack","Zoey"];
const LAST = ["Carter","Bennett","Hughes","Ramirez","Foster","Coleman","Whitfield","Larsen","Sullivan","Nguyen","Patel","O'Connor","Marsh","Kowalski","Dunning","Reyes","Abernathy","Voss","Lindqvist","Mercer"];
const rngCust = seeded(4242);
function genCustomers(n, menu) {
  if (!Array.isArray(menu) || menu.length === 0) return [];
  const arr = [];
  for (let i = 0; i < n; i++) {
    const fn = FIRST[Math.floor(rngCust() * FIRST.length)];
    const ln = LAST[Math.floor(rngCust() * LAST.length)];
    const visits = 2 + Math.floor(rngCust() * 14);
    const memberSince = 1 + Math.floor(rngCust() * 20);
    const orders = [];
    for (let v = 0; v < visits; v++) {
      const daysAgo = Math.floor(rngCust() * 400);
      const itemCount = 2 + Math.floor(rngCust() * 3);
      const items = Array.from({ length: itemCount }).map(() => menu[Math.floor(rngCust() * menu.length)]).filter(Boolean);
      const total = items.reduce((s, it) => s + (it?.price || 0), 0);
      const d = new Date(); d.setDate(d.getDate() - daysAgo);
      orders.push({ date: d, items, total: +total.toFixed(2) });
    }
    orders.sort((a, b) => b.date - a.date);
    const freq = {};
    orders.forEach((o) => o.items.forEach((it) => { freq[it.id] = (freq[it.id] || 0) + 1; }));
    const favId = Object.entries(freq).sort((a, b) => b[1] - a[1])[0]?.[0];

    const bMonth = 1 + Math.floor(rngCust() * 12);
    const bDay = 1 + Math.floor(rngCust() * 28);
    const birthday = `${String(bMonth).padStart(2,"0")}-${String(bDay).padStart(2,"0")}`;
    const hasAnniversary = rngCust() > 0.4;
    const anniversary = hasAnniversary
      ? `${String(1 + Math.floor(rngCust() * 12)).padStart(2,"0")}-${String(1 + Math.floor(rngCust() * 28)).padStart(2,"0")}`
      : null;

    const now = new Date();
    const thisMonthSpend = +orders
      .filter((o) => o.date.getMonth() === now.getMonth() && o.date.getFullYear() === now.getFullYear())
      .reduce((s, o) => s + o.total, 0)
      .toFixed(2);

    arr.push({
      id: `c${i + 1}`, name: `${fn} ${ln}`, phone: `612-555-${(1000 + i * 7 + 3).toString().slice(-4)}`,
      visits, memberSince, orders, favorite: favId ? menu.find(m=>m.id===favId)?.name : "—",
      totalSpend: +orders.reduce((s, o) => s + o.total, 0).toFixed(2),
      thisMonthSpend,
      tier: loyaltyTier(+orders.reduce((s, o) => s + o.total, 0).toFixed(2)).label,
      pointsTier: monthlyPointsTier(thisMonthSpend).label,
      loyaltyPoints: Math.round(orders.reduce((s, o) => s + o.total, 0) * 1.1),
      birthday, anniversary,
    });
  }
  return arr;
}
function upcomingCelebrations(customers, daysAhead = 30) {
  if (!Array.isArray(customers)) return [];
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const results = [];
  customers.forEach((c) => {
    [["Birthday", c.birthday], ["Anniversary", c.anniversary]].forEach(([type, mmdd]) => {
      if (!mmdd) return;
      const [mm, dd] = mmdd.split("-").map(Number);
      let next = new Date(today.getFullYear(), mm - 1, dd);
      if (next < today) next.setFullYear(today.getFullYear() + 1);
      const daysUntil = Math.round((next - today) / 86400000);
      if (daysUntil <= daysAhead) {
        results.push({ id: `${c.id}-${type}`, type, name: c.name, phone: c.phone, visits: c.visits, totalSpend: c.totalSpend, daysUntil });
      }
    });
  });
  return results.sort((a, b) => a.daysUntil - b.daysUntil);
}
/* --------------------------------- STAFF ------------------------------------ */
const STAFF = [
  { id: "owner1", role: "Owner", name: "Richard Lindey", phone: "612-555-0001", title: "Owner / Proprietor", icon: AdminPanelSettings },
  { id: "admin1", role: "Admin", name: "Dana Whitmore", phone: "612-555-0002", title: "General Manager", icon: Settings },
  { id: "kitchen1", role: "Kitchen", name: "Marco Alvez", phone: "612-555-0003", title: "Executive Chef", icon: SoupKitchen },
];
const WAITER = { id: "waiter1", role: "User", name: "Front of House", phone: "612-555-0099", title: "Waiter Terminal" };
const SERVANTS = [
  { id: "servant1", role: "Servant", name: "Alex Rivera", phone: "612-555-0201", title: "Servant" },
  { id: "servant2", role: "Servant", name: "Priya Nair", phone: "612-555-0202", title: "Servant" },
  { id: "servant3", role: "Servant", name: "Jordan Kim", phone: "612-555-0203", title: "Servant" },
  { id: "servant4", role: "Servant", name: "Maria Gomez", phone: "612-555-0204", title: "Servant" },
  { id: "servant5", role: "Servant", name: "Devon Clarke", phone: "612-555-0205", title: "Servant" },
];

/* --------------------------- SALES TIME SERIES ------------------------------ */
const rngSales = seeded(99);
function buildDailySales(days) {
  const out = []; const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today); d.setDate(d.getDate() - i);
    const dow = d.getDay();
    const weekendBoost = (dow === 5 || dow === 6) ? 1.45 : dow === 0 ? 1.15 : 1;
    const base = 3200 * weekendBoost;
    const noise = (rngSales() - 0.5) * 900;
    const revenue = Math.max(900, Math.round(base + noise));
    const orders = Math.round(revenue / (58 + rngSales() * 12));
    out.push({
      date: d.toISOString().slice(0, 10),
      label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      dow: d.toLocaleDateString("en-US", { weekday: "short" }),
      revenue, orders, profit: Math.round(revenue * (0.22 + (rngSales() - 0.5) * 0.06)),
    });
  }
  return out;
}
const DAILY_SALES = buildDailySales(90);
function hourlyForDay(dayRevenue, seedOffset) {
  const rng = seeded(dayRevenue * 13 + seedOffset);
  const hours = [11,12,13,14,15,16,17,18,19,20,21,22];
  const weights = [0.4,0.9,1.1,0.5,0.3,0.35,0.7,1.3,1.6,1.4,0.9,0.4];
  const total = weights.reduce((a,b)=>a+b,0);
  return hours.map((h, idx) => ({ hour: h > 12 ? `${h-12}PM` : h === 12 ? "12PM" : `${h}AM`, sales: Math.round((dayRevenue * (weights[idx]/total)) * (0.9 + rng()*0.2)) }));
}
function itemSalesFor(menu) {
  return menu.map((m) => {
    const rng = seeded(m.name.length * 777);
    const units = 40 + Math.floor(rng() * 260);
    return { name: m.name, cat: m.cat, units, revenue: +(units * m.price).toFixed(0), profit: +(units * (m.price - m.cost)).toFixed(0) };
  }).sort((a,b) => b.revenue - a.revenue);
}

function itemsSoldForDay(day, itemSales) {
  if (!itemSales.length) return [];
  const rng = seeded(day.revenue * 31 + day.label.length * 13);
  const picked = [...itemSales].sort(() => rng() - 0.5).slice(0, Math.min(7, itemSales.length));
  const totalWeight = picked.reduce((s, i) => s + i.revenue, 0) || 1;
  return picked.map((i) => {
    const revenue = +(day.revenue * (i.revenue / totalWeight)).toFixed(2);
    const avgPrice = i.revenue / Math.max(1, i.units);
    const qty = Math.max(1, Math.round(revenue / Math.max(1, avgPrice)));
    return { name: i.name, cat: i.cat, qty, revenue };
  }).sort((a, b) => b.revenue - a.revenue);
}

/* -------------------------------- INVENTORY --------------------------------- */
const INVENTORY_SEED = [
  { id: "i1", name: "Ribeye Cut (lb)", qty: 42, threshold: 30, unit: "lb", par: 120 },
  { id: "i2", name: "Filet Mignon (lb)", qty: 18, threshold: 25, unit: "lb", par: 90 },
  { id: "i3", name: "NY Strip (lb)", qty: 55, threshold: 30, unit: "lb", par: 100 },
  { id: "i4", name: "Tomahawk (ea)", qty: 6, threshold: 10, unit: "ea", par: 24 },
  { id: "i5", name: "Lobster Tail (ea)", qty: 33, threshold: 20, unit: "ea", par: 60 },
  { id: "i6", name: "Truffle Oil (bottle)", qty: 4, threshold: 5, unit: "btl", par: 12 },
  { id: "i7", name: "Cabernet Sauvignon (bottle)", qty: 61, threshold: 24, unit: "btl", par: 80 },
  { id: "i8", name: "Single Malt Scotch (bottle)", qty: 9, threshold: 10, unit: "btl", par: 20 },
  { id: "i9", name: "Asparagus (lb)", qty: 22, threshold: 15, unit: "lb", par: 40 },
  { id: "i10", name: "Russet Potatoes (lb)", qty: 88, threshold: 40, unit: "lb", par: 150 },
  { id: "i11", name: "Wagyu Ribeye (lb)", qty: 12, threshold: 15, unit: "lb", par: 30 },
  { id: "i12", name: "Fresh Oysters (dozen)", qty: 7, threshold: 8, unit: "dz", par: 20 },
  { id: "i13", name: "Mascarpone Cheese (lb)", qty: 14, threshold: 10, unit: "lb", par: 25 },
  { id: "i14", name: "Chicken Breast (kg)", qty: 38, threshold: 15, unit: "kg", par: 60 },
];

const BARCODE_SEED = [1, 2, 3, 4, 5].map((qty) => ({
  id: `bc-seed-${qty}`, code: `CHK-${qty}KG-${(1000 + qty * 37).toString(36).toUpperCase()}`,
  itemId: "i14", itemName: "Chicken Breast (kg)", qty, unit: "kg", createdAt: daysAgoLabel(4),
}));

function daysAgoISO(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }
function daysAgoLabel(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
const STOCK_LEDGER_SEED = [
  { id: "l1", iso: daysAgoISO(6), date: daysAgoLabel(6), time: "9:10 AM", item: "Ribeye Cut (lb)", type: "received", qty: 60, note: "Weekly delivery — Midwest Meats" },
  { id: "l2", iso: daysAgoISO(6), date: daysAgoLabel(6), time: "9:14 AM", item: "Filet Mignon (lb)", type: "received", qty: 40, note: "Weekly delivery — Midwest Meats" },
  { id: "l3", iso: daysAgoISO(5), date: daysAgoLabel(5), time: "6:32 PM", item: "Ribeye Cut (lb)", type: "used", qty: 22, note: "Dinner service" },
  { id: "l4", iso: daysAgoISO(4), date: daysAgoLabel(4), time: "6:48 PM", item: "Filet Mignon (lb)", type: "used", qty: 14, note: "Dinner service" },
  { id: "l5", iso: daysAgoISO(3), date: daysAgoLabel(3), time: "10:02 AM", item: "Tomahawk (ea)", type: "received", qty: 12, note: "Specialty cuts order" },
  { id: "l6", iso: daysAgoISO(3), date: daysAgoLabel(3), time: "7:05 PM", item: "Ribeye Cut (lb)", type: "used", qty: 19, note: "Dinner service" },
  { id: "l7", iso: daysAgoISO(2), date: daysAgoLabel(2), time: "11:20 AM", item: "Single Malt Scotch (bottle)", type: "received", qty: 6, note: "Bar restock" },
  { id: "l8", iso: daysAgoISO(2), date: daysAgoLabel(2), time: "7:40 PM", item: "Filet Mignon (lb)", type: "used", qty: 16, note: "Dinner service" },
  { id: "l9", iso: daysAgoISO(1), date: daysAgoLabel(1), time: "8:12 PM", item: "Tomahawk (ea)", type: "used", qty: 6, note: "Weekend service" },
  { id: "l10", iso: daysAgoISO(1), date: daysAgoLabel(1), time: "8:15 PM", item: "Ribeye Cut (lb)", type: "used", qty: 24, note: "Weekend service" },
];

const WASTAGE_SEED = [
  { item: "Filet Mignon 8oz", cost: 12.8, prepared: 60, sold: 47 },
  { item: "Tomahawk 32oz", cost: 28.0, prepared: 20, sold: 14 },
  { item: "Truffle Mash", cost: 3.1, prepared: 150, sold: 121 },
  { item: "Lobster Mac & Cheese", cost: 6.2, prepared: 80, sold: 66 },
  { item: "NY Strip 14oz", cost: 11.5, prepared: 90, sold: 79 },
  { item: "Shrimp Cocktail", cost: 5.4, prepared: 70, sold: 58 },
  { item: "Prime Ribeye 16oz", cost: 14.2, prepared: 100, sold: 88 },
].map((it, i, arr) => {
  const n = arr.length - 1 - i;
  const wastedUnits = Math.max(0, it.prepared - it.sold);
  return { id: `w${i}`, iso: daysAgoISO(n), date: daysAgoLabel(n), item: it.item, prepared: it.prepared, sold: it.sold, wastedUnits, cost: +(wastedUnits * it.cost).toFixed(1) };
});

/* ------------------------------ RESERVATIONS -------------------------------- */
const TABLES = [{ id: "T1", seats: 2 },{ id: "T2", seats: 2 },{ id: "T3", seats: 4 },{ id: "T4", seats: 4 },{ id: "T5", seats: 4 },{ id: "T6", seats: 6 },{ id: "T7", seats: 6 },{ id: "T8", seats: 8 }];

/* ------------------------------ SHARED ORDER QUEUE (Waiter <-> Kitchen) -------------------- */
const KITCHEN_SLA_MINUTES = 20;
function orderUrgency(minAgo) {
  const remaining = KITCHEN_SLA_MINUTES - minAgo;
  const urgency = remaining <= 0 ? "overdue" : remaining <= 5 ? "hurry" : "normal";
  return { remaining, urgency };
}
function withUrgency(o) {
  const minAgo = Math.max(0, Math.floor((Date.now() - (o.createdAt || Date.now())) / 60000));
  return { ...o, minAgo, ...orderUrgency(minAgo) };
}
const STATUS_COLOR = { queued: PALETTE.inkDim, cooking: PALETTE.alert, ready: PALETTE.sage, served: PALETTE.border };
const URGENCY_COLOR = { overdue: PALETTE.alert, hurry: PALETTE.brass, normal: PALETTE.border };

/* =============================== SHARED UI BITS ============================== */

function useOnlineStatus() {
  const forcedOffline = typeof window !== "undefined" && window.location.search.includes("forceOffline=1");
  const [online, setOnline] = useState(() => (typeof navigator !== "undefined" ? navigator.onLine : true));
  useEffect(() => {
    if (forcedOffline) return;
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, [forcedOffline]);
  return forcedOffline ? false : online;
}

function SmartImg({ src, fallback, alt = "", sx, component = "img" }) {
  const online = useOnlineStatus();
  const triedFallback = useRef(false);
  const resolvedFallback = resolveImg(fallback);
  const primary = online ? (src || resolvedFallback) : (resolvedFallback || src);
  const secondary = online ? resolvedFallback : src;
  const handleError = (e) => {
    if (triedFallback.current) return;
    triedFallback.current = true;
    if (secondary && secondary !== e.target.src) e.target.src = secondary;
  };
  return <Box component={component} src={primary} alt={alt} sx={sx} onError={handleError} />;
}

function Reveal({ children, delay = 0, effect = "up" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const hiddenTransform = effect === "up" ? "translateY(28px)" : effect === "scale" ? "scale(0.92)" : "translateY(0)";
  return (
    <Box ref={ref} sx={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0) scale(1)" : hiddenTransform,
      transition: `opacity 0.7s cubic-bezier(.2,.8,.2,1) ${delay}ms, transform 0.7s cubic-bezier(.2,.8,.2,1) ${delay}ms`,
    }}>
      {children}
    </Box>
  );
}

function GoldRule() {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ my: 2 }}>
      <Box sx={{ flex: 1, height: "1px", background: `linear-gradient(90deg, transparent, ${PALETTE.border})` }} />
      <LocalFireDepartment sx={{ fontSize: 14, color: PALETTE.brass }} />
      <Box sx={{ flex: 1, height: "1px", background: `linear-gradient(90deg, ${PALETTE.border}, transparent)` }} />
    </Stack>
  );
}
function SectionHead({ eyebrow, title, action }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" rowGap={1} sx={{ mb: 2.5 }}>
      <Box>
        <Typography sx={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: PALETTE.brass, fontWeight: 700, mb: 0.5 }}>{eyebrow}</Typography>
        <Typography variant="h5" sx={{ color: PALETTE.ink, fontWeight: 600 }}>{title}</Typography>
      </Box>
      {action}
    </Stack>
  );
}
function KpiCard({ icon: Icon, label, value, delta, positive = true }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.25, borderColor: PALETTE.border, borderRadius: 3, height: "100%" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", color: PALETTE.inkDim, fontWeight: 600 }}>{label}</Typography>
        <Icon sx={{ fontSize: 18, color: PALETTE.brass }} />
      </Stack>
      <Typography variant="h4" sx={{ fontWeight: 700, color: PALETTE.ink, fontSize: 26 }}>{value}</Typography>
      {delta && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.75, color: positive ? PALETTE.sage : PALETTE.alert }}>
          {positive ? <TrendingUp sx={{ fontSize: 14 }} /> : <TrendingDown sx={{ fontSize: 14 }} />}
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{delta}</Typography>
        </Stack>
      )}
    </Paper>
  );
}
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ px: 1.5, py: 1, border: `1px solid ${PALETTE.brass}`, borderRadius: 2 }}>
      <Typography sx={{ fontSize: 11, color: PALETTE.brass, fontFamily: "JetBrains Mono, monospace", mb: 0.5 }}>{label}</Typography>
      {payload.map((p, i) => (
        <Stack key={i} direction="row" justifyContent="space-between" spacing={2}>
          <Typography sx={{ fontSize: 11, color: PALETTE.inkDim }}>{p.name}</Typography>
          <Typography sx={{ fontSize: 11, color: PALETTE.ink, fontFamily: "JetBrains Mono, monospace", fontWeight: 600 }}>{typeof p.value === "number" ? fmtUSD(p.value) : p.value}</Typography>
        </Stack>
      ))}
    </Paper>
  );
}

/* =============================== HERO CAROUSEL (auto-swipe, 3 banners) ============================== */

const HERO_EFFECTS = ["fade", "slideLeft", "slideRight", "zoom"];
function heroSlideStyle(isActive, effect) {
  const base = { position: "absolute", inset: 0, transition: "opacity 1.1s cubic-bezier(.4,0,.2,1), transform 1.1s cubic-bezier(.4,0,.2,1)" };
  if (isActive) return { ...base, opacity: 1, transform: "translateX(0) scale(1)" };
  if (effect === "slideLeft") return { ...base, opacity: 0, transform: "translateX(-6%) scale(1.02)" };
  if (effect === "slideRight") return { ...base, opacity: 0, transform: "translateX(6%) scale(1.02)" };
  if (effect === "zoom") return { ...base, opacity: 0, transform: "scale(1.12)" };
  return { ...base, opacity: 0, transform: "translateX(0) scale(1)" };
}

function HeroCarousel({ onOpenReserve, banners }) {
  const [idx, setIdx] = useState(0);
  const slides = banners && banners.length ? banners : IMAGES.banners;
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 5000);
    return () => clearInterval(t);
  }, [slides.length]);

  return (
    <Box sx={{ position: "relative", overflow: "hidden", height: { xs: 420, md: 560 }, borderBottom: `1px solid ${PALETTE.border}` }}>
      {slides.map((s, i) => (
        <Box key={i} sx={heroSlideStyle(i === idx, HERO_EFFECTS[i % HERO_EFFECTS.length])}>
          <SmartImg src={s.img} fallback={s.imgLocal} alt={s.title} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
          <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(20,14,10,0.35), rgba(20,14,10,0.68))" }} />
        </Box>
      ))}
      <Container maxWidth="md" sx={{ position: "relative", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
        <Box key={idx} sx={{ animation: "heroTextIn 0.9s cubic-bezier(.2,.8,.2,1)",
          "@keyframes heroTextIn": { "0%": { opacity: 0, transform: "translateY(16px)" }, "100%": { opacity: 1, transform: "translateY(0)" } } }}>
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center" sx={{ mb: 2 }}>
            <Box sx={{ height: "1px", width: 40, bgcolor: PALETTE.brassLight }} />
            <Typography sx={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: PALETTE.brassLight, fontWeight: 700 }}>Minnesota · Est. Prime Steakhouse</Typography>
            <Box sx={{ height: "1px", width: 40, bgcolor: PALETTE.brassLight }} />
          </Stack>
          <Typography variant="h2" sx={{ fontWeight: 700, color: "#fff", fontSize: { xs: 34, md: 52 }, lineHeight: 1.1 }}>{slides[idx].title}</Typography>
          <Typography sx={{ mt: 2, color: "rgba(255,255,255,0.9)", fontSize: 18 }}>{slides[idx].subtitle}</Typography>
          <Stack direction="row"  spacing={2} justifyContent="center" sx={{ mt: 4,display:'flex',justifyContent:'center' }}>
            <Button href="#menu" size="large" variant="contained" sx={{ bgcolor: PALETTE.garnet, px: 4, py: 1.3, "&:hover": { bgcolor: PALETTE.garnetLight } }}>Order Online</Button>
            <Button onClick={onOpenReserve} size="large" variant="outlined" sx={{ borderColor: "#fff", borderWidth: 2, color: "#fff", px: 4, py: 1.3, "&:hover": { borderWidth: 2, borderColor: "#fff", bgcolor: alpha("#fff", 0.1) } }}>Reserve a Table</Button>
          </Stack>
        </Box>
      </Container>
      <IconButton onClick={() => setIdx((i) => (i - 1 + slides.length) % slides.length)} sx={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", bgcolor: alpha("#fff", 0.15), color: "#fff", "&:hover": { bgcolor: alpha("#fff", 0.25) } }}><ChevronLeft /></IconButton>
      <IconButton onClick={() => setIdx((i) => (i + 1) % slides.length)} sx={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", bgcolor: alpha("#fff", 0.15), color: "#fff", "&:hover": { bgcolor: alpha("#fff", 0.25) } }}><ChevronRight /></IconButton>
      <Stack direction="row" spacing={1} sx={{ position: "absolute", bottom: 20, left: "50%", transform: "translateX(-50%)" }}>
        {slides.map((_, i) => (
          <Box key={i} component="button" onClick={() => setIdx(i)} sx={{ width: i === idx ? 22 : 8, height: 8, borderRadius: 4, border: "none", cursor: "pointer", bgcolor: i === idx ? PALETTE.brassLight : alpha("#fff", 0.5), transition: "width .3s ease" }} />
        ))}
      </Stack>
    </Box>
  );
}

/* =============================== MENU CARD (3-image gallery) ============================== */

function MenuCard({ m, onAdd }) {
  const gallery = (m.img && m.img.length ? m.img : IMAGE_POOL_BY_CAT[m.cat]?.slice(0, 3) || []).slice(0, 3);
  const [active, setActive] = useState(0);
  return (
    <Card variant="outlined" sx={{  borderColor:"PALETTE.border",borderRadius: 2,width:'270px', height: "320px", display: "flex", flexDirection: "column", transition: "all .2s", "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.08)", transform: "translateY(-3px)" } }}>
      <SmartImg src={gallery[active]} fallback={m.imgLocal?.[active]} alt={m.name} sx={{ width: "100%", height: 160, objectFit: "cover", flexShrink: 0 }} />
      <Stack direction="row" spacing={0.5} sx={{ px: 1.2, pt: 1, height: 13 }}>
        {gallery.length > 1 && gallery.map((g, i) => (
          <Box key={i} component="button" onClick={() => setActive(i)} sx={{ flex: 1, height: 5, border: "none", borderRadius: 3, cursor: "pointer", bgcolor: i === active ? PALETTE.garnet : PALETTE.border }} />
        ))}
      </Stack>
      <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", pt: 1.2 }}>
        <Typography sx={{ fontWeight: 600, color: PALETTE.ink, fontSize: 16, height: 40, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.25 }}>{m.name}</Typography>
        <Typography sx={{ color: PALETTE.inkDim, fontSize: 13, mt: 0.5, height: 34, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.3 }}>{m.desc}</Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: "auto", pt: 2,display:'flex',alignItems:'center',justifyContent:'space-between' }}>
          <Typography sx={{ fontWeight: 700, color: PALETTE.garnet, fontFamily: "JetBrains Mono, monospace" }}>{fmtUSD2(m.price)}</Typography>
          <Button size="small" onClick={() => onAdd(m)} startIcon={<Add sx={{ fontSize: 16 }} />} variant="contained" sx={{ bgcolor: PALETTE.ink, "&:hover": { bgcolor: PALETTE.garnet } }}>Add</Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

/* =============================== NEW — COMBO SHOWCASE ============================== */

function ComboShowcase({ combos, onAddCombo ,customersForLoyalty = []}) {
  if (!combos || !combos.length) return null;
   const [comboIdea, setComboIdea] = useState("");
  const [phone, setPhone] = useState("");
  const [submitStatus, setSubmitStatus] = useState(""); // "", "saving", "saved", "error"
  const tripWinners = useMemo(() => coupleTripWinners(customersForLoyalty), [customersForLoyalty]);

  const submitCombo = async () => {
    if (!comboIdea.trim()) return;
    setSubmitStatus("saving");
    try {
      await apiPost("/save/combo-suggestion", { text: comboIdea.trim(), phone: phone.trim() });
      setSubmitStatus("saved");
      setComboIdea("");
    } catch (e) {
      // Backend route not deployed yet, or offline — still let the guest know it went through locally.
      setSubmitStatus("error");
    }
  };
  return (
    <Box sx={{ py: 7, borderTop: `1px solid ${PALETTE.border}`, borderBottom: `1px solid ${PALETTE.border}`, background: `linear-gradient(180deg, ${alpha(PALETTE.garnet, 0.05)}, transparent)` }}>
      <Container maxWidth="lg">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <LocalFireDepartment sx={{ fontSize: 18, color: PALETTE.garnet }} />
          <Typography sx={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: PALETTE.garnet, fontWeight: 700 }}>Limited-Time Bundles</Typography>
        </Stack>
        <Typography variant="h4" sx={{ fontWeight: 600, color: PALETTE.ink, mb: 3 }}>Combo Deals</Typography>
        <Grid container spacing={3} sx={{ width: '100%', margin: 0 , }}>
          {combos.map((c, idx) => {
            const full = comboFullPrice(c.items);
            const price = comboPrice(c.items);
            const pct = Math.round((1 - price / full) * 100);
            const galleryItems = c.items.slice(0, 3);
            return (
              <Grid item xs={12} sm={6} md={4} key={c.name}>
                <Reveal delay={idx * 90} effect="up">
                  <Paper elevation={0} sx={{ borderRadius: 1, overflow: "hidden", width:'360px', height: "100%", display: "flex", flexDirection: "column",
                    border: `1px solid ${PALETTE.border}`, transition: "all .25s cubic-bezier(.2,.8,.2,1)",
                    "&:hover": { transform: "translateY(-5px)", boxShadow: "0 16px 36px rgba(126,31,49,0.18)", borderColor: PALETTE.brass } }}>
                    <Box sx={{ position: "relative", height: 150, display: "flex" }}>
                      {galleryItems.map((it, i) => (
                        <Box key={it.id} sx={{ flex: 1, position: "relative", borderRight: i < galleryItems.length - 1 ? "2px solid #fff" : "none", overflow: "hidden" }}>
                          <SmartImg src={it.img?.[0]} fallback={it.imgLocal?.[0]} alt={it.name} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        </Box>
                      ))}
                      <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 55%, rgba(20,14,10,0.75) 100%)" }} />
                      <Chip size="small" label={`${pct}% OFF`} sx={{ position: "absolute", top: 10, left: 10, bgcolor: PALETTE.garnet, color: "#fff", fontWeight: 800, fontSize: 11 }} />
                      <Typography sx={{ position: "absolute", bottom: 8, left: 12, right: 12, color: "#fff", fontWeight: 700, fontSize: 16, fontFamily: "Fraunces, serif" }}>{c.name}</Typography>
                    </Box>
                    <Box sx={{ p: 2, display: "flex", flexDirection: "column", flexGrow: 1 }}>
                      <Stack spacing={0.4} sx={{ mb: 1.5, flexGrow: 1 }}>
                        {c.items.map((it) => <Typography key={it.id} sx={{ fontSize: 12.5, color: PALETTE.inkDim }}>• {it.name}</Typography>)}
                      </Stack>
                      <Stack direction="row" sx={{display:'flex',alignItems:'center',justifyContent:'space-between'}} justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={1} alignItems="baseline">
                          <Typography sx={{ fontWeight: 700, color: PALETTE.garnet, fontFamily: "JetBrains Mono, monospace", fontSize: 17 }}>{fmtUSD2(price)}</Typography>
                          <Typography sx={{ fontSize: 11.5, color: PALETTE.inkDim, textDecoration: "line-through" }}>{fmtUSD2(full)}</Typography>
                        </Stack>
                        <Button size="small" variant="contained" onClick={() => onAddCombo(c)} sx={{ bgcolor: PALETTE.garnet, borderRadius: 999, px: 2, "&:hover": { bgcolor: PALETTE.garnetLight } }}>Add combo</Button>
                      </Stack>
                    </Box>
                  </Paper>
                </Reveal>
              </Grid>
            );
          })}
        </Grid>

        <Grid item sx={{marginTop:'10px'}} xs={12} md={3}>
            <Reveal delay={180} effect="up">
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4, borderColor: PALETTE.border, height: "100%", bgcolor: "#fff", transition: "box-shadow .2s", "&:hover": { boxShadow: "0 10px 24px rgba(0,0,0,0.08)" } }}>
              <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Suggest a Combo</Typography>
              <Typography sx={{ fontSize: 12.5, color: PALETTE.inkDim, mb: 1.5 }}>Have an idea for our next bundle? Tell us what to pair.</Typography>
              <Stack spacing={1}>
                <TextField size="small" fullWidth placeholder="Your phone (optional)" value={phone} onChange={(e)=>setPhone(e.target.value)} />
                <TextField size="small" fullWidth multiline minRows={2} placeholder="e.g. Ribeye + Old Fashioned + Cheesecake"
                  value={comboIdea} onChange={(e)=>setComboIdea(e.target.value)} />
                <Button fullWidth variant="contained" onClick={submitCombo} disabled={submitStatus === "saving" || !comboIdea.trim()}
                  sx={{ bgcolor: PALETTE.garnet, "&:hover": { bgcolor: PALETTE.garnetLight } }}>
                  {submitStatus === "saving" ? "Sending…" : "Submit idea"}
                </Button>
                {submitStatus === "saved" && <Typography sx={{ fontSize: 11.5, color: PALETTE.sage, fontWeight: 600 }}>Thanks — sent to the kitchen team!</Typography>}
                {submitStatus === "error" && <Typography sx={{ fontSize: 11.5, color: PALETTE.brass, fontWeight: 600 }}>Saved on this device — the shared inbox isn't connected yet, but staff can still add a save route for it.</Typography>}
              </Stack>
            </Paper>
            </Reveal>
          </Grid>
      </Container>
    </Box>
  );
}

/* =============================== NEW — HOME PAGE SPECIAL OFFERS (customer-facing) ============================== */
const DEFAULT_LOYALTY_RULES = {
  multipliers: { Bronze: 1, Silver: 2, Gold: 3 },
  referralPct: 20,
  birthdayPct: 12,
  anniversaryPct: 17,
  expiry: { Bronze: 2, Silver: 4, Gold: 6 },
  demandPricing: { enabled: false, peakMultiplier: 1.5 },
  cashToPointRatio: { enabled: false, centsPerPoint: 1 },
  expiryDecay: { enabled: false, inactivityDays: 30, alertDays: 7 },
  dynamicDiscount: { enabled: false, lowEngagementPct: 15 },
  variableRewards: { enabled: false, min: 5, max: 50 },
  dayBasedBonus: { enabled: false, day: "Wednesday", mult: 2 },
  seasonBonus: { enabled: false, season: "Winter", mult: 1.5 },
};

// MODIFIED: accept loyaltyRules prop and use it to display dynamic tiers
// MODIFIED: accept loyaltyRules + activeOffers, and render EVERY active rule in "Level Up & Get Free Extras"
function SpecialOffersHome({ customersForLoyalty = [], loyaltyRules = DEFAULT_LOYALTY_RULES, activeOffers = [] }) {
  const [comboIdea, setComboIdea] = useState("");
  const [phone, setPhone] = useState("");
  const [submitStatus, setSubmitStatus] = useState("");
  const tripWinners = useMemo(() => coupleTripWinners(customersForLoyalty), [customersForLoyalty]);

  const submitCombo = async () => {
    if (!comboIdea.trim()) return;
    setSubmitStatus("saving");
    try {
      await apiPost("/save/combo-suggestion", { text: comboIdea.trim(), phone: phone.trim() });
      setSubmitStatus("saved");
      setComboIdea("");
    } catch (e) {
      setSubmitStatus("error");
    }
  };

  // Build dynamic tier data from loyaltyRules
  const tierData = [
    { tier: "Bronze", range: "$0–50/mo", mult: `${loyaltyRules.multipliers.Bronze}x points`, perk: `Earn ${loyaltyRules.multipliers.Bronze}x points on every purchase` },
    { tier: "Silver", range: "$50–100/mo", mult: `${loyaltyRules.multipliers.Silver}x points`, perk: `${loyaltyRules.multipliers.Silver}x points + a complimentary dip with every purchase` },
    { tier: "Gold", range: "$100–150+/mo", mult: `${loyaltyRules.multipliers.Gold}x points`, perk: `${loyaltyRules.multipliers.Gold}x points + a complimentary dip and a small dessert with every purchase` },
  ];

  // NEW — every base loyalty rule shown as its own perk card, in addition to the 3 tiers.
  // These are ALWAYS present (they come straight from loyaltyRules, not from an "apply" step),
  // so "Active rules — live on the homepage now" is never empty even with zero custom offers applied.
  const baseRulePerks = [
    { id: "referral", label: "Refer a Friend", detail: `${loyaltyRules.referralPct}% off for you and your friend` },
    { id: "birthday", label: "Birthday Reward", detail: `${loyaltyRules.birthdayPct}% off during your birthday month` },
    { id: "anniversary", label: "Anniversary Reward", detail: `${loyaltyRules.anniversaryPct}% off + entry into the Couples' Trip Special` },
    { id: "expiry", label: "Points Expiry", detail: `Bronze ${loyaltyRules.expiry.Bronze}mo · Silver ${loyaltyRules.expiry.Silver}mo · Gold ${loyaltyRules.expiry.Gold}mo` },
  ];

  // NEW — flatten every block value in every applied custom offer into a readable perk line
  const appliedOfferPerks = activeOffers.map((offer) => ({
    id: offer.id,
    label: offer.name,
    detail: offer.blocks.map((b) => {
      const v = typeof b.value === "object" ? Object.values(b.value)[0] : b.value;
      return `${b.label.split(" — ")[0]}: ${v}`;
    }).join(" · "),
  }));

  return (
    <Box sx={{ py: 7, borderTop: `1px solid ${PALETTE.border}`, borderBottom: `1px solid ${PALETTE.border}`, position: "relative", overflow: "hidden",
      background: `radial-gradient(circle at 15% 20%, ${alpha(PALETTE.sage,0.09)}, transparent 45%), radial-gradient(circle at 85% 80%, ${alpha(PALETTE.brass,0.1)}, transparent 45%)` }}>
      <Container maxWidth="lg">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Star sx={{ fontSize: 18, color: PALETTE.brass }} />
          <Typography sx={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: PALETTE.brass, fontWeight: 700 }}>Loyalty & Special Offers</Typography>
        </Stack>
        <Typography variant="h4" sx={{ fontWeight: 600, color: PALETTE.ink, mb: 3 }}>Rewards worth coming back for</Typography>

        <Box sx={{ mb: 2 }}>
          <Reveal delay={220} effect="up">
            <Box sx={{ mt: 3, p: 2.5, borderRadius: 4, position: "relative", overflow: "hidden",
              background: `linear-gradient(120deg, ${PALETTE.brass}, ${PALETTE.garnet}, ${PALETTE.brass})`,
              backgroundSize: "200% 200%", animation: "perksGlow 6s ease infinite",
              boxShadow: `0 0 0 0 ${alpha(PALETTE.brass, 0.5)}`,
              "@keyframes perksGlow": { "0%": { backgroundPosition: "0% 50%" }, "50%": { backgroundPosition: "100% 50%" }, "100%": { backgroundPosition: "0% 50%" } },
              "@keyframes perksPulse": { "0%": { boxShadow: `0 0 0 0 ${alpha("#fff", 0.35)}` }, "70%": { boxShadow: `0 0 0 16px ${alpha("#fff", 0)}` }, "100%": { boxShadow: `0 0 0 0 ${alpha("#fff", 0)}` } } }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <AutoAwesome sx={{ color: "#fff", fontSize: 20 }} />
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>Level Up & Get Free Extras</Typography>
              </Stack>

              {/* Tier cards — Bronze / Silver / Gold */}
              <Grid container spacing={1.5} sx={{ width: "100%", margin: 0 }}>
                {tierData.map((t) => (
                  <Grid item xs={12} sm={4} key={t.tier}>
                    <Paper elevation={0} sx={{ p: 1.75, borderRadius: 3, bgcolor: alpha("#fff", 0.14), backdropFilter: "blur(6px)",
                      border: `1px solid ${alpha("#fff", 0.3)}`, animation: t.tier === "Gold" ? "perksPulse 2.2s infinite" : "none", height: "100%" }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                        <Star sx={{ fontSize: 15, color: "#fff" }} />
                        <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 14 }}>{t.tier}</Typography>
                        <Chip size="small" label={t.range} sx={{ height: 18, fontSize: 9.5, bgcolor: alpha("#fff", 0.25), color: "#fff", fontWeight: 700 }} />
                      </Stack>
                      <Typography sx={{ color: alpha("#fff", 0.95), fontSize: 12.5, fontWeight: 600 }}>{t.perk}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* Base rules — referral / birthday / anniversary / expiry — always shown, live-editable in Active Rules */}
              <Divider sx={{ my: 1.5, borderColor: alpha("#fff", 0.25) }} />
              <Typography sx={{ color: alpha("#fff", 0.85), fontSize: 10.5, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, mb: 1 }}>Also active right now</Typography>
              <Grid container spacing={1.5} sx={{ width: "100%", margin: 0 }}>
                {baseRulePerks.map((p) => (
                  <Grid item xs={12} sm={6} md={3} key={p.id}>
                    <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, bgcolor: alpha("#fff", 0.12), backdropFilter: "blur(6px)", border: `1px solid ${alpha("#fff", 0.25)}`, height: "100%" }}>
                      <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 12.5, mb: 0.3 }}>{p.label}</Typography>
                      <Typography sx={{ color: alpha("#fff", 0.92), fontSize: 11.5 }}>{p.detail}</Typography>
                    </Paper>
                  </Grid>
                ))}

                {/* Any custom offers applied from the Rules Builder — always shown, never hidden */}
                {appliedOfferPerks.map((p) => (
                  <Grid item xs={12} sm={6} md={3} key={p.id}>
                    <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, bgcolor: alpha("#fff", 0.18), backdropFilter: "blur(6px)", border: `1px solid ${alpha("#fff", 0.35)}`, height: "100%" }}>
                      <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mb: 0.3 }}>
                        <AutoAwesome sx={{ fontSize: 13, color: "#fff" }} />
                        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 12.5 }}>{p.label}</Typography>
                      </Stack>
                      <Typography sx={{ color: alpha("#fff", 0.92), fontSize: 11.5 }}>{p.detail}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Reveal>
        </Box>

        <Grid container spacing={3} sx={{ display:'flex',justifyContent:'space-between',width: "100%", margin: 0 }}>
          <Grid sx={{width:'66%'}} item xs={12}>
            <Reveal effect="up">
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, height: "100%", color: "#fff",
                background: `linear-gradient(135deg, ${PALETTE.garnet}, ${PALETTE.garnetLight})`, boxShadow: "0 14px 30px rgba(126,31,49,0.25)" }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Box sx={{ width: 30, height: 30, borderRadius: "50%", bgcolor: alpha("#fff", 0.18), display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Star sx={{ fontSize: 16 }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700 }}>Married Couples' Trip Special</Typography>
                </Stack>
                <Typography sx={{ fontSize: 12.5, color: alpha("#fff", 0.85), mb: 1.5 }}>
                  Couples celebrating an anniversary with us are automatically entered — up to 40% off a trip ticket, randomly selected every month.
                </Typography>
                {tripWinners.length === 0 ? (
                  <Typography sx={{ fontSize: 12.5, color: alpha("#fff", 0.8) }}>Selections for this month haven't been drawn yet — check back soon.</Typography>
                ) : (
                  <Stack spacing={0.75} sx={{ maxHeight: 150, overflowY: "auto" }}>
                    {tripWinners.map((c) => (
                      <Paper key={c.id} elevation={0} sx={{ p: 1, borderRadius: 2, display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: alpha("#fff", 0.12) }}>
                        <Typography sx={{ fontSize: 12.5, color: "#fff" }}>{c.name}</Typography>
                        <Chip size="small" label={`${c.discountPct}% off a trip ticket`} sx={{ bgcolor: "#fff", color: PALETTE.sage, fontWeight: 700 }} />
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Reveal>
          </Grid>

          <Grid sx={{width:'30%'}} item xs={12}>
            <Reveal delay={100} effect="up">
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4, borderColor: PALETTE.border, height: "100%", bgcolor: "#fff", transition: "box-shadow .2s", "&:hover": { boxShadow: "0 10px 24px rgba(0,0,0,0.08)" } }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Monthly Points Tiers</Typography>
                <Typography sx={{ fontSize: 12.5, color: PALETTE.inkDim, mb: 1.5 }}>Every purchase earns points based on your cumulative spend that month.</Typography>
                <Stack spacing={1}>
                  {tierData.map((t) => (
                    <Stack key={t.tier} direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.2, py: 0.8, borderRadius: 2, bgcolor: PALETTE.bg }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{t.tier}</Typography>
                      <Typography sx={{ fontSize: 12, color: PALETTE.inkDim }}>{t.range}</Typography>
                      <Typography sx={{ fontSize: 12.5, color: PALETTE.brass, fontWeight: 700 }}>{t.mult}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Reveal>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

/* =============================== FLOATING AI CONCIERGE (right side) ============================== */

const COOK_LEVELS = ["Rare", "Medium Rare", "Medium", "Medium Well", "Well Done"];

const PHRASE_BANK = {
  topCustomer: [
    (c) => `${c.name} is your top spender — ${fmtUSD2(c.totalSpend)} lifetime, over ${c.visits} visits.`,
    (c) => `Looks like ${c.name} loves this place — ${fmtUSD2(c.totalSpend)} spent across ${c.visits} visits, the most of anyone.`,
    (c) => `Top of the list: ${c.name}, with ${fmtUSD2(c.totalSpend)} spent (${c.visits} visits) — your best regular.`,
  ],
  margin: [
    (m) => `${m.name} brings the highest margin — ${m.margin}% (${fmtUSD2(m.price - m.cost)} profit per plate).`,
    (m) => `Your most profitable dish is ${m.name}: ${m.margin}% margin, ${fmtUSD2(m.price - m.cost)} profit each time it's ordered.`,
    (m) => `Margin leader: ${m.name} at ${m.margin}% — ${fmtUSD2(m.price - m.cost)} profit per unit.`,
  ],
  specials: [
    (list) => `Chef's picks right now: ${list}. Want me to add one to your order?`,
    (list) => `On the specials board today: ${list}.`,
    (list) => `New and featured: ${list} — all worth trying.`,
  ],
  noSpecials: [
    "No specials marked right now — ask an Owner to flag a dish as a Chef's Special from Profit Intelligence.",
    "Nothing's tagged as a special at the moment — check back soon, or an Owner can add one.",
  ],
  inventoryFound: [
    (i) => `${i.name}: ${i.qty}${i.unit} on hand${i.qty <= i.threshold ? " — that's at or below the reorder threshold, worth restocking soon." : ", comfortably above par."}`,
    (i) => `Checked the shelf — ${i.name} is sitting at ${i.qty}${i.unit}${i.qty <= i.threshold ? ", which is low, time to reorder." : ", healthy stock level."}`,
  ],
  inventoryNotFound: [
    (q) => `Couldn't find an inventory item matching "${q}" — try the exact ingredient name, like "ribeye" or "chicken."`,
  ],
  wastage: [
    (total, count) => `Logged wastage/spoilage comes to ${fmtUSD2(total)} across ${count} entries — check the Inventory tab for the full breakdown.`,
    (total, count) => `${fmtUSD2(total)} lost to spoilage so far, over ${count} logged entries.`,
  ],
  highStock: [
    (list) => `Sitting well above par (20+ units over threshold): ${list}. Might be worth easing back on the next order.`,
    (list) => `These are overstocked right now — comfortably 20+ above their reorder point: ${list}.`,
  ],
  noHighStock: [
    "Nothing's overstocked by 20+ units right now — inventory's running lean across the board.",
  ],
  spoiledNames: [
    (list, total) => `Items that have gone to waste: ${list} — ${fmtUSD2(total)} lost in total.`,
    (list, total) => `Spoiled without being used: ${list}. That's ${fmtUSD2(total)} down the drain so far.`,
  ],
  noSpoiled: [
    "Nothing's been logged as spoiled — clean record so far.",
  ],
  wastagePeriod: [
    (period, total, count) => `Over the last ${period}: ${fmtUSD2(total)} in wastage across ${count} entries.`,
    (period, total, count) => `${period === "day" ? "Today" : `This ${period}`}, wastage adds up to ${fmtUSD2(total)} (${count} entries logged).`,
  ],
};
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function FloatingAIConcierge({ onOpenReserve, menu = [], onAddToCart, customers = [], inventory = [], wastageLog = [], ledger = [] ,loyaltyRules = DEFAULT_LOYALTY_RULES}) {
  const [open, setOpen] = useState(false);
  const specials = menu.filter((m) => m.isSpecial);
  const greetedRef = useRef(false);
  const [messages, setMessages] = useState([{ from: "ai", text: "Hi! I'm Lindey's AI concierge. Ask me a question, or say \"order\" to start ordering through chat." }]);
  const [input, setInput] = useState("");
  const [orderStep, setOrderStep] = useState(null);
  const [orderDraft, setOrderDraft] = useState({ cat: null, item: null, cook: null });
  const endRef = useRef(null);
  useEffect(() => { if (open) endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" }); }, [messages, open, orderStep]);
  useEffect(() => {
    if (open && onAddToCart && specials.length && !greetedRef.current) {
      greetedRef.current = true;
      say("ai", `While you're here — we've got ${specials.length} Chef's Special${specials.length > 1 ? "s" : ""} tonight: ${specials.map(s => s.name).join(", ")}. Want to try one, or should I show you the full menu?`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const say = (from, text, extra = {}) => setMessages((m) => [...m, { from, text, ...extra }]);

  const startOrderFlow = () => {
    setOrderStep("category");
    setOrderDraft({ cat: null, item: null, cook: null });
    if (specials.length) {
      say("ai", `What are you in the mood for? (Psst — don't forget our Chef's Specials: ${specials.map(s => s.name).join(", ")})`);
    } else {
      say("ai", "What are you in the mood for?");
    }
  };
  const pickCategory = (cat) => {
    say("user", cat);
    setOrderDraft((d) => ({ ...d, cat }));
    setOrderStep("item");
    say("ai", `Here's our ${cat.toLowerCase()} — pick one:`);
  };
  const pickItem = (item) => {
    say("user", item.name);
    setOrderDraft((d) => ({ ...d, item }));
    if (item.cat === "Steaks") {
      setOrderStep("cook");
      say("ai", "How would you like that cooked?");
    } else {
      setOrderStep("confirm");
      say("ai", `${item.name} — ${fmtUSD2(item.price)}. Add it to your cart?`);
    }
  };
  const pickCook = (cook) => {
    say("user", cook);
    setOrderDraft((d) => ({ ...d, cook }));
    setOrderStep("confirm");
    say("ai", `${orderDraft.item.name} (${cook}) — ${fmtUSD2(orderDraft.item.price)}. Add it to your cart?`);
  };
  const confirmAdd = (yes) => {
    say("user", yes ? "Yes" : "No");
    if (yes && onAddToCart) {
      onAddToCart(orderDraft.item, orderDraft.cook);
      say("ai", `Added ${orderDraft.item.name}${orderDraft.cook ? ` (${orderDraft.cook})` : ""} to your cart. Want to order anything else?`);
    } else if (yes) {
      say("ai", "Got it — head to checkout when you're ready to finish the order.");
    } else {
      say("ai", "No problem — want to pick something else?");
    }
    setOrderStep("category");
    setOrderDraft({ cat: null, item: null, cook: null });
  };

  const findItemByName = (text) => menu.find((m) => text.toLowerCase().includes(m.name.toLowerCase()));

  const answerQuery = (text) => {
    if (/reserv|book a table/i.test(text)) return { text: "Let's get you booked — tap below to pick a date, party size, and table.", showBook: true };
    if (/order|menu|hungry|steak|drink|dessert|starter|side/i.test(text) && !/(top|most|margin|profit|spend|special|new dish|stock|inventory|position|waste|spoil)/i.test(text)) {
      startOrderFlow();
      return null;
    }
    const directItem = findItemByName(text);
    if (directItem && onAddToCart) { pickItem(directItem); return null; }

    if (/(top|most|best).*(customer|spend|regular)|which cus/i.test(text)) {
      if (!customers.length) return { text: "No customer data available yet." };
      const top = [...customers].sort((a,b) => b.totalSpend - a.totalSpend)[0];
      return { text: pick(PHRASE_BANK.topCustomer)(top) };
    }
    if (/margin|profit|most profitable/i.test(text)) {
      if (!menu.length) return { text: "No menu data available yet." };
      const best = [...menu].sort((a,b) => (b.margin||0) - (a.margin||0))[0];
      return { text: pick(PHRASE_BANK.margin)(best) };
    }
    if (/special|new dish/i.test(text)) {
      const specials = menu.filter((m) => m.isSpecial);
      if (!specials.length) return { text: pick(PHRASE_BANK.noSpecials) };
      return { text: pick(PHRASE_BANK.specials)(specials.map((s) => s.name).join(", ")) };
    }
    if (/high\s*stock|overstock|stock.*(more than|over|above)\s*20/i.test(text)) {
      const highStock = inventory.filter((i) => (i.qty - i.threshold) >= 20);
      if (!highStock.length) return { text: pick(PHRASE_BANK.noHighStock) };
      return { text: pick(PHRASE_BANK.highStock)(highStock.map((i) => `${i.name} (${i.qty}${i.unit})`).join(", ")) };
    }
    if (/spoiled? item|spoiled? name|wasted item|without.*use|name.*spoil/i.test(text)) {
      const spoiled = ledger.filter((l) => l.type === "spoiled");
      if (!spoiled.length) return { text: pick(PHRASE_BANK.noSpoiled) };
      const names = [...new Set(spoiled.map((l) => l.itemName))];
      const total = spoiled.reduce((s,l) => s + (l.cost||0), 0);
      return { text: pick(PHRASE_BANK.spoiledNames)(names.join(", "), total) };
    }
    if (/waste.*(day|week|month)|wastage.*(day|week|month)|(day|week|month).*wast/i.test(text)) {
      const period = /day/i.test(text) ? "day" : /week/i.test(text) ? "week" : "month";
      const cutoffDays = { day: 1, week: 7, month: 30 }[period];
      const cutoff = Date.now() - cutoffDays * 86400000;
      const spoiled = ledger.filter((l) => l.type === "spoiled" && (l.createdAt || 0) >= cutoff);
      const total = spoiled.reduce((s,l) => s + (l.cost||0), 0) + wastageLog.reduce((s,w) => s + (w.cost||0), 0);
      const count = spoiled.length + wastageLog.length;
      return { text: pick(PHRASE_BANK.wastagePeriod)(period, total, count) };
    }
    if (/stock|inventory|position of|how much|how many/i.test(text)) {
      const found = inventory.find((i) => text.toLowerCase().includes(i.name.toLowerCase().split(" ")[0]));
      if (found) return { text: pick(PHRASE_BANK.inventoryFound)(found) };
      const m = /(?:of|about)\s+([a-z\s]+)/i.exec(text);
      return { text: pick(PHRASE_BANK.inventoryNotFound)(m ? m[1].trim() : text) };
    }
    if (/waste|wastage|spoil/i.test(text)) {
      const spoiled = ledger.filter((l) => l.type === "spoiled");
      const total = wastageLog.reduce((s,w) => s + (w.cost||0), 0) + spoiled.reduce((s,l) => s + (l.cost||0), 0);
      const count = wastageLog.length + spoiled.length;
      return { text: pick(PHRASE_BANK.wastage)(total, count) };
    }
     if (/loyalty|points|tier|multiplier|expiry|referral|birthday|anniversary/i.test(text)) {
      const rules = loyaltyRules || DEFAULT_LOYALTY_RULES;
      const totalCustomers = customers.length;
      const tierCounts = customers.reduce((acc, c) => {
        const tier = c.pointsTier || "Bronze";
        acc[tier] = (acc[tier] || 0) + 1;
        return acc;
      }, { Bronze: 0, Silver: 0, Gold: 0 });
      const totalPoints = customers.reduce((s, c) => s + (c.loyaltyPoints || 0), 0);
      const avgPoints = totalCustomers ? Math.round(totalPoints / totalCustomers) : 0;

      // Build a friendly summary
      const summary = [
        `Loyalty program status: ${totalCustomers} customers enrolled.`,
        `Tier distribution: Bronze ${tierCounts.Bronze}, Silver ${tierCounts.Silver}, Gold ${tierCounts.Gold}.`,
        `Current multipliers: Bronze ${rules.multipliers.Bronze}x, Silver ${rules.multipliers.Silver}x, Gold ${rules.multipliers.Gold}x.`,
        `Points expiry: Bronze ${rules.expiry.Bronze} mo, Silver ${rules.expiry.Silver} mo, Gold ${rules.expiry.Gold} mo.`,
        `Referral discount: ${rules.referralPct}%, Birthday: ${rules.birthdayPct}%, Anniversary: ${rules.anniversaryPct}%.`,
        `Total points issued: ${totalPoints} (avg ${avgPoints} per customer).`,
      ].join(" ");

      // If they ask something specific, we can tailor the answer
      if (/multiplier|point.*multipl/i.test(text)) {
        return { text: `Current multipliers: Bronze ${rules.multipliers.Bronze}x, Silver ${rules.multipliers.Silver}x, Gold ${rules.multipliers.Gold}x.` };
      }
      if (/expiry|expire/i.test(text)) {
        return { text: `Points expire after: Bronze ${rules.expiry.Bronze} months, Silver ${rules.expiry.Silver} months, Gold ${rules.expiry.Gold} months.` };
      }
      if (/referral|refer a friend/i.test(text)) {
        return { text: `Refer a friend gets both of you ${rules.referralPct}% off their first order.` };
      }
      if (/birthday/i.test(text)) {
        return { text: `We offer ${rules.birthdayPct}% off for birthdays — just let us know when you book!` };
      }
      if (/anniversary/i.test(text)) {
        return { text: `Celebrate your anniversary with us and get ${rules.anniversaryPct}% off — you're also automatically entered into our monthly trip special.` };
      }
      if (/tier|status|level/i.test(text)) {
        const topTier = Object.entries(tierCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "Bronze";
        return { text: `Most customers are ${topTier} tier. ${summary}` };
      }

      // Default loyalty info
      return { text: `Here's the loyalty program summary: ${summary}` };
    }
    if (/wait|busy|how long/i.test(text)) return { text: "Current wait is light — about 10–15 minutes for a table of 2–4. Weekends after 7 PM tend to run longer." };
    return { text: "Happy to help — ask about specials, top customers, margins, stock levels, wastage, or say \"order\" to order through chat." };
  };

  const send = () => {
    if (!input.trim()) return;
    const text = input;
    say("user", text);
    setInput("");
    const reply = answerQuery(text);
    if (reply) say("ai", reply.text, { showBook: reply.showBook });
  };

  const categories = onAddToCart ? [...CATS, ...(menu.some(m=>m.isSpecial) ? ["Chef's Specials"] : [])] : [];
  const itemsForCat = orderDraft.cat === "Chef's Specials" ? menu.filter(m=>m.isSpecial) : menu.filter(m => m.cat === orderDraft.cat);

  return (
    <>
      <Zoom in>
        <Fab onClick={() => setOpen((o) => !o)} variant={open ? "circular" : "extended"}
          sx={{ position: "fixed", bottom: 24, right: 24,  bgcolor: PALETTE.garnet, color: "#fff", boxShadow: "0 10px 30px rgba(126,31,49,0.4)", "&:hover": { bgcolor: PALETTE.garnetLight } }}>
          {open ? <Close sx={{ mr: 0 }} /> : <><SmartToy sx={{ mr: 1 }} /> Ask Lindey's AI</>}
        </Fab>
      </Zoom>
      <Collapse in={open} sx={{ position: "fixed", bottom: 96, right: 24, zIndex: 1300, width: { xs: "88vw", sm: 380 } }}>
        <Paper elevation={8} sx={{ borderRadius: 2, overflow: "hidden", display: "flex", flexDirection: "column", height: 500, border: `1px solid ${PALETTE.border}` }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${PALETTE.border}`, bgcolor: PALETTE.bg }}>
            <Avatar sx={{ bgcolor: alpha(PALETTE.garnet, 0.12), color: PALETTE.garnet, width: 32, height: 32 }}><AutoAwesome sx={{ fontSize: 16 }} /></Avatar>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Lindey's AI Concierge</Typography>
              <Typography sx={{ fontSize: 11, color: PALETTE.inkDim }}>{onAddToCart ? "Order · Reservations · Questions" : "Business Q&A"}</Typography>
            </Box>
          </Stack>
          <Stack spacing={1} sx={{ flexGrow: 1, overflowY: "auto", p: 1.5 }}>
            {messages.map((m, i) => (
              <Box key={i} sx={{ display: "flex", flexDirection: "column", alignItems: m.from === "user" ? "flex-end" : "flex-start" }}>
                <Box sx={{ maxWidth: "85%", px: 1.6, py: 1, borderRadius: 3, fontSize: 13.5, bgcolor: m.from === "user" ? PALETTE.garnet : PALETTE.bg, color: m.from === "user" ? "#fff" : PALETTE.ink, border: m.from === "ai" ? `1px solid ${PALETTE.border}` : "none" }}>{m.text}</Box>
                {m.showBook && onOpenReserve && (
                  <Button size="small" onClick={() => { onOpenReserve(); setOpen(false); }} startIcon={<CalendarMonth sx={{ fontSize: 14 }} />}
                    sx={{ mt: 0.75, bgcolor: PALETTE.garnet, color: "#fff", "&:hover": { bgcolor: PALETTE.garnetLight } }}>Book a Table</Button>
                )}
              </Box>
            ))}
            <div ref={endRef} />
          </Stack>

          {onAddToCart && orderStep && (
            <Box sx={{ p: 1.2, borderTop: `1px solid ${PALETTE.border}`, bgcolor: PALETTE.bg }}>
              {orderStep === "category" && (
                <Stack direction="row" flexWrap="wrap" gap={0.6}>
                  {categories.map((c) => <Chip key={c} label={c} size="small" clickable onClick={()=>pickCategory(c)} sx={{ bgcolor: alpha(PALETTE.brass,0.14), color: PALETTE.brass, fontWeight: 600 }} />)}
                </Stack>
              )}
              {orderStep === "item" && (
                <Stack direction="row" flexWrap="wrap" gap={0.6}>
                  {itemsForCat.map((m) => <Chip key={m.id} label={`${m.name} · ${fmtUSD2(m.price)}`} size="small" clickable onClick={()=>pickItem(m)} sx={{ bgcolor: alpha(PALETTE.brass,0.14), color: PALETTE.brass, fontWeight: 600 }} />)}
                </Stack>
              )}
              {orderStep === "cook" && (
                <Stack direction="row" flexWrap="wrap" gap={0.6}>
                  {COOK_LEVELS.map((c) => <Chip key={c} label={c} size="small" clickable onClick={()=>pickCook(c)} sx={{ bgcolor: alpha(PALETTE.brass,0.14), color: PALETTE.brass, fontWeight: 600 }} />)}
                </Stack>
              )}
              {orderStep === "confirm" && (
                <Stack direction="row" spacing={0.6}>
                  <Chip label="✓ Yes, add it" size="small" clickable onClick={()=>confirmAdd(true)} sx={{ bgcolor: PALETTE.sage, color: "#fff", fontWeight: 700 }} />
                  <Chip label="✕ No" size="small" clickable onClick={()=>confirmAdd(false)} sx={{ bgcolor: PALETTE.border, color: PALETTE.inkDim, fontWeight: 600 }} />
                </Stack>
              )}
            </Box>
          )}

          <Stack direction="row" spacing={1} sx={{ p: 1.5, borderTop: `1px solid ${PALETTE.border}` }}>
            <TextField fullWidth size="small" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={onAddToCart ? "Ask, or say \"order\"…" : "Ask about sales, customers, stock…"} />
            <IconButton onClick={send} sx={{ bgcolor: PALETTE.garnet, color: "#fff", "&:hover": { bgcolor: PALETTE.garnetLight } }}><Send sx={{ fontSize: 16 }} /></IconButton>
          </Stack>
        </Paper>
      </Collapse>
    </>
  );
}

/* =============================== PUBLIC STOREFRONT ============================ */

// MODIFIED: accept loyaltyRules prop and pass to SpecialOffersHome
function Storefront({ shopInfo, menu, combos, banners, aboutCards, orderQueue, onSendOrder, reservations, onStaffLogin, onOpenReserve, customersForLoyalty = [], serverOnline = true, loyaltyRules = DEFAULT_LOYALTY_RULES, activeOffers = [] }) {
    const [cat, setCat] = useState("Steaks");
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [foundCustomer, setFoundCustomer] = useState(null);
  const [searchedPhone, setSearchedPhone] = useState(false);
  const [orderDone, setOrderDone] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [orderType, setOrderType] = useState("online");
  const [tableId, setTableId] = useState("");
  const [myOrderIds, setMyOrderIds] = useState([]);
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [earnedPoints, setEarnedPoints] = useState(null);
  const [pointsByPhone, setPointsByPhone] = useState({});
  const customersRef = useRef(customersForLoyalty.length ? customersForLoyalty : genCustomers(35, menu));
  const pointsFor = (cust, phoneKey) => {
    const key = (phoneKey || "").replace(/\D/g, "");
    if (pointsByPhone[key] !== undefined) return pointsByPhone[key];
    return cust?.loyaltyPoints || 0;
  };

  const addToCart = (item, seasoning = null) => setCart((c) => {
    const existing = c.find((x) => x.item.id === item.id && x.seasoning === seasoning);
    if (existing) return c.map((x) => x === existing ? { ...x, qty: x.qty + 1 } : x);
    return [...c, { item, qty: 1, seasoning }];
  });
  const addCombo = (combo) => combo.items.forEach((it) => addToCart(it));
  const changeQty = (id, seasoning, delta) => setCart((c) => c.map((x) => (x.item.id === id && x.seasoning === seasoning) ? { ...x, qty: Math.max(0, x.qty + delta) } : x).filter((x) => x.qty > 0));
  const subtotal = cart.reduce((s, x) => s + x.item.price * x.qty, 0);
  const tax = subtotal * (parseFloat(shopInfo.taxRate) / 100 || 0);
  const total = subtotal + tax;
  const cartCount = cart.reduce((s, x) => s + x.qty, 0);

  const lookupPhone = () => {
    const match = customersRef.current.find((c) => phone.trim() && c.phone.replace(/\D/g, "").includes(phone.replace(/\D/g, "")));
    setFoundCustomer(match || null);
    setSearchedPhone(true);
  };

  const [orderError, setOrderError] = useState("");
  const placeOrder = async () => {
    if (orderType === "dinein" && !tableId) return;
    setPlacingOrder(true);
    setOrderError("");
    try {
      const items = cart.flatMap((x) => Array(x.qty).fill(x.seasoning ? `${x.item.name} (${x.seasoning})` : x.item.name));
      const order = await onSendOrder({ table: orderType === "dinein" ? tableId : "Online", items, orderType, phone: phone.trim(), name: foundCustomer?.name || "" });
      const monthlyBefore = foundCustomer?.thisMonthSpend || 0;
      const earned = pointsForPurchase(total, monthlyBefore);
      setEarnedPoints(earned);
      const phoneKey = phone.trim().replace(/\D/g, "");
      if (phoneKey) {
        setPointsByPhone((p) => ({ ...p, [phoneKey]: pointsFor(foundCustomer, phone.trim()) + earned.points }));
      }
      setMyOrderIds((ids) => [order.id, ...ids]);
      setOrderDone(true);
      setCart([]);
    } catch (e) {
      setOrderError(e.message || "Couldn't place your order — please try again.");
    } finally {
      setPlacingOrder(false);
    }
  };

  const [trackPhone, setTrackPhone] = useState("");
  const [trackSearched, setTrackSearched] = useState(false);
  const myOrders = orderQueue.filter((o) => myOrderIds.includes(o.id));
  const phoneMatchedOrders = trackSearched && trackPhone.trim()
    ? orderQueue.filter((o) => o.phone && o.phone.replace(/\D/g,"").includes(trackPhone.replace(/\D/g,"")))
    : [];
  const displayedOrders = [...myOrders, ...phoneMatchedOrders].filter((o, i, arr) => arr.findIndex(x => x.id === o.id) === i);
  const activeOrders = myOrders.filter((o) => o.status !== "served");

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: PALETTE.bg }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: alpha(PALETTE.bg, 0.9), backdropFilter: "blur(10px)", borderBottom: `1px solid ${PALETTE.border}`, color: PALETTE.ink }}>
       <Toolbar sx={{ maxWidth: 1200, width: "100%", mx: "auto" }}>
          <LocalFireDepartment sx={{ color: PALETTE.garnet, mr: 1 }} />
          <Typography sx={{ fontFamily: "Fraunces, serif", fontWeight: 700, fontSize: 20, flexGrow: 1 }}>{shopInfo.name}</Typography>
          <Button  variant="outlined" onClick={onOpenReserve} startIcon={<CalendarMonth />} sx={{ borderColor: PALETTE.border, color: PALETTE.ink, mr: 1, display: { xs: "none", sm: "inline-flex" }, }}>Reserve a Table</Button>
          <Badge color="secondary" variant="dot" invisible={activeOrders.length === 0} sx={{ mr: 1.5 }}>
            <Button onClick={() => setTrackingOpen(true)} startIcon={<AccessTime sx={{ fontSize: 16 }} />} variant="outlined"
              sx={{ borderColor: PALETTE.border, color: PALETTE.ink, display: { xs: "none", sm: "inline-flex" } }}>Track Order</Button>
          </Badge>
          <Button onClick={onStaffLogin} variant="outlined" sx={{ borderColor: PALETTE.border, color: PALETTE.ink, mr: 1.5, display: { xs: "none", sm: "inline-flex" } }}>Staff Login</Button>
          <IconButton onClick={() => setCartOpen(true)} sx={{ bgcolor: PALETTE.garnet, color: "#fff", "&:hover": { bgcolor: PALETTE.garnetLight }, mr: 1 }}>
            <Badge badgeContent={cartCount} color="secondary"><ShoppingCart sx={{ fontSize: 20 }} /></Badge>
          </IconButton>
          
        </Toolbar>
      </AppBar>

      <HeroCarousel onOpenReserve={onOpenReserve} banners={banners} />

      <Box sx={{ background: `linear-gradient(90deg, ${PALETTE.ink}, ${PALETTE.garnet})`, py: 1.6 }}>
        <Container maxWidth="lg">
          <Stack sx={{display:'flex',alignItems:'center',justifyContent:'center'}} 
  direction="row"
  flexWrap="wrap"
  useFlexGap
  spacing={3}
  justifyContent="center"
  alignItems="center"
>
  {[
    [LocalFireDepartment, "28-Day Dry Aged"],
    [Star, "4.9★ Guest Rating"],
    [CalendarMonth, "Since 1978"],
    [AutoAwesome, "AI Concierge On-Call"],
  ].map(([Icon, label], i) => (
    <Stack
      key={i}
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        minWidth: 180,
        justifyContent: "center",
      }}
    >
      <Icon
        sx={{
          fontSize: 15,
          color: PALETTE.brassLight,
        }}
      />
      <Typography
        sx={{
          fontSize: 12.5,
          color: "#fff",
          fontWeight: 600,
          letterSpacing: 0.3,
        }}
      >
        {label}
      </Typography>
    </Stack>
  ))}
</Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 7 }}>
        <Grid container spacing={3} sx={{ width: '100%',display:'flex',justifyContent:'center', margin: 0 }}>
          {aboutCards.map((s, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Reveal delay={i * 120} effect="up">
                <SmartImg src={s.img} fallback={s.imgLocal} alt={s.title} sx={{ width: "100%", height: 176, objectFit: "cover", borderRadius: 4 }} />
                <Typography sx={{ fontWeight: 600, mt: 1.5 }}>{s.title}</Typography>
                <Typography sx={{ fontSize: 13, color: PALETTE.inkDim, mt: 0.5 }}>{s.desc}</Typography>
              </Reveal>
            </Grid>
          ))}
        </Grid>
      </Container>

      {menu.some((m) => m.isSpecial) && (
        <Box sx={{ bgcolor: alpha(PALETTE.brass, 0.06), borderTop: `1px solid ${PALETTE.border}`, borderBottom: `1px solid ${PALETTE.border}`, py: 6 }}>
          <Container maxWidth="lg">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <AutoAwesome sx={{ fontSize: 18, color: PALETTE.brass }} />
              <Typography sx={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: PALETTE.brass, fontWeight: 700 }}>Chef's Specials & New Dishes</Typography>
            </Stack>
            <Typography variant="h4" sx={{ fontWeight: 600, color: PALETTE.ink, mb: 3 }}>Fresh off the pass</Typography>
            <Grid container spacing={3} sx={{ width: '100%', margin: 2 }}>
              {menu.filter((m) => m.isSpecial).map((m, i) => (
                <Grid item xs={12} sm={6} md={4} key={m.id}>
                  <Reveal delay={i * 100} effect="scale"><MenuCard m={m} onAdd={addToCart} /></Reveal>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      <Container maxWidth="lg" id="menu" sx={{ pb: 8 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" rowGap={2} sx={{ mb: 3 ,display:'flex',justifyContent:'center',gap:'20px'}}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: PALETTE.ink }}>Order Online</Typography>
          <Tabs value={cat} onChange={(e, v) => setCat(v)} textColor="inherit"
            TabIndicatorProps={{ style: { backgroundColor: PALETTE.garnet, height: 3, borderRadius: 3 } }} sx={{ minHeight: 0 }}>
            {CATS.map((c) => <Tab key={c} value={c} label={c} sx={{ textTransform: "none", fontWeight: 600, minHeight: 0, color: cat === c ? PALETTE.garnet : PALETTE.inkDim }} />)}
          </Tabs>
        </Stack>
        <Grid container spacing={3} sx={{ width: '100%', margin: 2 }}>
          {menu.filter((m) => m.cat === cat).map((m, i) => (
            <Grid item xs={12} sm={6} md={4} key={m.id}>
              <Reveal delay={(i % 6) * 80} effect="scale"><MenuCard m={m} onAdd={addToCart} /></Reveal>
            </Grid>
          ))}
        </Grid>
      </Container>

      <ComboShowcase combos={combos} onAddCombo={addCombo} />

      {/* Pass loyaltyRules down */}
<SpecialOffersHome customersForLoyalty={customersRef.current} loyaltyRules={loyaltyRules} activeOffers={activeOffers} />
      <Box sx={{ borderTop: `1px solid ${PALETTE.border}`, py: 5 }}>
        <Container maxWidth="lg" sx={{display:'flex',justifyContent:'space-between'}}>
          <Grid container spacing={3} sx={{ fontSize: 14, color: PALETTE.inkDim, width: '100%', display:'flex',justifyContent:'space-between',margin: 0 }}>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontWeight: 600, color: PALETTE.ink, mb: 1 }}>{shopInfo.name}</Typography>
              <Typography sx={{ fontSize: 13 }}>{shopInfo.tagline}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}><LocationOn sx={{ fontSize: 15 }} /><Typography sx={{ fontSize: 13 }}>{shopInfo.address}</Typography></Stack>
              <Stack direction="row" spacing={0.75} alignItems="center"><Phone sx={{ fontSize: 15 }} /><Typography sx={{ fontSize: 13 }}>{shopInfo.phone}</Typography></Stack>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: 13, mb: 0.5 }}>{shopInfo.hoursWeekday}</Typography>
              <Typography sx={{ fontSize: 13 }}>{shopInfo.hoursWeekend}</Typography>
            </Grid>
          </Grid>
        </Container>
        <Typography sx={{ textAlign: "center", fontSize: 12, color: PALETTE.inkDim, mt: 4 }}>{shopInfo.name} · Prototype ordering demo · Minnesota</Typography>
      </Box>

      <Drawer anchor="right" open={cartOpen} onClose={() => setCartOpen(false)} sx={{padding:'10px',}} PaperProps={{ sx: { width: { xs: "100%", sm: 400 }, padding:'10px', display: "flex", flexDirection: "column" } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2,display:'flex',justifyContent:'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Your Order</Typography>
          <IconButton onClick={() => setCartOpen(false)}><Close /></IconButton>
        </Stack>
        {cart.length === 0 ? (
          <Typography sx={{ color: PALETTE.inkDim, fontSize: 14 }}>Your cart is empty — add something delicious.</Typography>
        ) : (
          <Stack spacing={1.5} sx={{ flexGrow: 1, overflowY: "auto" }}>
            {cart.map((x, i) => (
              <Stack key={`${x.item.id}-${x.seasoning || "none"}-${i}`} direction="row" spacing={1.5} alignItems="center" sx={{ p: 1.2, bgcolor: PALETTE.bg, borderRadius: 2 }}>
                <SmartImg src={x.item.img?.[0]} fallback={x.item.imgLocal?.[0]} alt="" sx={{ width: 44, height: 44, borderRadius: 2, objectFit: "cover" }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{x.item.name}{x.seasoning ? ` — ${x.seasoning}` : ""}</Typography>
                  <Typography sx={{ fontSize: 12, color: PALETTE.inkDim }}>{fmtUSD2(x.item.price)} each</Typography>
                </Box>
                <Stack direction="row" alignItems="center" sx={{display:'flex',alignItems:'center'}} spacing={0.5}>
                  <IconButton size="small" onClick={() => changeQty(x.item.id, x.seasoning, -1)}><Remove sx={{ fontSize: 14 }} /></IconButton>
                  <Typography sx={{ fontSize: 13, minWidth: 18, textAlign: "center" }}>{x.qty}</Typography>
                  <IconButton size="small" onClick={() => changeQty(x.item.id, x.seasoning, 1)}><Add sx={{ fontSize: 14 }} /></IconButton>
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
        <Divider sx={{ my: 2 }} />
        <Stack spacing={0.5}>
          <Stack direction="row" sx={{display:'flex',justifyContent:'space-between'}} justifyContent="space-between"><Typography sx={{ fontSize: 13,display:'flex',justifyContent:'space-between', color: PALETTE.inkDim }}>Subtotal</Typography><Typography sx={{ fontSize: 13 }}>{fmtUSD2(subtotal)}</Typography></Stack>
          <Stack direction="row" justifyContent="space-between"><Typography sx={{ fontSize: 13, color: PALETTE.inkDim }}>Tax ({shopInfo.taxRate}%)</Typography><Typography sx={{ fontSize: 13 }}>{fmtUSD2(tax)}</Typography></Stack>
          <Stack direction="row" sx={{display:'flex',justifyContent:'space-between', mt: 0.5}} justifyContent="space-between" ><Typography sx={{ fontWeight: 700 }}>Total</Typography><Typography sx={{ fontWeight: 700 }}>{fmtUSD2(total)}</Typography></Stack>
        </Stack>
        <Button disabled={cart.length === 0} onClick={() => { setCartOpen(false); setCheckoutOpen(true); }} variant="contained" size="large"
          sx={{ mt: 2, bgcolor: PALETTE.garnet, "&:hover": { bgcolor: PALETTE.garnetLight } }}>Checkout</Button>
      </Drawer>

      <Dialog open={checkoutOpen} onClose={() => { setCheckoutOpen(false); setOrderDone(false); setSearchedPhone(false); setFoundCustomer(null); }} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1, position: "relative" } }}>
        <IconButton onClick={() => { setCheckoutOpen(false); setOrderDone(false); setSearchedPhone(false); setFoundCustomer(null); }}
          sx={{ position: "absolute", top: 8, right: 8, zIndex: 2, bgcolor: alpha(PALETTE.ink, 0.06), "&:hover": { bgcolor: alpha(PALETTE.ink, 0.12) } }}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
        <DialogContent>
          {!orderDone ? (
            <>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Almost there</Typography>
              <Typography sx={{ fontSize: 13, color: PALETTE.inkDim, mb: 1.5 }}>How would you like this order?</Typography>
              <ToggleButtonGroup fullWidth exclusive size="small" value={orderType} onChange={(e,v)=>v&&setOrderType(v)} sx={{ mb: 1.5 }}>
                <ToggleButton value="online" sx={{ fontSize: 12.5, "&.Mui-selected": { bgcolor: alpha(PALETTE.garnet,0.12), color: PALETTE.garnet } }}>Online (pickup / delivery)</ToggleButton>
                <ToggleButton value="dinein" sx={{ fontSize: 12.5, "&.Mui-selected": { bgcolor: alpha(PALETTE.garnet,0.12), color: PALETTE.garnet } }}>Dine-in — I'm at a table</ToggleButton>
              </ToggleButtonGroup>
              {orderType === "dinein" && (
                <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                  <Select displayEmpty value={tableId} onChange={(e)=>setTableId(e.target.value)} renderValue={(v)=>v?`Table ${v}`:"Select your table number…"}>
                    {TABLES.map((t) => {
                      const todayISO = new Date().toISOString().slice(0, 10);
                      const reserved = reservations.some((r) => r.table === t.id && r.date === todayISO);
                      return <MenuItem key={t.id} value={t.id} disabled={reserved}>{t.id} (seats {t.seats}){reserved ? " — reserved today" : ""}</MenuItem>;
                    })}
                  </Select>
                </FormControl>
              )}
              <Typography sx={{ fontSize: 13, color: PALETTE.inkDim, mb: 1 }}>Enter your phone number so we can find your favorites.</Typography>
              <TextField fullWidth size="small" placeholder="612-555-1010" value={phone} onChange={(e) => setPhone(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ fontSize: 16 }} /></InputAdornment> }} sx={{ mb: 1.5 }} />
              <Button fullWidth variant="outlined" onClick={lookupPhone} sx={{ mb: 2, borderColor: PALETTE.border, color: PALETTE.ink }}>Continue</Button>
              {searchedPhone && foundCustomer && (
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, mb: 2, borderColor: PALETTE.brass, bgcolor: alpha(PALETTE.brass, 0.06) }}>
                  <Stack direction="row" spacing={1} alignItems="center"><AutoAwesome sx={{ fontSize: 14, color: PALETTE.brass }} /><Typography sx={{ fontSize: 13, fontWeight: 600 }}>Welcome back, {foundCustomer.name.split(" ")[0]}!</Typography></Stack>
                  <Typography sx={{ fontSize: 12, color: PALETTE.inkDim, mt: 0.5 }}>Regular for {foundCustomer.memberSince} months · Favorite: {foundCustomer.favorite}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: PALETTE.brass, mt: 0.5, fontWeight: 600 }}>Current points tier this month: {monthlyPointsTier(foundCustomer.thisMonthSpend || 0).label} ({monthlyPointsTier(foundCustomer.thisMonthSpend || 0).mult}x points)</Typography>
                  <Typography sx={{ fontSize: 11.5, color: PALETTE.garnet, mt: 0.3, fontWeight: 700 }}>Total points balance: {pointsFor(foundCustomer, phone)} pts</Typography>
                </Paper>
              )}
              {searchedPhone && !foundCustomer && <Typography sx={{ fontSize: 12, color: PALETTE.inkDim, mb: 2 }}>New here — we'll start your profile with this order!</Typography>}
              {orderType === "dinein" && !tableId && <Typography sx={{ fontSize: 12, color: PALETTE.alert, mb: 1 }}>Select your table number above before placing the order.</Typography>}
              {orderError && <Typography sx={{ fontSize: 12.5, color: PALETTE.alert, mb: 1, fontWeight: 600 }}>{orderError}</Typography>}
              <Button fullWidth variant="contained" size="large" disabled={placingOrder || !phone.trim() || (orderType === "dinein" && !tableId)} onClick={placeOrder}
                sx={{ bgcolor: PALETTE.garnet, "&:hover": { bgcolor: PALETTE.garnetLight } }}>{placingOrder ? "Placing order…" : `Place Order · ${fmtUSD2(total)}`}</Button>
            </>
          ) : (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 2 }}>
              <CheckCircle sx={{ fontSize: 48, color: PALETTE.sage }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Order confirmed!</Typography>
              <Typography sx={{ fontSize: 13, color: PALETTE.inkDim, textAlign: "center" }}>
                {orderType === "dinein" ? `Heading to your table shortly.` : `We'll have it ready for pickup/delivery shortly.`}
              </Typography>
              {earnedPoints && (
                <Stack spacing={0.75} alignItems="center">
                  <Chip icon={<Star sx={{ fontSize: 14 }} />} label={`You earned ${earnedPoints.points} points (${earnedPoints.tier} tier, ${earnedPoints.mult}x)`}
                    sx={{ bgcolor: alpha(PALETTE.brass, 0.15), color: PALETTE.brass, fontWeight: 700 }} />
                  <Typography sx={{ fontSize: 12, color: PALETTE.inkDim }}>New points balance: <b style={{ color: PALETTE.garnet }}>{pointsFor(foundCustomer, phone)} pts</b></Typography>
                </Stack>
              )}
              {myOrders[0] && (
                <Chip size="small" label={`Status: ${myOrders[0].status}`} sx={{ bgcolor: alpha(STATUS_COLOR[myOrders[0].status], 0.15), color: STATUS_COLOR[myOrders[0].status], textTransform: "capitalize", fontWeight: 700 }} />
              )}
              <Stack direction="row" spacing={1}>
                <Button onClick={() => { setCheckoutOpen(false); setTrackingOpen(true); }} variant="outlined" sx={{ borderColor: PALETTE.border, color: PALETTE.ink }}>Track my order</Button>
                <Button onClick={() => { setCheckoutOpen(false); setOrderDone(false); setPhone(""); setSearchedPhone(false); setFoundCustomer(null); setEarnedPoints(null); }} sx={{ color: PALETTE.garnet }}>Done</Button>
              </Stack>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={trackingOpen} onClose={()=>setTrackingOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Track your order</Typography>
          <Typography sx={{ fontSize: 12.5, color: PALETTE.inkDim, mb: 2 }}>Live status — updates automatically as the kitchen works through it. Lost this page? Look up your order by phone number any time.</Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField fullWidth size="small" placeholder="Enter the phone number you ordered with" value={trackPhone} onChange={(e)=>setTrackPhone(e.target.value)}
              onKeyDown={(e)=>e.key==="Enter"&&setTrackSearched(true)} InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ fontSize: 16 }} /></InputAdornment> }} />
            <Button variant="contained" onClick={()=>setTrackSearched(true)} sx={{ bgcolor: PALETTE.garnet, whiteSpace: "nowrap", "&:hover": { bgcolor: PALETTE.garnetLight } }}>Look up</Button>
          </Stack>
          <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.brass, fontWeight: 700, mb: 1 }}>Present & recent — live status</Typography>
          {displayedOrders.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: PALETTE.inkDim, mb: 2 }}>{trackSearched ? "No live orders found for that phone number." : "No orders placed yet this visit — or look up by phone above."}</Typography>
          ) : (
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              {displayedOrders.map((o) => (
                <Paper key={o.id} variant="outlined" sx={{ p: 1.5, borderRadius: 3, borderColor: PALETTE.border }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{o.orderType === "dinein" ? `Table ${o.table}` : "Online order"}</Typography>
                    <Chip size="small" label={o.status} sx={{ bgcolor: alpha(STATUS_COLOR[o.status], 0.15), color: STATUS_COLOR[o.status], textTransform: "capitalize", fontWeight: 700 }} />
                  </Stack>
                  <Typography sx={{ fontSize: 12, color: PALETTE.inkDim, mt: 0.5 }}>{o.items.join(", ")}</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Stack direction="row" spacing={0.5}>
                      {["queued","cooking","ready","served"].map((s, i) => {
                        const order = ["queued","cooking","ready","served"];
                        const reached = order.indexOf(o.status) >= i;
                        return <Box key={s} sx={{ flex: 1, height: 5, borderRadius: 3, bgcolor: reached ? PALETTE.garnet : PALETTE.border }} />;
                      })}
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.4 }}>
                      {["Received","Cooking","Ready","Served"].map((l) => <Typography key={l} sx={{ fontSize: 9.5, color: PALETTE.inkDim }}>{l}</Typography>)}
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Stack>
          )}

          {(() => {
            const historyCustomer = trackSearched && trackPhone.trim()
              ? customersRef.current.find((c) => c.phone.replace(/\D/g,"").includes(trackPhone.replace(/\D/g,"")))
              : (foundCustomer || null);
            if (!historyCustomer || !historyCustomer.orders?.length) return null;
            return (
              <>
                <Divider sx={{ mb: 1.5 }} />
                <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700, mb: 1 }}>Order history — past visits</Typography>
                <Stack spacing={1} sx={{ maxHeight: 220, overflowY: "auto" }}>
                  {historyCustomer.orders.slice(0, 10).map((o, i) => (
                    <Stack key={i} direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.2, py: 0.8, borderRadius: 2, bgcolor: PALETTE.bg }}>
                      <Typography sx={{ fontSize: 11.5, color: PALETTE.inkDim, minWidth: 70 }}>{o.date.toLocaleDateString("en-US",{month:"short",day:"numeric"})}</Typography>
                      <Typography sx={{ fontSize: 12, flexGrow: 1, px: 1 }}>{o.items.map(it=>it.name).join(", ")}</Typography>
                      <Typography sx={{ fontSize: 12, color: PALETTE.garnet, fontWeight: 700 }}>{fmtUSD2(o.total)}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            );
          })()}
          <Button fullWidth onClick={()=>setTrackingOpen(false)} sx={{ mt: 2, color: PALETTE.inkDim }}>Close</Button>
        </DialogContent>
      </Dialog>

      <FloatingAIConcierge onOpenReserve={onOpenReserve} menu={menu} onAddToCart={(item, seasoning) => addToCart(item, seasoning)} customers={customersRef.current} inventory={[]} wastageLog={[]} ledger={[]} />
    </Box>
  );
}

/* =============================== LOGIN SCREEN ============================== */

function LoginScreen({ onLogin, onBackToShop }) {
  const customers = useMemo(() => genCustomers(35, MENU_SEED), []);
  const [tab, setTab] = useState("staff");
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: PALETTE.bg, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", p: 2 }}>
      <Box sx={{ position: "absolute", inset: 0, opacity: 0.06, background: `radial-gradient(circle at 20% 20%, ${PALETTE.brass} 0, transparent 35%), radial-gradient(circle at 80% 70%, ${PALETTE.garnet} 0, transparent 40%)` }} />
      <Container maxWidth="lg" sx={{ position: "relative" }}>
        <Button startIcon={<ArrowBack />} onClick={onBackToShop} sx={{ color: PALETTE.inkDim, mb: 2 }}>Back to shop</Button>
        <Stack alignItems="center" spacing={1} sx={{ mb: 4 }}>
          <LocalFireDepartment sx={{ color: PALETTE.garnet, fontSize: 34 }} />
          <Typography variant="h3" sx={{ fontWeight: 700, color: PALETTE.ink, fontSize: { xs: 32, md: 42 } }}>Staff Sign In</Typography>
          <Typography sx={{ color: PALETTE.inkDim, fontSize: 14 }}>Lindey's Prime Steakhouse — Operations Hub</Typography>
        </Stack>
        <Paper variant="outlined" sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, borderColor: PALETTE.border, width: "100%" }}>
          <Tabs value={tab} onChange={(e,v)=>setTab(v)} sx={{ mb: 2, minHeight: 0 }} TabIndicatorProps={{ style: { backgroundColor: PALETTE.garnet } }}>
            <Tab value="staff" label="Staff" sx={{ textTransform: "none", fontWeight: 600, minHeight: 0 }} />
            <Tab value="customers" label="Customer Directory" sx={{ textTransform: "none", fontWeight: 600, minHeight: 0 }} />
          </Tabs>
          {tab === "staff" ? (
            <Grid container spacing={1.5} sx={{ width: '100%', margin: 0 }}>
              {STAFF.map((s) => (
                <Grid item xs={12} sm={4} key={s.id}>
                  <Card variant="outlined" onClick={() => onLogin(s)} sx={{ p: 2, cursor: "pointer", borderColor: PALETTE.border, borderRadius: 3, height: "100%", transition: "all .15s", "&:hover": { borderColor: PALETTE.brass, transform: "translateY(-2px)" } }}>
                    <Avatar sx={{ bgcolor: alpha(PALETTE.brass, 0.15), color: PALETTE.brass, width: 32, height: 32, mb: 1 }}><s.icon sx={{ fontSize: 17 }} /></Avatar>
                    <Chip label={s.role} size="small" sx={{ bgcolor: alpha(PALETTE.brass, 0.12), color: PALETTE.brass, mb: 1, fontSize: 10 }} />
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{s.name}</Typography>
                    <Typography sx={{ fontSize: 12, color: PALETTE.inkDim }}>{s.title}</Typography>
                    <Typography sx={{ fontSize: 12, color: PALETTE.garnet, fontFamily: "JetBrains Mono, monospace", mt: 1 }}>{s.phone}</Typography>
                  </Card>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Card variant="outlined" onClick={() => onLogin(WAITER)} sx={{ p: 2.5, cursor: "pointer", borderColor: PALETTE.border, borderRadius: 3, transition: "all .15s", "&:hover": { borderColor: PALETTE.brass, boxShadow: "0 8px 24px rgba(0,0,0,0.06)" } }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ bgcolor: alpha(PALETTE.sage, 0.15), color: PALETTE.sage }}><Groups sx={{ fontSize: 18 }} /></Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Front of House Terminal</Typography>
                      <Typography sx={{ fontSize: 12, color: PALETTE.inkDim }}>Take orders · Reservations · Customer memory · 612-555-0099</Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mt: 1, mb: 1 }}>Servants</Typography>
                <Grid container spacing={1.5} sx={{ width: '100%', margin: 0 }}>
                  {SERVANTS.map((s) => (
                    <Grid item xs={6} sm={4} md={2.4} key={s.id}>
                      <Card variant="outlined" onClick={() => onLogin(s)} sx={{ p: 1.5, cursor: "pointer", borderColor: PALETTE.border, borderRadius: 3, height: "100%", transition: "all .15s", "&:hover": { borderColor: PALETTE.sage, transform: "translateY(-2px)" } }}>
                        <Avatar sx={{ bgcolor: alpha(PALETTE.sage, 0.15), color: PALETTE.sage, width: 28, height: 28, mb: 0.75 }}><Groups sx={{ fontSize: 15 }} /></Avatar>
                        <Typography sx={{ fontWeight: 600, fontSize: 12.5 }}>{s.name}</Typography>
                        <Typography sx={{ fontSize: 10.5, color: PALETTE.garnet, fontFamily: "JetBrains Mono, monospace" }}>{s.phone}</Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
              <Grid container spacing={1} sx={{ width: '100%', margin: 0 }}>
                {customers.map((c) => (
                  <Grid item xs={12} sm={6} md={4} key={c.id}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1.2, borderRadius: 2, border: `1px solid ${PALETTE.border}` }}>
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{c.name}</Typography>
                        <Typography sx={{ fontSize: 11, color: PALETTE.inkDim, fontFamily: "JetBrains Mono, monospace" }}>{c.phone}</Typography>
                      </Box>
                      <Chip size="small" label={`${c.visits} visits`} sx={{ bgcolor: alpha(PALETTE.brass, 0.12), color: PALETTE.brass, fontSize: 10 }} />
                    </Stack>
                  </Grid>
                ))}
              </Grid>
              <Typography sx={{ fontSize: 11, color: PALETTE.inkDim, mt: 1.5 }}>Customers don't sign in — staff look them up by phone at order time.</Typography>
            </Box>
          )}
        </Paper>
        <Typography sx={{ textAlign: "center", fontSize: 11, color: PALETTE.inkDim, mt: 2 }}>Prototype demo build — all data hardcoded for presentation.</Typography>
      </Container>
    </Box>
  );
}

/* ============================ SALES ANALYTICS =============================== */
function loyaltyTier(totalSpend) {
  if (totalSpend >= 1200) return { label: "Gold", color: "#B5813A" };
  if (totalSpend >= 600) return { label: "Silver", color: "#8C8C8C" };
  return { label: "Bronze", color: "#A0522D" };
}

/* =============================== NEW — UPGRADED LOYALTY PANEL =============================== */
function LoyaltyProgramPanel({ customers = [], onNavigate }) {
  const [open, setOpen] = useState(false);
  const celebrations = useMemo(() => upcomingCelebrations(customers, 30), [customers]);

  return (
    <>
      <Chip
        onClick={() => onNavigate ? onNavigate("loyalty") : setOpen(true)}
        icon={<Star sx={{ fontSize: 13 }} />}
        label={`Loyalty & Special Offers${celebrations.length ? ` — ${celebrations.length} upcoming` : ""}`}
        clickable
        sx={{ mt: 1, width: "100%", bgcolor: alpha(PALETTE.brass, 0.12), color: PALETTE.brass, fontWeight: 700, justifyContent: "flex-start" }}
      />
    </>
  );
}

/* =============================== NEW — LOYALTY & SPECIAL OFFERS HUB (dedicated tab) =============================== */

const TIER_COLORS = { Bronze: "#A0522D", Silver: "#8C8C8C", Gold: "#B5813A" };
const RULE_LIBRARY = [
  { id: "gold-mult", label: "Gold — 3x points", type: "multiplier", tier: "Gold", value: 3 },
  { id: "silver-mult", label: "Silver — 2x points", type: "multiplier", tier: "Silver", value: 2 },
  { id: "bronze-mult", label: "Bronze — 1x points", type: "multiplier", tier: "Bronze", value: 1 },
  { id: "referral", label: "Refer a friend — 20% off", type: "referral", value: 20 },
  { id: "birthday", label: "Birthday — 12% off", type: "birthday", value: 12 },
  { id: "anniversary", label: "Anniversary — 17% off", type: "anniversary", value: 17 },
  { id: "gold-expiry", label: "Gold points expire — 6 mo", type: "expiry", tier: "Gold", value: 6 },
  { id: "silver-expiry", label: "Silver points expire — 4 mo", type: "expiry", tier: "Silver", value: 4 },
  { id: "bronze-expiry", label: "Bronze points expire — 2 mo", type: "expiry", tier: "Bronze", value: 2 },
  { id: "demand-pricing", label: "Demand-Based Pricing — 1.5x cost at peak", type: "demandPricing", value: { peakMultiplier: 1.5 } },
  { id: "cash-map", label: "Cash-to-Point Mapping — 1¢/point", type: "cashToPointRatio", value: { centsPerPoint: 1 } },
  { id: "expiry-decay", label: "Expiration Decay — alert @7d, expire @30d idle", type: "expiryDecay", value: { inactivityDays: 30, alertDays: 7 } },
  { id: "dynamic-discount", label: "Dynamic Discount — 15% off for low-engagement guests", type: "dynamicDiscount", value: { lowEngagementPct: 15 } },
  { id: "variable-reward", label: "Variable Rewards — surprise 5–50 pts/order", type: "variableRewards", value: { min: 5, max: 50 } },
  { id: "day-bonus", label: "Day Bonus — 2x points on Wednesdays", type: "dayBasedBonus", value: { day: "Wednesday", mult: 2 } },
  { id: "season-bonus", label: "Season Bonus — 1.5x points in Winter", type: "seasonBonus", value: { season: "Winter", mult: 1.5 } },
];
function TIER_PERKS_FOR(rules) {
  return {
    Bronze: `${rules.multipliers.Bronze}x points · points expire in ${rules.expiry.Bronze} mo`,
    Silver: `${rules.multipliers.Silver}x points + complimentary dip · points expire in ${rules.expiry.Silver} mo`,
    Gold: `${rules.multipliers.Gold}x points + complimentary dip and a small dessert · points expire in ${rules.expiry.Gold} mo`,
  };
}

function tierCounts(customers) {
  const counts = { Bronze: 0, Silver: 0, Gold: 0 };
  customers.forEach((c) => { counts[c.pointsTier || "Bronze"] = (counts[c.pointsTier || "Bronze"] || 0) + 1; });
  return ["Bronze", "Silver", "Gold"].map((tier) => ({ tier, count: counts[tier] || 0 }));
}

// Deterministic mock of "last month's" tier distribution, so the AI growth chart has something
// stable to compare against — seeded per-customer so it doesn't reshuffle on every render.
function lastMonthTierCounts(customers) {
  const counts = { Bronze: 0, Silver: 0, Gold: 0 };
  customers.forEach((c) => {
    const rng = seeded(c.id.length * 97 + c.name.length * 13 + 3);
    const r = rng();
    const tier = r > 0.8 ? "Gold" : r > 0.45 ? "Silver" : "Bronze";
    counts[tier]++;
  });
  return counts;
}

// Deterministic mock "tier achieved" history for the last 6 months, per customer — used to answer
// "how many times has this person hit Gold/Silver" in their detail dialog.
function tierAchievementHistory(customer) {
  const rng = seeded((customer.id?.length || 1) * 311 + (customer.phone?.length || 1) * 7 + customer.name.length);
  const months = ["Mar", "Apr", "May", "Jun", "Jul", "Aug"];
  return months.map((m) => {
    const r = rng();
    const tier = r > 0.72 ? "Gold" : r > 0.38 ? "Silver" : "Bronze";
    return { month: m, tier };
  });
}

function visitFrequencyDays(customer) {
  if (!customer.orders || customer.orders.length < 2) return null;
  const sorted = [...customer.orders].sort((a, b) => a.date - b.date);
  let totalGap = 0;
  for (let i = 1; i < sorted.length; i++) totalGap += (sorted[i].date - sorted[i - 1].date) / 86400000;
  return Math.max(1, Math.round(totalGap / (sorted.length - 1)));
}

function referralLink(customer) {
  const code = (customer.phone || "").replace(/\D/g, "").slice(-6) || "GUEST";
  return `https://lindeysprimesteakhouse.example/refer/${code}`;
}

// 3/8: aggregate points issued/redeemed for a given tier of customers
function tierPointsSummary(customersInTier) {
  const totalGiven = customersInTier.reduce((s, c) => s + (c.loyaltyPoints || 0), 0);
  const totalRedeemed = customersInTier.reduce((s, c) => s + Math.round((c.loyaltyPoints || 0) * 0.08), 0);
  const avgGiven = customersInTier.length ? Math.round(totalGiven / customersInTier.length) : 0;
  return { totalGiven, totalRedeemed, avgGiven, remaining: totalGiven - totalRedeemed };
}

async function downloadTierReportPDF(tier, customersInTier, summary) {
  try {
    const [{ default: jsPDF }] = await Promise.all([import("jspdf")]);
    const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    let y = 50;
    pdf.setFontSize(18); pdf.text(`${tier} Tier — Loyalty Report`, 40, y); y += 24;
    pdf.setFontSize(10); pdf.text(`Generated ${new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}`, 40, y); y += 24;
    pdf.setFontSize(12);
    pdf.text(`Customers in tier: ${customersInTier.length}`, 40, y); y += 18;
    pdf.text(`Total points given: ${summary.totalGiven}`, 40, y); y += 18;
    pdf.text(`Total points redeemed: ${summary.totalRedeemed}`, 40, y); y += 18;
    pdf.text(`Average points / customer: ${summary.avgGiven}`, 40, y); y += 28;
    pdf.setFontSize(11); pdf.text("Name", 40, y); pdf.text("Phone", 220, y); pdf.text("Points", 360, y); pdf.text("Redeemed", 440, y); y += 14;
    pdf.setLineWidth(0.5); pdf.line(40, y, 550, y); y += 12;
    pdf.setFontSize(10);
    customersInTier.forEach((c) => {
      if (y > 780) { pdf.addPage(); y = 50; }
      pdf.text(c.name, 40, y);
      pdf.text(c.phone, 220, y);
      pdf.text(String(c.loyaltyPoints || 0), 360, y);
      pdf.text(String(Math.round((c.loyaltyPoints || 0) * 0.08)), 440, y);
      y += 16;
    });
    pdf.save(`${tier.toLowerCase()}_tier_report.pdf`);
  } catch (err) {
    console.error("PDF generation failed — make sure 'jspdf' is installed (npm install jspdf).", err);
    alert("PDF export needs the 'jspdf' package installed in this project: npm install jspdf");
  }
}

function TierCustomersDialog({ tier, customers, onClose, onSelectCustomer }) {
  if (!tier) return null;
  const inTier = customers.filter((c) => c.pointsTier === tier);
  const summary = tierPointsSummary(inTier);
  return (
    <Dialog open={Boolean(tier)} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: alpha(TIER_COLORS[tier], 0.15), display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Star sx={{ fontSize: 17, color: TIER_COLORS[tier] }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{tier} Tier — {inTier.length} customers</Typography>
          </Stack>
          <IconButton size="small" onClick={onClose}><Close sx={{ fontSize: 18 }} /></IconButton>
        </Stack>
        <Grid container spacing={2} sx={{ width: "100%", margin: 0, mb: 2 }}>
          {[["Total points given", summary.totalGiven], ["Total redeemed", summary.totalRedeemed], ["Avg / customer", summary.avgGiven], ["Points remaining", summary.remaining]].map(([label, val]) => (
            <Grid item xs={6} sm={3} key={label}>
              <Typography sx={{ fontSize: 17, fontWeight: 700, color: TIER_COLORS[tier] }}>{val}</Typography>
              <Typography sx={{ fontSize: 10.5, color: PALETTE.inkDim }}>{label}</Typography>
            </Grid>
          ))}
        </Grid>
        <Button size="small" startIcon={<Download sx={{ fontSize: 14 }} />} variant="outlined"
          onClick={() => downloadTierReportPDF(tier, inTier, summary)}
          sx={{ mb: 2, borderColor: PALETTE.border, color: PALETTE.ink }}>Download PDF report</Button>
        <Box sx={{ maxHeight: 320, overflowY: "auto", border: `1px solid ${PALETTE.border}`, borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>{["Name", "Phone", "Points", "Redeemed"].map((h) => <TableCell key={h} sx={{ fontSize: 10.5, textTransform: "uppercase", color: PALETTE.inkDim, fontWeight: 700, bgcolor: PALETTE.bg }}>{h}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {inTier.map((c) => (
                <TableRow key={c.id} hover onClick={() => onSelectCustomer(c)} sx={{ cursor: "pointer" }}>
                  <TableCell sx={{ fontSize: 12.5 }}>{c.name}</TableCell>
                  <TableCell sx={{ fontSize: 12, color: PALETTE.inkDim, fontFamily: "JetBrains Mono, monospace" }}>{c.phone}</TableCell>
                  <TableCell sx={{ fontSize: 12.5, color: PALETTE.brass, fontWeight: 700 }}>{c.loyaltyPoints}</TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{Math.round((c.loyaltyPoints || 0) * 0.08)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Button fullWidth onClick={onClose} sx={{ mt: 2, color: PALETTE.inkDim }}>Close</Button>
      </DialogContent>
    </Dialog>
  );
}

function CustomerDetailDialog({ customer, onClose, loyaltyRules }) {
  if (!customer) return null;
  const rules = loyaltyRules || DEFAULT_LOYALTY_RULES;
  const history = useMemo(() => tierAchievementHistory(customer), [customer.id]);
  const goldMonths = history.filter((h) => h.tier === "Gold").length;
  const silverMonths = history.filter((h) => h.tier === "Silver").length;
  const freqDays = visitFrequencyDays(customer);
  const avgSpend = customer.visits ? +(customer.totalSpend / customer.visits).toFixed(2) : 0;
  const tierColor = TIER_COLORS[customer.pointsTier] || TIER_COLORS.Bronze;
  const [linkCopied, setLinkCopied] = useState(false);
  const [dTab, setDTab] = useState("overview");
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(referralLink(customer)); } catch { /* clipboard may be unavailable */ }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <Dialog open={Boolean(customer)} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}>
      <Box sx={{ bgcolor: PALETTE.bg, px: 3, py: 2.5, borderBottom: `1px solid ${PALETTE.border}`, position: "relative" }}>
        <IconButton onClick={onClose} sx={{ position: "absolute", top: 8, right: 8 }}><Close sx={{ fontSize: 18 }} /></IconButton>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ width: 60, height: 60, bgcolor: alpha(PALETTE.garnet, 0.15), color: PALETTE.garnet, fontSize: 22, fontWeight: 700 }}>
            {customer.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{customer.name}</Typography>
            <Stack direction="row" spacing={0.75} alignItems="center"><Phone sx={{ fontSize: 13, color: PALETTE.inkDim }} /><Typography sx={{ fontSize: 12.5, color: PALETTE.inkDim }}>{customer.phone}</Typography></Stack>
          </Box>
          <Stack alignItems="center" spacing={0.3}>
            <Box sx={{ width: 40, height: 40, borderRadius: "50%", bgcolor: alpha(tierColor, 0.15), display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Star sx={{ fontSize: 20, color: tierColor }} />
            </Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: tierColor }}>{customer.pointsTier}</Typography>
          </Stack>
        </Stack>
      </Box>
      <Tabs value={dTab} onChange={(e, v) => setDTab(v)} variant="fullWidth" sx={{ bgcolor: PALETTE.sage, minHeight: 0 }}
        TabIndicatorProps={{ style: { backgroundColor: "#fff" } }}>
        {["overview", "history", "rewards"].map((t) => (
          <Tab key={t} value={t} label={t === "overview" ? "Overview" : t === "history" ? "Order History" : "Rewards"}
            sx={{ textTransform: "capitalize", fontWeight: 700, fontSize: 12.5, color: "#fff", minHeight: 40, opacity: dTab === t ? 1 : 0.75 }} />
        ))}
      </Tabs>
      <DialogContent sx={{ p: 3 }}>
        {dTab === "overview" && (
          <>
            <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700, mb: 1.5 }}>Customer Highlights</Typography>
            <Grid container spacing={2} sx={{ width: "100%", margin: 0, mb: 3 }}>
              {[
                ["Orders", customer.visits],
                ["Avg Spend / Visit", fmtUSD2(avgSpend)],
                ["Lifetime Spend", fmtUSD2(customer.totalSpend)],
                ["Regular for", `${customer.memberSince} mo`],
                ["Avg Visit Frequency", freqDays ? `${freqDays} days` : "—"],
                ["Favorite Dish", customer.favorite],
              ].map(([label, val]) => (
                <Grid item xs={6} sm={4} key={label}>
                  <Typography sx={{ fontSize: 17, fontWeight: 700, color: PALETTE.sage }}>{val}</Typography>
                  <Typography sx={{ fontSize: 11, color: PALETTE.inkDim }}>{label}</Typography>
                </Grid>
              ))}
            </Grid>
            <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700, mb: 1.5 }}>Tier Achievement — last 6 months</Typography>
            <Stack direction="row" spacing={0.75} sx={{ mb: 1 }}>
              {history.map((h) => (
                <Stack key={h.month} alignItems="center" spacing={0.4} sx={{ flex: 1 }}>
                  <Box sx={{ width: "100%", height: 40, borderRadius: 1.5, bgcolor: alpha(TIER_COLORS[h.tier], 0.18), display: "flex", alignItems: "flex-end", justifyContent: "center", border: `1px solid ${alpha(TIER_COLORS[h.tier], 0.4)}` }}>
                    <Box sx={{ width: "70%", height: h.tier === "Gold" ? "90%" : h.tier === "Silver" ? "60%" : "30%", bgcolor: TIER_COLORS[h.tier], borderRadius: "3px 3px 0 0" }} />
                  </Box>
                  <Typography sx={{ fontSize: 10, color: PALETTE.inkDim }}>{h.month}</Typography>
                </Stack>
              ))}
            </Stack>
            <Typography sx={{ fontSize: 12, color: PALETTE.inkDim }}>Hit <b style={{ color: TIER_COLORS.Gold }}>Gold {goldMonths}×</b> and <b style={{ color: TIER_COLORS.Silver }}>Silver {silverMonths}×</b> in the last 6 months.</Typography>
            <Paper variant="outlined" sx={{ mt: 2, p: 1.5, borderRadius: 2, borderColor: PALETTE.brass, bgcolor: alpha(PALETTE.brass, 0.06) }}>
              <Stack direction="row" spacing={1}><AutoAwesome sx={{ fontSize: 15, color: PALETTE.brass, mt: 0.1 }} /><Typography sx={{ fontSize: 12.5 }}>{TIER_PERKS_FOR(rules)[customer.pointsTier]}</Typography></Stack>
            </Paper>
          </>
        )}
        {dTab === "history" && (
          <Stack spacing={1} sx={{ maxHeight: 360, overflowY: "auto" }}>
            {(customer.orders || []).map((o, i) => (
              <Stack key={i} direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.4, py: 1, borderRadius: 2, bgcolor: PALETTE.bg }}>
                <Typography sx={{ fontSize: 11.5, color: PALETTE.inkDim, minWidth: 70 }}>{o.date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</Typography>
                <Typography sx={{ fontSize: 12.5, flexGrow: 1, px: 1 }}>{o.items.map((it) => it.name).join(", ")}</Typography>
                <Typography sx={{ fontSize: 12.5, color: PALETTE.garnet, fontWeight: 700 }}>{fmtUSD2(o.total)}</Typography>
              </Stack>
            ))}
            {(!customer.orders || customer.orders.length === 0) && <Typography sx={{ fontSize: 13, color: PALETTE.inkDim }}>No orders on file yet.</Typography>}
          </Stack>
        )}
        {dTab === "rewards" && (
          <>
            <Grid container spacing={2} sx={{ width: "100%", margin: 0, mb: 2.5 }}>
              {[
                ["Available Points", customer.loyaltyPoints || 0, PALETTE.sage],
                ["Lifetime Points", customer.loyaltyPoints || 0, PALETTE.brass],
                ["Redeemed Points", Math.round((customer.loyaltyPoints || 0) * 0.08), PALETTE.garnet],
              ].map(([label, val, color]) => (
                <Grid item xs={4} key={label}>
                  <Typography sx={{ fontSize: 20, fontWeight: 700, color }}>{val}</Typography>
                  <Typography sx={{ fontSize: 10.5, color: PALETTE.inkDim }}>{label}</Typography>
                </Grid>
              ))}
            </Grid>
            <Typography sx={{ fontSize: 12, color: PALETTE.inkDim, mb: 2 }}>Points expire in <b>{rules.expiry[customer.pointsTier]} months</b> from the date earned.</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700, mb: 1 }}>Refer a friend</Typography>
            <Typography sx={{ fontSize: 12, color: PALETTE.inkDim, mb: 1 }}>Share this link — {customer.name.split(" ")[0]} and their friend both get {rules.referralPct}% off when a friend's first order is placed.</Typography>
            <Stack direction="row" spacing={1}>
              <TextField size="small" fullWidth value={referralLink(customer)} InputProps={{ readOnly: true }} />
              <Button variant="contained" onClick={copyLink} sx={{ bgcolor: PALETTE.garnet, whiteSpace: "nowrap", "&:hover": { bgcolor: PALETTE.garnetLight } }}>{linkCopied ? "Copied!" : "Copy link"}</Button>
            </Stack>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* =============================== NEW — LOYALTY RULES BUILDER (7) =============================== */
function RulesBuilder({ loyaltyRules, onChangeLive, savedOffers = [], onSaveOffer, onApplyOffer, onDeleteOffer, activeOffers = [], onUpdateActiveOfferBlockValue, onRemoveActiveOffer }) {
  const [draftBlocks, setDraftBlocks] = useState([]);
  const [offerName, setOfferName] = useState("");

  const addBlock = (block) => {
    if (draftBlocks.some((b) => b.id === block.id)) return;
    setDraftBlocks((d) => [...d, block]);
  };
  const removeBlock = (id) => setDraftBlocks((d) => d.filter((b) => b.id !== id));

  const applyDraftToLive = () => {
    const next = { multipliers: { ...loyaltyRules.multipliers }, expiry: { ...loyaltyRules.expiry }, referralPct: loyaltyRules.referralPct, birthdayPct: loyaltyRules.birthdayPct, anniversaryPct: loyaltyRules.anniversaryPct };
    draftBlocks.forEach((b) => {
      if (b.type === "multiplier") next.multipliers[b.tier] = b.value;
      if (b.type === "expiry") next.expiry[b.tier] = b.value;
      if (b.type === "referral") next.referralPct = b.value;
      if (b.type === "birthday") next.birthdayPct = b.value;
      if (b.type === "anniversary") next.anniversaryPct = b.value;
    });
    onChangeLive(next);
  };

  const saveOffer = () => {
    if (!offerName.trim() || draftBlocks.length === 0) return;
    onSaveOffer({ id: `offer-${Date.now()}`, name: offerName.trim(), blocks: draftBlocks });
    setOfferName("");
    setDraftBlocks([]);
  };

  return (
    <Stack spacing={2.5}>
      {/* TOP: currently active / selected rule set — this is what's live on the homepage banner */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.brass, bgcolor: alpha(PALETTE.brass, 0.05) }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <AutoAwesome sx={{ fontSize: 16, color: PALETTE.brass }} />
          <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.brass, fontWeight: 700 }}>Active rules — live on the homepage now</Typography>
        </Stack>
        <Grid container spacing={1.5} sx={{ width: "100%", margin: 0, mb: 1.5 }}>
          {["Bronze", "Silver", "Gold"].map((t) => (
            <Grid item xs={6} sm={2} key={t}>
              <Typography sx={{ fontSize: 10.5, color: PALETTE.inkDim, fontWeight: 700 }}>{t} multiplier</Typography>
              <TextField size="small" type="number" fullWidth value={loyaltyRules.multipliers[t]}
                onChange={(e) => onChangeLive({ ...loyaltyRules, multipliers: { ...loyaltyRules.multipliers, [t]: Math.max(1, parseInt(e.target.value) || 1) } })} />
            </Grid>
          ))}
          {["Bronze", "Silver", "Gold"].map((t) => (
            <Grid item xs={6} sm={2} key={`${t}-exp`}>
              <Typography sx={{ fontSize: 10.5, color: PALETTE.inkDim, fontWeight: 700 }}>{t} expiry (mo)</Typography>
              <TextField size="small" type="number" fullWidth value={loyaltyRules.expiry[t]}
                onChange={(e) => onChangeLive({ ...loyaltyRules, expiry: { ...loyaltyRules.expiry, [t]: Math.max(1, parseInt(e.target.value) || 1) } })} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={1.5} sx={{ width: "100%", margin: 0 }}>
          {[["referralPct", "Refer a friend %"], ["birthdayPct", "Birthday %"], ["anniversaryPct", "Anniversary %"]].map(([key, label]) => (
            <Grid item xs={12} sm={4} key={key}>
              <Typography sx={{ fontSize: 10.5, color: PALETTE.inkDim, fontWeight: 700 }}>{label}</Typography>
              <TextField size="small" type="number" fullWidth value={loyaltyRules[key]}
                onChange={(e) => onChangeLive({ ...loyaltyRules, [key]: Math.max(0, parseInt(e.target.value) || 0) })} />
            </Grid>
          ))}
        </Grid>

        {/* NEW — Applied saved offers show up here as their own editable fields, same styling as the fields above */}
        {activeOffers.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontSize: 10.5, color: PALETTE.inkDim, fontWeight: 700, mb: 1 }}>Applied offers — editable, live on the homepage</Typography>
            <Stack spacing={1.5}>
              {activeOffers.map((offer) => (
                <Paper key={offer.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: alpha(PALETTE.garnet, 0.3), bgcolor: "#fff" }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: PALETTE.garnet }}>{offer.name}</Typography>
                    <Button size="small" onClick={() => onRemoveActiveOffer(offer.id)} sx={{ color: PALETTE.alert, fontSize: 11, minWidth: 0 }}>Remove</Button>
                  </Stack>
                  <Grid  sx={{ width: "100%", margin: 0 }}>
                    {offer.blocks.map((b) => (
                      <Grid item xs={6} sm={4} key={b.id}>
                        <Typography sx={{ fontSize: 10, color: PALETTE.inkDim, fontWeight: 700 }}>{b.label.split(" — ")[0]}</Typography>
                        <TextField size="small" type="number" fullWidth value={typeof b.value === "object" ? Object.values(b.value)[0] : b.value}
                          onChange={(e) => onUpdateActiveOfferBlockValue(offer.id, b.id, parseFloat(e.target.value) || 0)} />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              ))}
            </Stack>
          </>
        )}
      </Paper>

      {/* MIDDLE: draft offer being composed */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border }}>
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700, mb: 1 }}>Draft offer — add blocks below, then save or apply</Typography>
        {draftBlocks.length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: PALETTE.inkDim, mb: 1.5 }}>No blocks added yet — click "+ Add" on a block in the library below.</Typography>
        ) : (
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
            {draftBlocks.map((b) => (
              <Chip key={b.id} label={b.label} onDelete={() => removeBlock(b.id)}
                sx={{ bgcolor: alpha(PALETTE.garnet, 0.1), color: PALETTE.garnet, fontWeight: 700 }} />
            ))}
          </Stack>
        )}
        <Stack direction="row" spacing={1}>
          <TextField size="small" fullWidth placeholder="Name this offer, e.g. 'Summer VIP Push'" value={offerName} onChange={(e) => setOfferName(e.target.value)} />
          <Button variant="outlined" disabled={!draftBlocks.length} onClick={applyDraftToLive} sx={{ borderColor: PALETTE.border, color: PALETTE.ink, whiteSpace: "nowrap" }}>Apply live</Button>
          <Button variant="contained" disabled={!draftBlocks.length || !offerName.trim()} onClick={saveOffer} sx={{ bgcolor: PALETTE.garnet, whiteSpace: "nowrap", "&:hover": { bgcolor: PALETTE.garnetLight } }}>Save offer</Button>
        </Stack>
      </Paper>

      {/* BOTTOM: rule block library + saved offers */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border }}>
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700, mb: 1 }}>Rule block library</Typography>
        <Grid container spacing={1} sx={{ width: "100%", margin: 0, mb: 2.5 }}>
          {RULE_LIBRARY.map((b) => (
            <Grid item xs={12} sm={6} md={4} key={b.id}>
              <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "space-between", borderColor: PALETTE.border }}>
                <Typography sx={{ fontSize: 12.5 }}>{b.label}</Typography>
                <Button size="small" onClick={() => addBlock(b)} sx={{ color: PALETTE.garnet, minWidth: 0, fontSize: 11.5 }}>+ Add</Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
        <Divider sx={{ mb: 2 }} />
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700, mb: 1 }}>Saved offers — click Apply to move it into "Active rules" above, or delete</Typography>
        {savedOffers.length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: PALETTE.inkDim }}>No offers saved yet — build one above and click "Save offer".</Typography>
        ) : (
          <Stack spacing={1}>
            {savedOffers.map((o) => {
              const isActive = activeOffers.some((a) => a.id === o.id);
              return (
                <Paper key={o.id} variant="outlined" sx={{ p: 1.2, borderRadius: 2, borderColor: isActive ? PALETTE.sage : PALETTE.border }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{o.name}</Typography>
                      {isActive && <Chip size="small" label="Active" sx={{ height: 18, fontSize: 9.5, bgcolor: alpha(PALETTE.sage, 0.15), color: PALETTE.sage, fontWeight: 700 }} />}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" disabled={isActive} onClick={() => onApplyOffer(o)} sx={{ color: PALETTE.sage, fontSize: 11.5 }}>{isActive ? "Applied" : "Apply"}</Button>
                      <Button size="small" onClick={() => onDeleteOffer(o.id)} sx={{ color: PALETTE.alert, fontSize: 11.5 }}>Delete</Button>
                    </Stack>
                  </Stack>
                  <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.75 }}>
                    {o.blocks.map((b) => <Chip key={b.id} size="small" label={b.label} sx={{ height: 20, fontSize: 10, bgcolor: alpha(PALETTE.brass, 0.12), color: PALETTE.brass }} />)}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}

// MODIFIED: add clickable KPI cards in Program Performance Metrics
function LoyaltyOffersHub({ customers = [], loyaltyRules, onChangeLoyaltyRules, savedOffers, onSaveOffer, onApplyOffer, onDeleteOffer, activeOffers = [], onUpdateActiveOfferBlockValue, onRemoveActiveOffer }) {
  const [comboIdea, setComboIdea] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null); // 1/3/8: click a bar -> tier customer list
  const [search, setSearch] = useState("");
  const today = new Date();
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayDialogOpen, setDayDialogOpen] = useState(false);
  const [spinningBday, setSpinningBday] = useState(false);
  const [bdayWinner, setBdayWinner] = useState(null);
  const [bdaySpinDeg, setBdaySpinDeg] = useState(0);
  const [spinningAnniv, setSpinningAnniv] = useState(false);
  const [annivWinner, setAnnivWinner] = useState(null);
  const [annivSpinDeg, setAnnivSpinDeg] = useState(0);
  const rules = loyaltyRules || DEFAULT_LOYALTY_RULES;

  // State for metric detail popups
  const [metricDetail, setMetricDetail] = useState(null);
  const [metricRows, setMetricRows] = useState([]);
  const [metricColumns, setMetricColumns] = useState([]);
  const [metricTitle, setMetricTitle] = useState("");

  const celebrations = useMemo(() => upcomingCelebrations(customers, 30), [customers]);
  const tripWinners = useMemo(() => coupleTripWinners(customers), [customers]);
  const tierData = useMemo(() => tierCounts(customers), [customers]);
  const lastMonth = useMemo(() => lastMonthTierCounts(customers), [customers]);
  const growthData = useMemo(() => tierData.map((t) => ({ tier: t.tier, "Last Month": lastMonth[t.tier], "This Month": t.count })), [tierData, lastMonth]);
  const avgSpendByTier = useMemo(() => {
    const groups = { Bronze: [], Silver: [], Gold: [] };
    customers.forEach((c) => (groups[c.pointsTier || "Bronze"] || groups.Bronze).push(c.totalSpend));
    return Object.entries(groups).map(([tier, arr]) => ({ tier, avg: arr.length ? +(arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(2) : 0 }));
  }, [customers]);

  // 4: single "current status" — the tier most customers are sitting in right now
  const dominantTier = useMemo(() => [...tierData].sort((a, b) => b.count - a.count)[0]?.tier || "Bronze", [tierData]);

  // 3/8: loyalty program performance metrics
  const programMetrics = useMemo(() => {
    const total = customers.length || 1;
    const activeThisMonth = customers.filter((c) => (c.thisMonthSpend || 0) > 0).length;
    const retained = customers.filter((c) => c.visits > 1).length;
    const avgVisits = +(customers.reduce((s, c) => s + c.visits, 0) / total).toFixed(1);
    const clv = +(customers.reduce((s, c) => s + c.totalSpend, 0) / total).toFixed(2);
    const avgTxn = +(customers.reduce((s, c) => s + (c.visits ? c.totalSpend / c.visits : 0), 0) / total).toFixed(2);
    const totalLifetimePoints = customers.reduce((s, c) => s + (c.loyaltyPoints || 0), 0);
    const totalRedeemed = customers.reduce((s, c) => s + Math.round((c.loyaltyPoints || 0) * 0.08), 0);
    const redemptionRate = totalLifetimePoints ? +((totalRedeemed / totalLifetimePoints) * 100).toFixed(1) : 0;
    const now = new Date();
    const churned = customers.filter((c) => {
      if (!c.orders?.length) return true;
      const mostRecent = c.orders.reduce((latest, o) => (o.date > latest ? o.date : latest), c.orders[0].date);
      return (now - mostRecent) / 86400000 > 90;
    }).length;
    const churnRate = +((churned / total) * 100).toFixed(1);
    const referralRng = seeded(total * 271 + 11);
    const referralActivity = Math.round(total * (0.12 + referralRng() * 0.1));
    const rank = { Bronze: 0, Silver: 1, Gold: 2 };
    const progressed = customers.filter((c) => {
      const rng = seeded(c.id.length * 97 + c.name.length * 13 + 3);
      const r = rng();
      const lastTier = r > 0.8 ? "Gold" : r > 0.45 ? "Silver" : "Bronze";
      return rank[c.pointsTier] > rank[lastTier];
    }).length;
    const tierProgressionRate = +((progressed / total) * 100).toFixed(1);
    return {
      enrollmentRate: 100,
      activeParticipationRate: +((activeThisMonth / total) * 100).toFixed(1),
      redemptionRate, retentionRate: +((retained / total) * 100).toFixed(1),
      repeatPurchaseFrequency: avgVisits, clv, avgTxn, churnRate, referralActivity, tierProgressionRate,
      totalLifetimePoints, totalRedeemed,
    };
  }, [customers]);

  // Helper to open metric detail dialog
  const openMetricDetail = (label, rows, columns, title) => {
    setMetricTitle(title || label);
    setMetricRows(rows);
    setMetricColumns(columns);
    setMetricDetail(true);
  };

  const submitCombo = async () => {
    if (!comboIdea.trim()) return;
    const text = comboIdea.trim();
    setSuggestions((s) => [{ id: Date.now(), text }, ...s]);
    setComboIdea("");
    try { await apiPost("/save/combo-suggestion", { text, phone: "" }); } catch { /* server route optional — kept locally either way */ }
  };

  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const firstWeekday = new Date(calYear, calMonth, 1).getDay();
  const celebrantsByDay = useMemo(() => {
    const map = {};
    customers.forEach((c) => {
      if (c.birthday) {
        const [mm, dd] = c.birthday.split("-").map(Number);
        if (mm - 1 === calMonth) (map[dd] = map[dd] || []).push({ ...c, celType: "Birthday" });
      }
      if (c.anniversary) {
        const [mm, dd] = c.anniversary.split("-").map(Number);
        if (mm - 1 === calMonth) (map[dd] = map[dd] || []).push({ ...c, celType: "Anniversary" });
      }
    });
    return map;
  }, [customers, calMonth]);

  const eligibleBirthday = useMemo(() => customers.filter((c) => c.birthday && Number(c.birthday.split("-")[0]) - 1 === calMonth), [customers, calMonth]);
  const eligibleAnniv = useMemo(() => customers.filter((c) => c.anniversary && Number(c.anniversary.split("-")[0]) - 1 === calMonth), [customers, calMonth]);

  const spinBirthday = () => {
    if (!eligibleBirthday.length || spinningBday) return;
    setBdayWinner(null);
    setSpinningBday(true);
    setBdaySpinDeg((d) => d + 1440 + Math.floor(Math.random() * 360));
    setTimeout(() => { setBdayWinner(eligibleBirthday[Math.floor(Math.random() * eligibleBirthday.length)]); setSpinningBday(false); }, 1600);
  };
  const spinAnniv = () => {
    if (!eligibleAnniv.length || spinningAnniv) return;
    setAnnivWinner(null);
    setSpinningAnniv(true);
    setAnnivSpinDeg((d) => d + 1440 + Math.floor(Math.random() * 360));
    setTimeout(() => { setAnnivWinner(eligibleAnniv[Math.floor(Math.random() * eligibleAnniv.length)]); setSpinningAnniv(false); }, 1600);
  };

  const filteredCustomers = customers.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()) || c.phone.includes(search));
  const monthLabel = new Date(calYear, calMonth, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const perks = TIER_PERKS_FOR(rules);

  return (
    <Stack spacing={3}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}>
        <SectionHead eyebrow="AI Loyalty Intelligence" title="Loyalty & Special Offers" />
        {/* 4: single current program status badge */}
        <Chip icon={<Star sx={{ fontSize: 14 }} />} label={`Program status: mostly ${dominantTier} this month`}
          sx={{ bgcolor: alpha(TIER_COLORS[dominantTier], 0.15), color: TIER_COLORS[dominantTier], fontWeight: 700, height: 34 }} />
      </Box>

      {/* Loyalty program performance metrics — enrollment, participation, redemption, retention,
          repeat purchase frequency, CLV, avg transaction, churn, referral activity, tier progression */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border, bgcolor: alpha(PALETTE.brass, 0.03) }}>
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700, mb: 1.5 }}>Program Performance Metrics</Typography>
        <Grid container spacing={2} sx={{ width: "100%", margin: 0 }}>
          <Grid item xs={6} sm={4} md={2.4}>
            <Box onClick={() => openMetricDetail(
              "Enrollment Rate",
              customers.map(c => ({ name: c.name, phone: c.phone, tier: c.pointsTier })),
              [
                { key: "name", label: "Name" },
                { key: "phone", label: "Phone" },
                { key: "tier", label: "Tier" }
              ],
              "All Customers (Enrolled)"
            )} sx={{ cursor: "pointer" }}>
              <KpiCard icon={CheckCircle} label="Enrollment Rate" value={`${programMetrics.enrollmentRate}%`} />
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Box onClick={() => openMetricDetail(
              "Active Participation",
              customers.filter(c => (c.thisMonthSpend || 0) > 0).map(c => ({ name: c.name, phone: c.phone, thisMonth: fmtUSD2(c.thisMonthSpend), visits: c.visits })),
              [
                { key: "name", label: "Name" },
                { key: "phone", label: "Phone" },
                { key: "thisMonth", label: "This Month Spend" },
                { key: "visits", label: "Visits" }
              ],
              "Active Customers This Month"
            )} sx={{ cursor: "pointer" }}>
              <KpiCard icon={Groups} label="Active Participation" value={`${programMetrics.activeParticipationRate}%`} delta="this month" />
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Box onClick={() => openMetricDetail(
              "Redemption Rate",
              customers.map(c => ({ name: c.name, phone: c.phone, points: c.loyaltyPoints, redeemed: Math.round((c.loyaltyPoints || 0) * 0.08) })),
              [
                { key: "name", label: "Name" },
                { key: "phone", label: "Phone" },
                { key: "points", label: "Points" },
                { key: "redeemed", label: "Redeemed" }
              ],
              "Redemption Details"
            )} sx={{ cursor: "pointer" }}>
              <KpiCard icon={Star} label="Redemption Rate" value={`${programMetrics.redemptionRate}%`} />
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Box onClick={() => openMetricDetail(
              "Retention Rate",
              customers.filter(c => c.visits > 1).map(c => ({ name: c.name, phone: c.phone, visits: c.visits, totalSpend: fmtUSD2(c.totalSpend) })),
              [
                { key: "name", label: "Name" },
                { key: "phone", label: "Phone" },
                { key: "visits", label: "Visits" },
                { key: "totalSpend", label: "Total Spend" }
              ],
              "Retained Customers (2+ visits)"
            )} sx={{ cursor: "pointer" }}>
              <KpiCard icon={TrendingUp} label="Retention Rate" value={`${programMetrics.retentionRate}%`} />
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Box onClick={() => openMetricDetail(
              "Repeat Purchase Frequency",
              customers.map(c => ({ name: c.name, phone: c.phone, visits: c.visits })),
              [
                { key: "name", label: "Name" },
                { key: "phone", label: "Phone" },
                { key: "visits", label: "Visits" }
              ],
              "Visit Counts"
            )} sx={{ cursor: "pointer" }}>
              <KpiCard icon={RestaurantMenu} label="Repeat Purchase Freq." value={`${programMetrics.repeatPurchaseFrequency} visits`} />
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Box onClick={() => openMetricDetail(
              "Customer Lifetime Value",
              customers.map(c => ({ name: c.name, phone: c.phone, lifetime: fmtUSD2(c.totalSpend) })),
              [
                { key: "name", label: "Name" },
                { key: "phone", label: "Phone" },
                { key: "lifetime", label: "Lifetime Spend" }
              ],
              "CLV by Customer"
            )} sx={{ cursor: "pointer" }}>
              <KpiCard icon={AttachMoney} label="Customer Lifetime Value" value={fmtUSD2(programMetrics.clv)} />
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Box onClick={() => openMetricDetail(
              "Avg Transaction Value",
              customers.map(c => ({ name: c.name, phone: c.phone, avg: fmtUSD2(c.visits ? c.totalSpend / c.visits : 0) })),
              [
                { key: "name", label: "Name" },
                { key: "phone", label: "Phone" },
                { key: "avg", label: "Avg per Visit" }
              ],
              "Avg Transaction per Customer"
            )} sx={{ cursor: "pointer" }}>
              <KpiCard icon={AttachMoney} label="Avg Transaction Value" value={fmtUSD2(programMetrics.avgTxn)} />
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Box onClick={() => openMetricDetail(
              "Churn Rate",
              customers.filter(c => {
                if (!c.orders?.length) return true;
                const mostRecent = c.orders.reduce((latest, o) => (o.date > latest ? o.date : latest), c.orders[0].date);
                return (new Date() - mostRecent) / 86400000 > 90;
              }).map(c => ({ name: c.name, phone: c.phone, lastVisit: c.orders?.[0]?.date.toLocaleDateString() || "Never" })),
              [
                { key: "name", label: "Name" },
                { key: "phone", label: "Phone" },
                { key: "lastVisit", label: "Last Visit" }
              ],
              "Churned Customers (90+ days inactive)"
            )} sx={{ cursor: "pointer" }}>
              <KpiCard icon={WarningAmber} label="Churn Rate" value={`${programMetrics.churnRate}%`} positive={false} delta="90+ days inactive" />
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Box onClick={() => openMetricDetail(
              "Referral Activity",
              customers.map(c => ({ name: c.name, phone: c.phone, referrals: Math.round(Math.random() * 5) })),
              [
                { key: "name", label: "Name" },
                { key: "phone", label: "Phone" },
                { key: "referrals", label: "Referrals" }
              ],
              "Referral Activity (mock)"
            )} sx={{ cursor: "pointer" }}>
              <KpiCard icon={Send} label="Referral Activity" value={programMetrics.referralActivity} delta="links shared" />
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Box onClick={() => openMetricDetail(
              "Tier Progression",
              customers.filter(c => {
                const rng = seeded(c.id.length * 97 + c.name.length * 13 + 3);
                const r = rng();
                const lastTier = r > 0.8 ? "Gold" : r > 0.45 ? "Silver" : "Bronze";
                return { Bronze: 0, Silver: 1, Gold: 2 }[c.pointsTier] > { Bronze: 0, Silver: 1, Gold: 2 }[lastTier];
              }).map(c => ({ name: c.name, phone: c.phone, tier: c.pointsTier })),
              [
                { key: "name", label: "Name" },
                { key: "phone", label: "Phone" },
                { key: "tier", label: "Current Tier" }
              ],
              "Customers Who Moved Up a Tier"
            )} sx={{ cursor: "pointer" }}>
              <KpiCard icon={AutoAwesome} label="Tier Progression" value={`${programMetrics.tierProgressionRate}%`} delta="moved up a tier" />
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Box onClick={() => openMetricDetail(
              "Total Points Given",
              customers.map(c => ({ name: c.name, phone: c.phone, points: c.loyaltyPoints })),
              [
                { key: "name", label: "Name" },
                { key: "phone", label: "Phone" },
                { key: "points", label: "Points" }
              ],
              "Lifetime Points by Customer"
            )} sx={{ cursor: "pointer" }}>
              <KpiCard icon={Star} label="Total Points Given" value={programMetrics.totalLifetimePoints} />
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Box onClick={() => openMetricDetail(
              "Total Points Redeemed",
              customers.map(c => ({ name: c.name, phone: c.phone, redeemed: Math.round((c.loyaltyPoints || 0) * 0.08) })),
              [
                { key: "name", label: "Name" },
                { key: "phone", label: "Phone" },
                { key: "redeemed", label: "Redeemed" }
              ],
              "Points Redeemed by Customer"
            )} sx={{ cursor: "pointer" }}>
              <KpiCard icon={Star} label="Total Points Redeemed" value={programMetrics.totalRedeemed} />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Metric detail dialog */}
      <DetailsDialog
        open={metricDetail}
        onClose={() => setMetricDetail(false)}
        title={metricTitle}
        subtitle={`${metricRows.length} records`}
        columns={metricColumns}
        rows={metricRows}
      />

      {/* 1/3/8: tier bar + block(pie) chart — click a bar to see who's in that tier, points given/redeemed, and export */}
      <Grid container spacing={2.5} sx={{ width: "100%", margin: 0 }}>
        <Grid item xs={12} sx={{width:'49%'}} md={6}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border, height: "100%" }}>
            <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 1 }}>Customer status — Bronze / Silver / Gold — click a bar for details</Typography>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={tierData}>
                <CartesianGrid strokeDasharray="3 3" stroke={PALETTE.border} />
                <XAxis dataKey="tier" tick={{ fill: PALETTE.inkDim, fontSize: 11 }} />
                <YAxis tick={{ fill: PALETTE.inkDim, fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" name="Customers" radius={[6, 6, 0, 0]} cursor="pointer" onClick={(d) => setSelectedTier(d.tier)}>
                  {tierData.map((t) => <Cell key={t.tier} fill={TIER_COLORS[t.tier]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item sx={{width:'49%'}} xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border, height: "100%" }}>
            <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 1 }}>Customer status — share (block chart) — click a slice for details</Typography>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={tierData} dataKey="count" nameKey="tier" innerRadius={0} outerRadius={85} paddingAngle={2} cursor="pointer"
                  onClick={(d) => setSelectedTier(d.tier)}
                  label={({ tier, percent }) => `${tier} ${(percent * 100).toFixed(0)}%`} labelLine={{ stroke: PALETTE.inkDim, strokeWidth: 1 }}>
                  {tierData.map((t) => <Cell key={t.tier} fill={TIER_COLORS[t.tier]} />)}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
                <Legend verticalAlign="bottom" iconType="square" wrapperStyle={{ fontSize: 11, color: PALETTE.inkDim }} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      <TierCustomersDialog tier={selectedTier} customers={customers} onClose={() => setSelectedTier(null)} onSelectCustomer={(c) => { setSelectedTier(null); setSelectedCustomer(c); }} />

      {/* AI growth vs last month — bars clickable too */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}><AutoAwesome sx={{ fontSize: 16, color: PALETTE.brass }} /><Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700 }}>AI: tier growth vs last month — click a bar for that tier's detail</Typography></Stack>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={growthData}>
            <CartesianGrid strokeDasharray="3 3" stroke={PALETTE.border} />
            <XAxis dataKey="tier" tick={{ fill: PALETTE.inkDim, fontSize: 11 }} />
            <YAxis tick={{ fill: PALETTE.inkDim, fontSize: 11 }} allowDecimals={false} />
            <Tooltip content={<ChartTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11, color: PALETTE.inkDim }} />
            <Bar dataKey="Last Month" fill={PALETTE.border} radius={[4, 4, 0, 0]} cursor="pointer" onClick={(d) => setSelectedTier(d.tier)} />
            <Bar dataKey="This Month" fill={PALETTE.garnet} radius={[4, 4, 0, 0]} cursor="pointer" onClick={(d) => setSelectedTier(d.tier)} />
          </BarChart>
        </ResponsiveContainer>
        <Typography sx={{ fontSize: 12.5, color: PALETTE.inkDim, mt: 1 }}>
          {(() => {
            const g = growthData.find((t) => t.tier === "Gold");
            const diff = g["This Month"] - g["Last Month"];
            return diff > 0 ? `Gold-tier customers are up ${diff} vs last month — the current promotion is working.` : diff < 0 ? `Gold-tier customers are down ${Math.abs(diff)} vs last month — worth a re-engagement push.` : `Gold-tier count is flat vs last month.`;
          })()}
        </Typography>
      </Paper>

      {/* trip winners list */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border }}>
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700, mb: 1 }}>Couples' Trip Special — winners history</Typography>
        {tripWinners.length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: PALETTE.inkDim }}>No couples selected yet this month.</Typography>
        ) : (
          <Grid container spacing={1} sx={{ width: "100%", margin: 0 }}>
            {tripWinners.map((c) => (
              <Grid item xs={12} sm={6} md={4} key={c.id}>
                <Paper variant="outlined" onClick={() => setSelectedCustomer(c)} sx={{ p: 1.2, borderRadius: 2, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", "&:hover": { borderColor: PALETTE.brass } }}>
                  <Typography sx={{ fontSize: 12.5 }}>{c.name}</Typography>
                  <Chip size="small" label={`${c.discountPct}% off`} sx={{ bgcolor: alpha(PALETTE.sage, 0.15), color: PALETTE.sage, fontWeight: 700 }} />
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
<Box sx={{display:'flex',justifyContent:'space-between', width:'100%'}}>
{/* 5: birthday calendar — made smaller/neater */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: PALETTE.border, width:'40%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700 }}>Birthday & Anniversary Calendar</Typography>
          <Stack direction="row" spacing={0.25} alignItems="center">
            <IconButton size="small" onClick={() => { const m = calMonth - 1; if (m < 0) { setCalMonth(11); setCalYear((y) => y - 1); } else setCalMonth(m); setSelectedDay(null); }}><ChevronLeft sx={{ fontSize: 15 }} /></IconButton>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, minWidth: 96, textAlign: "center" }}>{monthLabel}</Typography>
            <IconButton size="small" onClick={() => { const m = calMonth + 1; if (m > 11) { setCalMonth(0); setCalYear((y) => y + 1); } else setCalMonth(m); setSelectedDay(null); }}><ChevronRight sx={{ fontSize: 15 }} /></IconButton>
          </Stack>
        </Stack>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.35, mb: 0.5 }}>
          {["Su","Mo","Tu","We","Th","Fr","Sa"].map((d) => <Typography key={d} sx={{ fontSize: 9, color: PALETTE.inkDim, textAlign: "center", fontWeight: 700 }}>{d}</Typography>)}
          {Array.from({ length: firstWeekday }).map((_, i) => <Box key={`pad-${i}`} />)}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const people = celebrantsByDay[day];
            const hasBday = people?.some((p) => p.celType === "Birthday");
            const hasAnniv = people?.some((p) => p.celType === "Anniversary");
            return (
              <Box key={day} onClick={() => { if (people) { setSelectedDay(day); setDayDialogOpen(true); } }} sx={{
                aspectRatio: "1", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                borderRadius: 1, fontSize: 9.5, cursor: people ? "pointer" : "default", minHeight: 22,
                bgcolor: selectedDay === day ? PALETTE.garnet : people ? alpha(hasBday ? PALETTE.garnet : PALETTE.sage, 0.14) : PALETTE.bg,
                color: selectedDay === day ? "#fff" : PALETTE.ink, fontWeight: people ? 700 : 400,
                border: people ? `1px solid ${alpha(hasBday ? PALETTE.garnet : PALETTE.sage, 0.4)}` : "none" }}>
                {day}
                {people && <Box sx={{ display: "flex", gap: 0.25, mt: 0.1 }}>
                  {hasBday && <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: selectedDay === day ? "#fff" : PALETTE.garnet }} />}
                  {hasAnniv && <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: selectedDay === day ? "#fff" : PALETTE.sage }} />}
                </Box>}
              </Box>
            );
          })}
        </Box>
     
      </Paper>
      {/* spin wheel selection */}
      <Grid container spacing={2.5} sx={{ display:'flex',flexDirection:'row',justifyContent:'space-around',width: "60%", margin: 0 }}>
        {[
          { label: "Birthday Winner Spin", eligible: eligibleBirthday, spinning: spinningBday, winner: bdayWinner, deg: bdaySpinDeg, onSpin: spinBirthday, color: PALETTE.garnet },
          { label: "Anniversary Winner Spin", eligible: eligibleAnniv, spinning: spinningAnniv, winner: annivWinner, deg: annivSpinDeg, onSpin: spinAnniv, color: PALETTE.sage },
        ].map((s) => (
          <Grid item xs={12} md={6} key={s.label}>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border, textAlign: "center" }}>
              <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700, mb: 1.5 }}>{s.label} — {monthLabel.split(" ")[0]} ({s.eligible.length} eligible)</Typography>
              <Box sx={{ width: 130, height: 130, borderRadius: "50%", mx: "auto", mb: 1.5, position: "relative",
                background: `conic-gradient(${s.color} 0deg 60deg, ${alpha(s.color,0.5)} 60deg 120deg, ${s.color} 120deg 180deg, ${alpha(s.color,0.5)} 180deg 240deg, ${s.color} 240deg 300deg, ${alpha(s.color,0.5)} 300deg 360deg)`,
                transform: `rotate(${s.deg}deg)`, transition: s.spinning ? "transform 1.6s cubic-bezier(.17,.67,.32,1.02)" : "none",
                border: `4px solid ${PALETTE.bg}`, boxShadow: "0 4px 14px rgba(0,0,0,0.15)" }}>
                <Star sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", color: "#fff", fontSize: 22 }} />
              </Box>
              <Button variant="contained" disabled={!s.eligible.length || s.spinning} onClick={s.onSpin}
                sx={{ bgcolor: s.color, "&:hover": { bgcolor: alpha(s.color, 0.85) } }}>{s.spinning ? "Spinning…" : "Spin for a winner"}</Button>
              {s.winner && (
                <Paper variant="outlined" onClick={() => setSelectedCustomer(s.winner)} sx={{ mt: 1.5, p: 1.2, borderRadius: 2, cursor: "pointer", borderColor: s.color, bgcolor: alpha(s.color, 0.08) }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>🎉 {s.winner.name}</Typography>
                  <Typography sx={{ fontSize: 11, color: PALETTE.inkDim }}>Click to view full profile</Typography>
                </Paper>
              )}
              {!s.eligible.length && <Typography sx={{ fontSize: 11.5, color: PALETTE.inkDim, mt: 1.5 }}>No eligible customers this month.</Typography>}
            </Paper>
          </Grid>
        ))}
      </Grid>

</Box>
      

      {/* calendar day click -> popup with a grid of that day's celebrants (name + birthday/anniversary) */}
      <Dialog open={dayDialogOpen} onClose={() => setDayDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogContent>
          <Stack direction="row"  justifyContent="space-between" alignItems="center" sx={{ mb: 1.5,display:'flex',justifyContent:'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{monthLabel.split(" ")[0]} {selectedDay} — Celebrations</Typography>
            <IconButton size="small" onClick={() => setDayDialogOpen(false)}><Close sx={{ fontSize: 18 }} /></IconButton>
          </Stack>
          <Typography sx={{ fontSize: 12, color: PALETTE.inkDim, mb: 2 }}>
            {selectedDay && celebrantsByDay[selectedDay]?.length > 1
              ? `${celebrantsByDay[selectedDay].length} people celebrate on this date — click a card for their full profile.`
              : "Click the card below for their full profile."}
          </Typography>
          <Grid container spacing={1.5} sx={{ display:'flex',justifyContent:'space-around',width: "100%", margin: 0 }}>
            {selectedDay && (celebrantsByDay[selectedDay] || []).map((p, i) => (
              <Grid item xs={12} sm={(celebrantsByDay[selectedDay]?.length || 1) > 1 ? 6 : 12} key={i}>
                <Paper variant="outlined" onClick={() => { setDayDialogOpen(false); setSelectedCustomer(p); }}
                  sx={{ p: 1.75, borderRadius: 3, cursor: "pointer", borderColor: p.celType === "Birthday" ? PALETTE.garnet : PALETTE.sage,
                    bgcolor: alpha(p.celType === "Birthday" ? PALETTE.garnet : PALETTE.sage, 0.06), transition: "transform .15s", "&:hover": { transform: "translateY(-2px)" } }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ bgcolor: alpha(p.celType === "Birthday" ? PALETTE.garnet : PALETTE.sage, 0.2), color: p.celType === "Birthday" ? PALETTE.garnet : PALETTE.sage, width: 40, height: 40, fontSize: 15, fontWeight: 700 }}>
                      {p.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{p.name}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: PALETTE.inkDim, fontFamily: "JetBrains Mono, monospace" }}>{p.phone}</Typography>
                    </Box>
                    <Chip size="small" label={p.celType} sx={{ bgcolor: p.celType === "Birthday" ? PALETTE.garnet : PALETTE.sage, color: "#fff", fontWeight: 700 }} />
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
          <Button fullWidth onClick={() => setDayDialogOpen(false)} sx={{ mt: 2, color: PALETTE.inkDim }}>Close</Button>
        </DialogContent>
      </Dialog>

     

      {/* 9: tier perks + configurable points expiry */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border }}>
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700, mb: 1.5 }}>Tier Perks — complimentary extras & points expiry</Typography>
        <Grid container spacing={1.5} sx={{ width: "100%", margin: 0 }}>
          {["Bronze", "Silver", "Gold"].map((t) => (
            <Grid item xs={12} sm={4} key={t}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: alpha(TIER_COLORS[t], 0.4), bgcolor: alpha(TIER_COLORS[t], 0.06), height: "100%" }}>
                <Typography sx={{ fontWeight: 700, color: TIER_COLORS[t], mb: 0.5 }}>{t}</Typography>
                <Typography sx={{ fontSize: 12, color: PALETTE.inkDim }}>{perks[t]}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* 7: rules builder — active rules on top, block library + saved offers below */}
      {/* <RulesBuilder loyaltyRules={rules} onChangeLive={onChangeLoyaltyRules} savedOffers={savedOffers} onSaveOffer={onSaveOffer} onApplyOffer={onApplyOffer} onDeleteOffer={onDeleteOffer} /> */}
  <RulesBuilder
    loyaltyRules={rules}
    onChangeLive={onChangeLoyaltyRules}
    savedOffers={savedOffers}
    onSaveOffer={onSaveOffer}
    onApplyOffer={onApplyOffer}
    onDeleteOffer={onDeleteOffer}
    activeOffers={activeOffers}
    onUpdateActiveOfferBlockValue={onUpdateActiveOfferBlockValue}
    onRemoveActiveOffer={onRemoveActiveOffer}
  />
      {/* avg spend by tier + referral overview */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border }}>
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700, mb: 1 }}>Average lifetime spend by tier</Typography>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={avgSpendByTier}>
            <CartesianGrid strokeDasharray="3 3" stroke={PALETTE.border} />
            <XAxis dataKey="tier" tick={{ fill: PALETTE.inkDim, fontSize: 11 }} />
            <YAxis tick={{ fill: PALETTE.inkDim, fontSize: 11 }} tickFormatter={fmtUSD} width={55} />
            <Tooltip content={<ChartTooltip />} />
            <Bar dataKey="avg" name="Avg Spend" radius={[6, 6, 0, 0]} cursor="pointer" onClick={(d) => setSelectedTier(d.tier)}>
              {avgSpendByTier.map((t) => <Cell key={t.tier} fill={TIER_COLORS[t.tier]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <Typography sx={{ fontSize: 12, color: PALETTE.inkDim, mt: 1 }}>Referral links live on every customer's detail card (Rewards tab) — sharing rewards both the referrer and the new guest with bonus points on their first order.</Typography>
      </Paper>

      {/* combo suggestions */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border }}>
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700, mb: 1 }}>Suggest a combo</Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <TextField fullWidth size="small" placeholder="e.g. Ribeye + Old Fashioned + Cheesecake" value={comboIdea} onChange={(e) => setComboIdea(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitCombo()} />
          <Button variant="contained" onClick={submitCombo} sx={{ bgcolor: PALETTE.garnet, "&:hover": { bgcolor: PALETTE.garnetLight } }}>Submit</Button>
        </Stack>
        {suggestions.length > 0 && (
          <Stack spacing={0.5} sx={{ maxHeight: 120, overflowY: "auto" }}>
            {suggestions.map((s) => <Typography key={s.id} sx={{ fontSize: 12, color: PALETTE.inkDim }}>• {s.text}</Typography>)}
          </Stack>
        )}
      </Paper>

      {/* customer directory -> detail dialog */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5, flexWrap: "wrap", rowGap: 1 }}>
          <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700 }}>All customers — click a row for full points/rewards detail</Typography>
          <TextField size="small" placeholder="Search name or phone…" value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15 }} /></InputAdornment> }} sx={{ width: 240 }} />
        </Stack>
        <Box sx={{ maxHeight: 380, overflowY: "auto", border: `1px solid ${PALETTE.border}`, borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>{["Name", "Phone", "Tier", "Points", "Visits"].map((h) => <TableCell key={h} sx={{ fontSize: 10.5, textTransform: "uppercase", color: PALETTE.inkDim, fontWeight: 700, bgcolor: PALETTE.bg }}>{h}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {filteredCustomers.map((c) => (
                <TableRow key={c.id} hover onClick={() => setSelectedCustomer(c)} sx={{ cursor: "pointer" }}>
                  <TableCell sx={{ fontSize: 12.5 }}>{c.name}</TableCell>
                  <TableCell sx={{ fontSize: 12, color: PALETTE.inkDim, fontFamily: "JetBrains Mono, monospace" }}>{c.phone}</TableCell>
                  <TableCell><Chip size="small" label={c.pointsTier} sx={{ bgcolor: alpha(TIER_COLORS[c.pointsTier], 0.15), color: TIER_COLORS[c.pointsTier], fontWeight: 700 }} /></TableCell>
                  <TableCell sx={{ fontSize: 12.5, color: PALETTE.brass, fontWeight: 700 }}>{c.loyaltyPoints}</TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{c.visits}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {/* upcoming celebrations list, kept from the old panel */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border }}>
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700, mb: 1 }}>Upcoming Birthdays & Anniversaries — next 30 days</Typography>
        {celebrations.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: PALETTE.inkDim }}>Nothing coming up in the next 30 days.</Typography>
        ) : (
          <Grid container spacing={1} sx={{ width: "100%", margin: 0 }}>
            {celebrations.map((c) => (
              <Grid item xs={12} sm={6} md={4} key={c.id}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, borderColor: PALETTE.border }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>{c.name}</Typography>
                    <Chip size="small" label={c.type} sx={{ bgcolor: alpha(c.type === "Birthday" ? PALETTE.garnet : PALETTE.sage, 0.12), color: c.type === "Birthday" ? PALETTE.garnet : PALETTE.sage, fontWeight: 700 }} />
                  </Stack>
                  <Typography sx={{ fontSize: 12, color: PALETTE.inkDim, fontFamily: "JetBrains Mono, monospace" }}>{c.phone}</Typography>
                  <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                    <Typography sx={{ fontSize: 12, color: PALETTE.brass, fontWeight: 600 }}>{c.daysUntil === 0 ? "Today" : c.daysUntil === 1 ? "Tomorrow" : `In ${c.daysUntil} days`}</Typography>
                    <Typography sx={{ fontSize: 12, color: PALETTE.inkDim }}>{c.visits} visits · {fmtUSD2(c.totalSpend)} lifetime</Typography>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      <CustomerDetailDialog customer={selectedCustomer} onClose={() => setSelectedCustomer(null)} loyaltyRules={rules} />
    </Stack>
  );
}

function SalesAnalytics({ menu, customers = [], onNavigate ,comboSuggestions = []}) {
  const [range, setRange] = useState("week");
  const [sortBy, setSortBy] = useState("revenue");
  const [expandedDay, setExpandedDay] = useState(null);
  const [revenueDetailsOpen, setRevenueDetailsOpen] = useState(false);
  const [categoryDetailsOpen, setCategoryDetailsOpen] = useState(false);
  const [itemPerfDetailsOpen, setItemPerfDetailsOpen] = useState(false);
  const itemSales = useMemo(() => itemSalesFor(menu), [menu]);
  const sliceMap = { day: 1, week: 7, month: 30 };
  const data = DAILY_SALES.slice(-sliceMap[range]);
  const totalRevenue = data.reduce((s, d) => s + d.revenue, 0);
  const totalProfit = data.reduce((s, d) => s + d.profit, 0);
  const totalOrders = data.reduce((s, d) => s + d.orders, 0);
  const prevSlice = DAILY_SALES.slice(-sliceMap[range] * 2, -sliceMap[range]);
  const prevRevenue = prevSlice.reduce((s, d) => s + d.revenue, 0) || 1;
  const deltaPct = (((totalRevenue - prevRevenue) / prevRevenue) * 100).toFixed(1);
  const sortedItems = [...itemSales].sort((a, b) => b[sortBy] - a[sortBy]);
  const pieData = CATS.map((cat) => ({ name: cat, value: itemSales.filter(i=>i.cat===cat).reduce((s,i)=>s+i.revenue,0) }));
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
const handleComboClick = (suggestion) => {
  const customer = customers.find(c => c.phone === suggestion.phone);
  if (customer) {
    setSelectedSuggestion(customer);
  } else {
    // fallback: show a minimal popup with suggestion details
    setSelectedSuggestion({ name: 'Unknown', phone: suggestion.phone,});
  }
};
  return (
    <Stack spacing={3}>
      <Box sx={{display:'flex',alignItems:'start',justifyContent:'space-between'}}>
        <SectionHead eyebrow="AI Sales Monitoring" title="Trending Performance"  />
        <ToggleButtonGroup exclusive value={range} onChange={(e,v)=>v&&setRange(v)} size="medium">
          {["day","week","month"].map((r) => <ToggleButton key={r} value={r} sx={{ textTransform: "capitalize", px: 2, "&.Mui-selected": { bgcolor: PALETTE.garnet, color: "#fff", "&:hover": { bgcolor: PALETTE.garnetLight } } }}>{r}</ToggleButton>)}
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
        <Grid item xs={6} md={3}>
          <DetailsTrigger title="Revenue Detail" subtitle={`Daily revenue — last ${range}`}
            columns={[{key:"label",label:"Date"},{key:"dow",label:"Day"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)},{key:"orders",label:"Orders"}]} rows={data}
            onRowClick={(day) => ({
              title: `Revenue Breakdown — ${day.label}`, subtitle: `${fmtUSD2(day.revenue)} across ${day.orders} orders — items sold that day`,
              columns: [{key:"name",label:"Item"},{key:"cat",label:"Category"},{key:"qty",label:"Qty Sold"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)}],
              rows: itemsSoldForDay(day, itemSales),
            })}>
            <KpiCard icon={AttachMoney} label="Revenue" value={fmtUSD(totalRevenue)} delta={`${deltaPct}% vs prior ${range}`} positive={deltaPct >= 0} />
          </DetailsTrigger>
        </Grid>
        <Grid item xs={6} md={3}>
          <DetailsTrigger title="Profit Detail" subtitle={`Daily profit — last ${range}`}
            columns={[{key:"label",label:"Date"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)},{key:"profit",label:"Profit",value:(r)=>fmtUSD2(r.profit)},{key:"margin",label:"Margin %",value:(r)=>`${((r.profit/r.revenue)*100).toFixed(1)}%`}]} rows={data}
            onRowClick={(day) => ({
              title: `Profit Breakdown — ${day.label}`, subtitle: `${fmtUSD2(day.profit)} profit — items sold that day`,
              columns: [{key:"name",label:"Item"},{key:"cat",label:"Category"},{key:"qty",label:"Qty Sold"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)}],
              rows: itemsSoldForDay(day, itemSales),
            })}>
            <KpiCard icon={TrendingUp} label="Profit" value={fmtUSD(totalProfit)} delta={`${((totalProfit/totalRevenue)*100).toFixed(1)}% margin`} />
          </DetailsTrigger>
        </Grid>
        <Grid item xs={6} md={3}>
          <DetailsTrigger title="Orders Detail" subtitle={`Daily order counts — last ${range}`}
            columns={[{key:"label",label:"Date"},{key:"orders",label:"Orders"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)},{key:"avg",label:"Avg / Order",value:(r)=>fmtUSD2(r.revenue/Math.max(1,r.orders))}]} rows={data}
            onRowClick={(day) => ({
              title: `Orders Breakdown — ${day.label}`, subtitle: `${day.orders} orders — items sold that day`,
              columns: [{key:"name",label:"Item"},{key:"cat",label:"Category"},{key:"qty",label:"Qty Sold"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)}],
              rows: itemsSoldForDay(day, itemSales),
            })}>
            <KpiCard icon={RestaurantMenu} label="Orders" value={totalOrders} delta={`avg ${fmtUSD2(totalRevenue/totalOrders)}`} />
          </DetailsTrigger>
        </Grid>
        <Grid item xs={6} md={3}>
          <DetailsTrigger title="AI Signal Detail" subtitle="What's driving this signal, day by day"
            columns={[{key:"label",label:"Date"},{key:"dow",label:"Day"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)},{key:"signal",label:"Signal",value:(r)=> (r.dow==="Fri"||r.dow==="Sat") ? "Weekend peak" : r.revenue > totalRevenue/data.length ? "Above average" : "Below average"}]} rows={data}
            onRowClick={(day) => ({
              title: `Signal Breakdown — ${day.label}`, subtitle: `Items sold that day`,
              columns: [{key:"name",label:"Item"},{key:"cat",label:"Category"},{key:"qty",label:"Qty Sold"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)}],
              rows: itemsSoldForDay(day, itemSales),
            })}>
            <KpiCard icon={AutoAwesome} label="AI Signal" value={deltaPct >= 0 ? "Trending Up" : "Softening"} delta={deltaPct >= 0 ? "Peak: weekends" : "Watch weekday dip"} positive={deltaPct >= 0} />
          </DetailsTrigger>
        </Grid>

         <Grid item xs={6} md={3}>
         <DetailsTrigger
  title="Suggested Combo's"
  subtitle="Combo ideas submitted by guests and staff, with their details"
  columns={[
    { key: "text", label: "Suggested Combo" },
     { key: "name", label: "Name", value: (r) => r.name || "—" },
    { key: "phone", label: "Phone", value: (r) => r.phone || "—" },
    { key: "date", label: "Submitted" }
  ]}
  rows={comboSuggestions}
  onRowClick={(row) => {
    // Instead of drilling into another DetailsDialog, we open the customer popup
    handleComboClick(row);
    // Return null to prevent default drill-down
    return null;
  }}
>
  <KpiCard
    icon={AutoAwesome}
    label="Suggested Combo's"
    value={comboSuggestions.length}
    delta={comboSuggestions.length ? "Click to view details" : "None submitted yet"}
  />
</DetailsTrigger>
        </Grid>
        <Dialog
  open={Boolean(selectedSuggestion)}
  onClose={() => setSelectedSuggestion(null)}
  maxWidth="md"
  fullWidth
  PaperProps={{ sx: { borderRadius: 4 } }}
>
  <DialogContent sx={{ bgcolor: PALETTE.bg }}>
    {selectedSuggestion && (
      <CustomerDetailCard customer={selectedSuggestion} />
    )}
    <Button fullWidth onClick={() => setSelectedSuggestion(null)} sx={{ mt: 2, color: PALETTE.inkDim }}>
      Close
    </Button>
  </DialogContent>
</Dialog>
        <Grid item xs={6} md={3}>
          <LoyaltyProgramPanel customers={customers} onNavigate={onNavigate} />
        </Grid>

      </Grid>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 ,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600 }}>Revenue trend — click a point for hourly breakdown</Typography>
          <Button size="small" onClick={()=>setRevenueDetailsOpen(true)} startIcon={<Download sx={{ fontSize: 13 }} />} sx={{ fontSize: 11, color: PALETTE.inkDim, border: `1px solid ${PALETTE.border}` }}>Details & export</Button>
        </Stack>
        <DetailsDialog open={revenueDetailsOpen} onClose={()=>setRevenueDetailsOpen(false)} title="Revenue Trend Detail" subtitle={`Daily revenue/profit — last ${range}`}
          columns={[{key:"label",label:"Date"},{key:"dow",label:"Day"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)},{key:"profit",label:"Profit",value:(r)=>fmtUSD2(r.profit)},{key:"orders",label:"Orders"}]} rows={data} />
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={data} onClick={(e) => e?.activeLabel && setExpandedDay(data.find(d=>d.label===e.activeLabel))}>
            <defs><linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={PALETTE.brass} stopOpacity={0.45} /><stop offset="95%" stopColor={PALETTE.brass} stopOpacity={0.02} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke={PALETTE.border} />
            <XAxis dataKey="label" tick={{ fill: PALETTE.inkDim, fontSize: 11 }} minTickGap={20} />
            <YAxis tick={{ fill: PALETTE.inkDim, fontSize: 11 }} tickFormatter={fmtUSD} width={55} />
            <Tooltip content={<ChartTooltip />} />
            <Area type="monotone" dataKey="revenue" name="Revenue" stroke={PALETTE.brass} fill="url(#revGrad)" strokeWidth={2} />
            <Line type="monotone" dataKey="profit" name="Profit" stroke={PALETTE.garnet} strokeWidth={2} dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </Paper>

      {expandedDay && (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.brass }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 1,display:'flex',justifyContent:'space-between',alignItems:'center' }}>
            <Typography sx={{ fontSize: 13 }}>Hourly sales — <b style={{ color: PALETTE.garnet }}>{expandedDay.label}</b> ({expandedDay.dow})</Typography>
            <IconButton size="small" onClick={()=>setExpandedDay(null)}><Close sx={{ fontSize: 16 }} /></IconButton>
          </Stack>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={hourlyForDay(expandedDay.revenue, expandedDay.date.length)}>
              <CartesianGrid strokeDasharray="3 3" stroke={PALETTE.border} />
              <XAxis dataKey="hour" tick={{ fill: PALETTE.inkDim, fontSize: 10 }} />
              <YAxis tick={{ fill: PALETTE.inkDim, fontSize: 10 }} tickFormatter={fmtUSD} width={50} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="sales" name="Sales" fill={PALETTE.garnet} radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      <Grid container spacing={2.5} sx={{ width: '100%', margin: 0 }}>
        <Grid sx={{width:'49%'}} item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border, height: "100%" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1,display:'flex',justifyContent:'space-between',alignItems:'center' }}>
              <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600 }}>Item performance</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Select size="small" value={sortBy} onChange={(e)=>setSortBy(e.target.value)} sx={{ fontSize: 12, height: 32 }}>
                  <MenuItem value="revenue">Revenue</MenuItem><MenuItem value="units">Units Sold</MenuItem><MenuItem value="profit">Profit</MenuItem>
                </Select>
                <IconButton size="small" onClick={()=>setItemPerfDetailsOpen(true)} title="Details & export" sx={{ border: `1px solid ${PALETTE.border}` }}><Download sx={{ fontSize: 14 }} /></IconButton>
              </Stack>
            </Stack>
            <DetailsDialog open={itemPerfDetailsOpen} onClose={()=>setItemPerfDetailsOpen(false)} title="Item Performance Detail" subtitle="Every menu item — revenue, units sold, profit"
              columns={[{key:"name",label:"Item"},{key:"cat",label:"Category"},{key:"units",label:"Units Sold"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)},{key:"profit",label:"Profit",value:(r)=>fmtUSD2(r.profit)}]} rows={sortedItems} />
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={sortedItems.slice(0,8)} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={PALETTE.border} horizontal={false} />
                <XAxis type="number" tick={{ fill: PALETTE.inkDim, fontSize: 10 }} />
                <YAxis type="category" dataKey="name" width={130} tick={{ fill: PALETTE.inkDim, fontSize: 10 }} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey={sortBy} fill={PALETTE.brass} radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid sx={{width:'49%'}} item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border, height: "100%" }}>
            <Stack direction="row" sx={{display:'flex',justifyContent:'space-between',alignItems:'center'}} justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600 }}>Revenue by category</Typography>
              <IconButton size="small" onClick={()=>setCategoryDetailsOpen(true)} title="Details & export" sx={{ border: `1px solid ${PALETTE.border}` }}><Download sx={{ fontSize: 14 }} /></IconButton>
            </Stack>
            <DetailsDialog open={categoryDetailsOpen} onClose={()=>setCategoryDetailsOpen(false)} title="Revenue by Category Detail" subtitle="Category totals behind the pie chart"
              columns={[{key:"name",label:"Category"},{key:"value",label:"Revenue",value:(r)=>fmtUSD2(r.value)},{key:"share",label:"Share",value:(r)=>`${((r.value/pieData.reduce((s,p)=>s+p.value,0))*100).toFixed(1)}%`}]} rows={pieData} />
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={{ stroke: PALETTE.inkDim, strokeWidth: 1 }}>
                  {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
                <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 11, color: PALETTE.inkDim }} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );
}

/* ============================ INVENTORY & WASTAGE ============================ */

function BarcodeImage({ value, height = 46 }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    let cancelled = false, poll;
    const render = () => {
      if (cancelled || !canvasRef.current) return;
      if (window.JsBarcode) {
        try { window.JsBarcode(canvasRef.current, value, { format: "CODE128", width: 2, height, displayValue: false, margin: 4 }); } catch {}
      } else {
        poll = setTimeout(render, 150);
      }
    };
    render();
    return () => { cancelled = true; clearTimeout(poll); };
  }, [value, height]);
  return <canvas ref={canvasRef} />;
}

function printBarcodeLabel(label) {
  const win = window.open("", "_blank", "width=380,height=320");
  if (!win) return;
  win.document.write(`
    <html><head><title>${label.itemName} label</title>
    <style>
      body{font-family: Arial, sans-serif; text-align:center; margin:0; padding:16px;}
      .sticker{width:230px; margin:0 auto; border:1px dashed #ccc; padding:12px; border-radius:8px;}
      h3{margin:4px 0 2px; font-size:15px;}
      p{margin:2px 0; font-size:12px; color:#555;}
      .code{font-family: monospace; font-size:11px; letter-spacing:1px; margin-top:4px;}
    </style>
    <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
    </head><body>
      <div class="sticker">
        <h3>${label.itemName}</h3>
        <p>${label.qty}${label.unit}</p>
        <svg id="bc"></svg>
        <p class="code">${label.code}</p>
      </div>
      <script>
        window.onload = function () {
          JsBarcode("#bc", ${JSON.stringify(label.code)}, { format: "CODE128", width: 2, height: 46, displayValue: false, margin: 4 });
          setTimeout(function () { window.print(); }, 200);
        };
      </script>
    </body></html>
  `);
  win.document.close();
}

function BarcodeLabels({ items, ledger, barcodes, onCreateBarcode, onScanBarcode }) {
  const [form, setForm] = useState({ itemId: items[0]?.id || "", qty: "1" });
  const [scanInput, setScanInput] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [creating, setCreating] = useState(false);
  const scanRef = useRef(null);

  const createLabel = async () => {
    const item = items.find((i) => i.id === form.itemId);
    const qty = parseFloat(form.qty);
    if (!item || !qty || qty <= 0) return;
    setCreating(true);
    try {
      await onCreateBarcode({ itemId: item.id, qty });
      setForm({ itemId: items[0]?.id || "", qty: "1" });
    } finally {
      setCreating(false);
    }
  };

  const handleScanKeyDown = async (e) => {
    if (e.key !== "Enter") return;
    const code = scanInput.trim();
    if (!code) return;
    setScanInput("");
    const res = await onScanBarcode(code);
    setScanResult({ ok: res.ok, msg: res.message });
  };

  const recentScans = ledger.filter((l) => l.note?.startsWith("Scanned barcode")).slice(0, 8);

  return (
    <Stack spacing={3}>
      <SectionHead eyebrow="Barcode Labels" title="Create, Print & Scan-to-Log" />

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border }}>
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 1.5 }}>Create a custom barcode label</Typography>
        <Grid container spacing={1.5} sx={{ width: '100%', margin: 0 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <Select value={form.itemId} onChange={(e)=>setForm({...form, itemId: e.target.value})}>
                {items.map((i) => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" type="number" label={`Qty (${items.find(i=>i.id===form.itemId)?.unit || "unit"})`} value={form.qty} onChange={(e)=>setForm({...form, qty: e.target.value})} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Button fullWidth variant="contained" disabled={creating} startIcon={<Add />} onClick={createLabel} sx={{ bgcolor: PALETTE.garnet, height: "100%", "&:hover": { bgcolor: PALETTE.garnetLight } }}>{creating ? "Generating…" : "Generate label"}</Button>
          </Grid>
        </Grid>
        <Typography sx={{ fontSize: 11.5, color: PALETTE.inkDim, mt: 1.5 }}>
          Example: create five "Chicken Breast (kg)" labels at 1, 2, 3, 4, and 5kg — one sticker per tray — then print and stick each on its tray in the walk-in.
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border }}>
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 1.5 }}>Printable stickers ({barcodes.length})</Typography>
        <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
          {barcodes.map((b) => (
            <Grid item xs={12} sm={6} md={3} key={b.id}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: PALETTE.border, textAlign: "center", height: "100%" }}>
                <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{b.itemName}</Typography>
                <Typography sx={{ fontSize: 12, color: PALETTE.garnet, fontWeight: 700, mb: 1 }}>{b.qty}{b.unit}</Typography>
                <Box sx={{ display: "flex", justifyContent: "center", bgcolor: "#fff", p: 1, borderRadius: 1 }}><BarcodeImage value={b.code} /></Box>
                <Typography sx={{ fontSize: 10, color: PALETTE.inkDim, fontFamily: "JetBrains Mono, monospace", mt: 0.5 }}>{b.code}</Typography>
                <Button size="small" fullWidth onClick={()=>printBarcodeLabel(b)} sx={{ mt: 1, color: PALETTE.garnet, border: `1px solid ${PALETTE.border}` }}>Print sticker</Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.brass, bgcolor: alpha(PALETTE.brass, 0.05) }}>
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 1 }}>Scan barcode — log stock taken out</Typography>
        <Typography sx={{ fontSize: 12, color: PALETTE.inkDim, mb: 1.5 }}>
          Click into this box, then scan a printed sticker with a USB/Bluetooth barcode scanner — scanners type the code and press Enter automatically, just like a keyboard. You can also type a code manually to test.
        </Typography>
        <TextField inputRef={scanRef} fullWidth size="small" placeholder="Scan or type a barcode, then press Enter…" value={scanInput}
          onChange={(e)=>setScanInput(e.target.value)} onKeyDown={handleScanKeyDown} autoFocus
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16 }} /></InputAdornment> }} />
        {scanResult && (
          <Typography sx={{ fontSize: 12.5, mt: 1, color: scanResult.ok ? PALETTE.sage : PALETTE.alert, fontWeight: 600 }}>{scanResult.msg}</Typography>
        )}
        {recentScans.length > 0 && (
          <Stack spacing={0.5} sx={{ mt: 2, maxHeight: 160, overflowY: "auto" }}>
            <Typography sx={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600 }}>Recent scans</Typography>
            {recentScans.map((l) => (
              <Stack key={l.id} direction="row" justifyContent="space-between" sx={{ fontSize: 11.5, px: 1, py: 0.6, borderRadius: 1.5, bgcolor: PALETTE.panel }}>
                <Typography sx={{ fontSize: 11, color: PALETTE.inkDim, minWidth: 90 }}>{l.date} · {l.time}</Typography>
                <Typography sx={{ fontSize: 11.5, flexGrow: 1, px: 1 }}>{l.itemName || l.item}</Typography>
                <Typography sx={{ fontSize: 11.5, color: PALETTE.alert }}>-{l.qty}</Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}

function InventoryHub({ items, ledger, wastageLog, menu, barcodes, onLogMovement, onLogWastage, onCreateBarcode, onScanBarcode, onSetExpiryDate }) {
  const [tab, setTab] = useState("stock");
  return (
    <Stack spacing={2.5}>
      <ToggleButtonGroup exclusive value={tab} onChange={(e,v)=>v&&setTab(v)} size="small">
        <ToggleButton value="stock" sx={{ px: 2.5, "&.Mui-selected": { bgcolor: PALETTE.garnet, color: "#fff", "&:hover": { bgcolor: PALETTE.garnetLight } } }}>Stock & Wastage</ToggleButton>
        <ToggleButton value="barcodes" sx={{ px: 2.5, "&.Mui-selected": { bgcolor: PALETTE.garnet, color: "#fff", "&:hover": { bgcolor: PALETTE.garnetLight } } }}>Barcode Labels</ToggleButton>
      </ToggleButtonGroup>
      {tab === "stock"
        ? <InventoryWastage items={items} ledger={ledger} wastageLog={wastageLog} menu={menu} onLogMovement={onLogMovement} onLogWastage={onLogWastage} onSetExpiryDate={onSetExpiryDate} />
        : <BarcodeLabels items={items} ledger={ledger} barcodes={barcodes} onCreateBarcode={onCreateBarcode} onScanBarcode={onScanBarcode} />}
    </Stack>
  );
}

function InventoryWastage({ items, ledger, wastageLog, menu, onLogMovement, onLogWastage, onSetExpiryDate }) {
  const low = items.filter((i) => i.qty <= i.threshold);
  const totalWasteCost = wastageLog.reduce((s, w) => s + w.cost, 0);
  const todayISO = new Date().toISOString().slice(0, 10);
  const expiringSoon = items.filter((i) => {
    if (!i.outDate) return false;
    const days = Math.ceil((new Date(i.outDate) - new Date(todayISO)) / 86400000);
    return days <= 3;
  });
  const spoiledEntries = ledger.filter((l) => l.type === "spoiled");
  const totalSpoiledCost = spoiledEntries.reduce((s, l) => s + (l.cost || 0), 0);
  const [spoiledDetailsOpen, setSpoiledDetailsOpen] = useState(false);

  const [moveForm, setMoveForm] = useState({ itemId: items[0]?.id || "", type: "received", qty: "", note: "", date: todayISO });
  const [movingBusy, setMovingBusy] = useState(false);
  const logMovement = async () => {
    const qty = parseInt(moveForm.qty);
    if (!qty || qty <= 0) return;
    setMovingBusy(true);
    try {
      await onLogMovement({
        itemId: moveForm.itemId, type: moveForm.type, qty,
        note: moveForm.note || (moveForm.type === "received" ? "Manual stock receipt" : moveForm.type === "spoiled" ? "Spoiled — discarded unused" : "Manual stock usage"),
        date: moveForm.date,
      });
      setMoveForm({ itemId: items[0]?.id || "", type: "received", qty: "", note: "", date: todayISO });
    } finally {
      setMovingBusy(false);
    }
  };
  const quickAdjust = (itemId, type, qty) => onLogMovement({ itemId, type, qty, note: type === "received" ? "Quick restock" : "Quick adjustment", date: todayISO });

  const [prepForm, setPrepForm] = useState({ menuId: menu[0]?.id || "", prepared: "", sold: "" });
  const [prepBusy, setPrepBusy] = useState(false);
  const logPrepSold = async () => {
    const prepared = parseInt(prepForm.prepared), sold = parseInt(prepForm.sold);
    if (!prepared || sold === "" || isNaN(sold)) return;
    const menuItem = menu.find((m) => m.id === prepForm.menuId);
    if (!menuItem) return;
    setPrepBusy(true);
    try {
      await onLogWastage({ itemName: menuItem.name, unitCost: menuItem.cost, prepared, sold });
      setPrepForm({ menuId: menu[0]?.id || "", prepared: "", sold: "" });
    } finally {
      setPrepBusy(false);
    }
  };

  const chartData = [...wastageLog].reverse().slice(-10);

  return (
    <Stack spacing={3}>
      <SectionHead eyebrow="AI Inventory & Wastage" title="Stock Room & Waste Ledger" />
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: PALETTE.border }}>
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700, mb: 1.5 }}>Alerts</Typography>
        <Stack spacing={1.5}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
              <WarningAmber sx={{ fontSize: 16, color: low.length ? PALETTE.alert : PALETTE.inkDim }} />
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: low.length ? PALETTE.ink : PALETTE.inkDim }}>Low stock ({low.length})</Typography>
            </Stack>
            {low.length > 0 ? (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {low.map((i) => <Chip key={i.id} size="small" label={`${i.name} — ${i.qty}${i.unit} left`} sx={{ bgcolor: alpha(PALETTE.alert, 0.12), color: PALETTE.alert }} />)}
              </Stack>
            ) : <Typography sx={{ fontSize: 12, color: PALETTE.inkDim, pl: 3 }}>All items above threshold.</Typography>}
          </Box>
          <Divider />
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
              <WarningAmber sx={{ fontSize: 16, color: expiringSoon.length ? PALETTE.brass : PALETTE.inkDim }} />
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: expiringSoon.length ? PALETTE.ink : PALETTE.inkDim }}>Expiring soon / easily spoiled ({expiringSoon.length})</Typography>
            </Stack>
            {expiringSoon.length > 0 ? (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {expiringSoon.map((i) => {
                  const days = Math.ceil((new Date(i.outDate) - new Date(todayISO)) / 86400000);
                  return <Chip key={i.id} size="small" label={`${i.name} — ${days < 0 ? "expired" : days === 0 ? "expires today" : `${days}d left`}`}
                    sx={{ bgcolor: alpha(days < 0 ? PALETTE.alert : PALETTE.brass, 0.14), color: days < 0 ? PALETTE.alert : PALETTE.brass, fontWeight: 700 }} />;
                })}
              </Stack>
            ) : <Typography sx={{ fontSize: 12, color: PALETTE.inkDim, pl: 3 }}>Nothing expiring within 3 days.</Typography>}
          </Box>
          <Divider />
          <Box onClick={()=>spoiledEntries.length && setSpoiledDetailsOpen(true)} sx={spoiledEntries.length ? { cursor: "pointer" } : {}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <WarningAmber sx={{ fontSize: 16, color: spoiledEntries.length ? PALETTE.brass : PALETTE.inkDim }} />
                <Typography sx={{ fontWeight: 600, fontSize: 13, color: spoiledEntries.length ? PALETTE.ink : PALETTE.inkDim }}>Spoiled / unused stock ({spoiledEntries.length}){spoiledEntries.length ? " — click for details" : ""}</Typography>
              </Stack>
              {spoiledEntries.length > 0 && <Typography sx={{ fontSize: 13, fontWeight: 700, color: PALETTE.brass }}>{fmtUSD2(totalSpoiledCost)} lost</Typography>}
            </Stack>
            {spoiledEntries.length === 0 && <Typography sx={{ fontSize: 12, color: PALETTE.inkDim, pl: 3, mt: 0.5 }}>Nothing logged as spoiled yet.</Typography>}
          </Box>
        </Stack>
      </Paper>
      <DetailsDialog open={spoiledDetailsOpen} onClose={()=>setSpoiledDetailsOpen(false)} title="Spoiled / Unused Stock" subtitle="What spoiled, when, and what it cost"
        columns={[{key:"date",label:"Date"},{key:"time",label:"Time"},{key:"itemName",label:"Item"},{key:"qty",label:"Qty Spoiled"},{key:"cost",label:"Cost",value:(r)=>fmtUSD2(r.cost||0)},{key:"note",label:"Note"}]} rows={spoiledEntries} />

      <Grid container spacing={2.5} sx={{ width: '100%', margin: 0,display:'flex',justifyContent:'space-between' }}>
        <Grid sx={{width:'49%'}} item xs={12} >
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border, height: "100%" }}>
            <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 1.5 }}>Inventory vs. par level</Typography>
            <Stack spacing={1.2} sx={{ maxHeight: 340, overflowY: "auto", pr: 0.5 }}>
              {items.map((i) => {
                const pct = Math.min(100, (i.qty / i.par) * 100);
                const isLow = i.qty <= i.threshold;
                const isExpired = i.outDate && i.outDate < todayISO;
                const daysToExpiry = i.outDate ? Math.ceil((new Date(i.outDate) - new Date(todayISO)) / 86400000) : null;
                const isExpiringSoon = daysToExpiry !== null && daysToExpiry >= 0 && daysToExpiry <= 3;
                return (
                  <Box key={i.id} sx={{ p: 1.2, borderRadius: 2, bgcolor: PALETTE.bg }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography sx={{ fontSize: 12.5 }}>{i.name}</Typography>
                      <Typography sx={{ fontSize: 12, color: isLow ? PALETTE.alert : PALETTE.inkDim, fontFamily: "JetBrains Mono, monospace" }}>{i.qty}{i.unit} / {i.par}{i.unit}</Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3, bgcolor: PALETTE.border, "& .MuiLinearProgress-bar": { bgcolor: isLow ? PALETTE.alert : PALETTE.brass, borderRadius: 3 } }} />
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5, flexWrap: "wrap", rowGap: 0.5 }}>
                      <Typography sx={{ fontSize: 10.5, color: PALETTE.inkDim }}>{i.receivedDate ? `In: ${i.receivedDate}` : ""}</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                        <Box>
                          {i.outDate && (
                            <Typography sx={{ color: isExpired ? PALETTE.alert : isExpiringSoon ? PALETTE.brass : PALETTE.inkDim }}>
                              {isExpired ? "Expired" : isExpiringSoon ? `${daysToExpiry}d left` : "Best by"}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
                          <Typography sx={{ fontSize: 11, color: PALETTE.inkDim, fontWeight: 600 }}>Expiry:</Typography>
                          <TextField type="date" size="small" value={i.outDate || ""} onChange={(e) => onSetExpiryDate(i.id, e.target.value)} sx={{ width: 118, fontSize:'10px' }} />
                        </Box>
                      </Box>
                    </Stack>
                    <Stack direction="row" justifyContent="flex-end" spacing={0.5} sx={{ mt: 0.75 }}>
                      <Button size="small" onClick={()=>quickAdjust(i.id, "used", 1)} sx={{ fontSize: 11, minWidth: 0, px: 1, color: PALETTE.inkDim }}>-1</Button>
                      <Button size="small" onClick={()=>quickAdjust(i.id, "received", 5)} sx={{ fontSize: 11, minWidth: 0, px: 1, color: PALETTE.inkDim }}>+5 restock</Button>
                      <Button size="small" onClick={()=>onLogMovement({ itemId: i.id, type: "spoiled", qty: 1, note: "Marked spoiled from inventory list" })} sx={{ fontSize: 11, minWidth: 0, px: 1, color: PALETTE.brass }}>-1 spoiled</Button>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        </Grid>

        <Grid sx={{width:'49%'}} item xs={12} >
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border, height: "100%", display: "flex", flexDirection: "column" }}>
            <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 1.5 }}>Log stock movement — goods in / goods out</Typography>
            <Grid container spacing={1} sx={{ width: '100%', margin: 0, mb: 1 }}>
              <Grid item xs={12} sm={5}>
                <FormControl fullWidth size="small">
                  <Select value={moveForm.itemId} onChange={(e)=>setMoveForm({...moveForm, itemId: e.target.value})}>
                    {items.map((i) => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <ToggleButtonGroup fullWidth exclusive size="small" value={moveForm.type} onChange={(e,v)=>v&&setMoveForm({...moveForm, type: v})}>
                  <ToggleButton value="received" sx={{ fontSize: 10.5, "&.Mui-selected": { bgcolor: alpha(PALETTE.sage,0.18), color: PALETTE.sage } }}>In</ToggleButton>
                  <ToggleButton value="used" sx={{ fontSize: 10.5, "&.Mui-selected": { bgcolor: alpha(PALETTE.alert,0.18), color: PALETTE.alert } }}>Out</ToggleButton>
                  <ToggleButton value="spoiled" sx={{ fontSize: 10.5, "&.Mui-selected": { bgcolor: alpha(PALETTE.brass,0.22), color: PALETTE.brass } }}>Spoiled</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField fullWidth size="small" type="number" placeholder="Qty" value={moveForm.qty} onChange={(e)=>setMoveForm({...moveForm, qty: e.target.value})} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" placeholder="Note (e.g. supplier, or for spoiled: what spoiled and why)" value={moveForm.note} onChange={(e)=>setMoveForm({...moveForm, note: e.target.value})} />
              </Grid>
              {moveForm.type === "received" && (
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" type="date" label="Received date" value={moveForm.date}
                    onChange={(e)=>setMoveForm({...moveForm, date: e.target.value})} InputLabelProps={{ shrink: true }} />
                </Grid>
              )}
              <Grid item xs={12}>
                <Button fullWidth variant="contained" size="small" disabled={movingBusy} onClick={logMovement} sx={{ bgcolor: PALETTE.garnet, "&:hover": { bgcolor: PALETTE.garnetLight } }}>{movingBusy ? "Saving…" : "Log movement"}</Button>
              </Grid>
            </Grid>
            <Divider sx={{ my: 1 }} />
            <Typography sx={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 0.75 }}>Recent movements — how it goes, how it comes ({ledger.length} total, scroll for more)</Typography>
            <Stack spacing={0.5} sx={{ flexGrow: 1, overflowY: "auto", maxHeight: 200 }}>
              {ledger.slice(0, 20).map((l) => (
                <Box key={l.id} sx={{ px: 1, py: 0.6, borderRadius: 1.5, bgcolor: PALETTE.bg }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ fontSize: 11.5 }}>
                    <Typography sx={{ fontSize: 11, color: PALETTE.inkDim, minWidth: 90 }}>{l.date}{l.time ? ` · ${l.time}` : ""}</Typography>
                    <Typography sx={{ fontSize: 11.5, flexGrow: 1, px: 1 }}>{l.itemName || l.item}</Typography>
                    <Chip size="small" label={l.type === "received" ? `+${l.qty} in` : l.type === "spoiled" ? `-${l.qty} spoiled` : `-${l.qty} out`}
                      sx={{ height: 20, fontSize: 10, bgcolor: alpha(l.type === "received" ? PALETTE.sage : l.type === "spoiled" ? PALETTE.brass : PALETTE.alert, 0.15), color: l.type === "received" ? PALETTE.sage : l.type === "spoiled" ? PALETTE.brass : PALETTE.alert }} />
                  </Stack>
                  {l.note && <Typography sx={{ fontSize: 10.5, color: PALETTE.inkDim, fontStyle: "italic", pl: "90px", mt: 0.2 }}>— {l.note}</Typography>}
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ width: '100%', margin: 0 }}>
        <Grid sx={{width:'49%',display:'flex',flexDirection:'column',justifyContent:'space-between'}}  item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border, height: "100%",display:'flex',flexDirection:'column',justifyContent:'space-between' }}>
            <Stack spacing={1}>
            <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 1.5 }}>Log prepared vs. sold (EOD) — drives wastage tracking</Typography>
              <FormControl fullWidth size="small">
                <Select value={prepForm.menuId} onChange={(e)=>setPrepForm({...prepForm, menuId: e.target.value})}>
                  {menu.map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                </Select>
              </FormControl>
              <Stack direction="row" spacing={1}>
                <TextField fullWidth size="small" type="number" label="Prepared qty" value={prepForm.prepared} onChange={(e)=>setPrepForm({...prepForm, prepared: e.target.value})} />
                <TextField fullWidth size="small" type="number" label="Sold qty" value={prepForm.sold} onChange={(e)=>setPrepForm({...prepForm, sold: e.target.value})} />
              </Stack>
              {prepForm.prepared && prepForm.sold !== "" && (
                <Typography sx={{ fontSize: 12, color: PALETTE.inkDim }}>
                  Wasted: <b style={{ color: PALETTE.alert }}>{Math.max(0, parseInt(prepForm.prepared) - parseInt(prepForm.sold || 0))} units</b>
                </Typography>
              )}
              <Button fullWidth variant="contained" size="small" disabled={prepBusy} onClick={logPrepSold} sx={{ bgcolor: PALETTE.garnet, "&:hover": { bgcolor: PALETTE.garnetLight } }}>{prepBusy ? "Saving…" : "Save end-of-day entry"}</Button>
            </Stack>

           <Paper variant="outlined" sx={{  p: 1.2, borderRadius: 2, borderColor: PALETTE.brass, bgcolor: alpha(PALETTE.brass,0.06) }}>
              <Stack direction="row" spacing={1}><AutoAwesome sx={{ fontSize: 14, color: PALETTE.brass, mt: 0.2 }} />
                <Typography sx={{ fontSize: 12, color: PALETTE.ink }}>AI: reduce Truffle Mash prep ~18% on weekdays; lower Filet Mignon par 10% — est. savings {fmtUSD2(totalWasteCost*0.32)}/week.</Typography>
              </Stack>
          </Paper>
            </Paper>
        </Grid>

        <Grid  sx={{width:'49%'}} item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border, height: "100%" }}>
            <Stack sx={{display:'flex',alignItems:'center',justifyContent:'space-between'}} direction="row" justifyContent="space-between">
              <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600 }}>Prep · Sold · Wasted — by day</Typography>
              <Typography sx={{ fontSize: 12, color: PALETTE.alert, fontWeight: 600 }}>Total waste cost: {fmtUSD2(totalWasteCost)}</Typography>
            </Stack>
            <ResponsiveContainer width="100%" height={190}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={PALETTE.border} />
                <XAxis dataKey="date" tick={{ fill: PALETTE.inkDim, fontSize: 10 }} />
                <YAxis tick={{ fill: PALETTE.inkDim, fontSize: 10 }} width={28} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="prepared" name="Prepared" fill={PALETTE.brassLight} radius={[4,4,0,0]} />
                <Bar dataKey="sold" name="Sold" fill={PALETTE.sage} radius={[4,4,0,0]} />
                <Bar dataKey="wastedUnits" name="Wasted" fill={PALETTE.alert} radius={[4,4,0,0]} />
                <Legend wrapperStyle={{ fontSize: 10.5, color: PALETTE.inkDim }} />
              </BarChart>
            </ResponsiveContainer>
            <Stack spacing={0.5} sx={{ mt: 1, maxHeight: 140, overflowY: "auto" }}>
              {wastageLog.map((w) => (
                <Stack key={w.id} direction="row" justifyContent="space-between" sx={{ fontSize: 11.5, px: 1, py: 0.6, borderRadius: 1.5, bgcolor: PALETTE.bg }}>
                  <Typography sx={{ fontSize: 11, color: PALETTE.inkDim, minWidth: 46 }}>{w.date}</Typography>
                  <Typography sx={{ fontSize: 11.5, flexGrow: 1, px: 1 }}>{w.item}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: PALETTE.inkDim }}>prepped {w.prepared} · sold {w.sold} · wasted {w.wastedUnits}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: PALETTE.alert, minWidth: 56, textAlign: "right" }}>{fmtUSD2(w.cost)}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );
}

/* ============================ PROFIT INTELLIGENCE ============================ */

function ProductEditPopover({ item, anchorEl, onClose, onSave, onDelete }) {
  const [form, setForm] = useState(() => ({
    name: item.name, cat: item.cat, margin: item.margin, isSpecial: Boolean(item.isSpecial),
    materialCost: item.materialCost ?? item.cost, labourCost: item.labourCost ?? 0, burdenCost: item.burdenCost ?? 0,
    img0: item.img?.[0] || "", img1: item.img?.[1] || "", img2: item.img?.[2] || "",
  }));
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  useEffect(() => {
    setForm({
      name: item.name, cat: item.cat, margin: item.margin, isSpecial: Boolean(item.isSpecial),
      materialCost: item.materialCost ?? item.cost, labourCost: item.labourCost ?? 0, burdenCost: item.burdenCost ?? 0,
      img0: item.img?.[0] || "", img1: item.img?.[1] || "", img2: item.img?.[2] || "",
    });
    setConfirmingDelete(false);
  }, [item]);

  const materialCost = parseFloat(form.materialCost) || 0;
  const labourCost = parseFloat(form.labourCost) || 0;
  const burdenCost = parseFloat(form.burdenCost) || 0;
  const cost = +(materialCost + labourCost + burdenCost).toFixed(2);
  const margin = parseFloat(form.margin) || 0;
  const previewPrice = +(cost * (1 + margin / 100)).toFixed(2);

  const save = () => {
    onSave(item.id, {
      name: form.name, cat: form.cat, cost, margin, materialCost, labourCost, burdenCost, isSpecial: form.isSpecial,
      img: [form.img0, form.img1, form.img2].filter(Boolean),
    }).catch(() => {});
    onClose();
  };

  return (
    <Popover
  open={Boolean(anchorEl)}
  anchorEl={anchorEl}
  onClose={onClose}
  anchorOrigin={{
    vertical: "bottom",
    horizontal: "left",
  }}
  slotProps={{
    paper: {
      sx: {
        p: 1.25,
        width: 440,
        maxWidth: "95vw",
        borderRadius: 2,
        maxHeight: "90vh",
        overflowY: "auto",
      },
    },
  }}
>
      <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.brass, fontWeight: 700, mb: 1.5 }}>Edit Product</Typography>

      <Stack spacing={1.5}>
        <TextField size="small" fullWidth label="Item name" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} />
        <Stack direction="row" spacing={1.5}>
          <FormControl size="small" fullWidth>
            <Select value={form.cat} onChange={(e)=>setForm({...form, cat: e.target.value})}>
              {CATS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" fullWidth type="number" label="Margin %" value={form.margin} onChange={(e)=>setForm({...form, margin: e.target.value})}
            InputProps={{ endAdornment: <InputAdornment position="end"><Percent sx={{ fontSize: 14 }} /></InputAdornment> }} />
        </Stack>

        <FormControlLabel
          control={<Switch size="small" checked={form.isSpecial} onChange={(e)=>setForm({...form, isSpecial: e.target.checked})} sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: PALETTE.brass }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: PALETTE.brass } }} />}
          label={<Typography sx={{ fontSize: 12.5 }}>Mark as Chef's Special / New Dish — shown in its own showcase on the homepage</Typography>}
        />

        <Divider />
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600 }}>Making cost breakdown</Typography>
        <Stack direction="row" spacing={1.5}>
          <TextField size="small" fullWidth type="number" label="Material cost $" value={form.materialCost} onChange={(e)=>setForm({...form, materialCost: e.target.value})} />
          <TextField size="small" fullWidth type="number" label="Labour cost $" value={form.labourCost} onChange={(e)=>setForm({...form, labourCost: e.target.value})} />
          <TextField size="small" fullWidth type="number" label="Burden cost $" value={form.burdenCost} onChange={(e)=>setForm({...form, burdenCost: e.target.value})} />
        </Stack>
        <Typography sx={{ fontSize: 12, color: PALETTE.inkDim }}>Total making cost: <b style={{ color: PALETTE.ink }}>{fmtUSD2(cost)}</b></Typography>

        <Typography sx={{ fontSize: 12, color: PALETTE.inkDim, pt: 0.5 }}>
          Final price preview: <b style={{ color: PALETTE.garnet }}>{fmtUSD2(previewPrice)}</b> · Profit/unit: <b style={{ color: PALETTE.sage }}>{fmtUSD2(previewPrice - cost)}</b>
        </Typography>

        <Divider />
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600 }}>Gallery — 3 images</Typography>
        {[0, 1, 2].map((i) => {
          const hasLocalCopy = Boolean(item.imgLocal?.[i]);
          return (
            <Stack key={i} spacing={0.4}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <SmartImg src={form[`img${i}`]} fallback={item.imgLocal?.[i]} alt=""
                  sx={{ width: 44, height: 44, borderRadius: 2, objectFit: "cover", bgcolor: PALETTE.bg, flexShrink: 0 }} />
                <TextField size="small" fullWidth label={`Image ${i + 1} URL`} value={form[`img${i}`]}
                  onChange={(e)=>setForm({...form, [`img${i}`]: e.target.value})} />
              </Stack>
              <Typography sx={{ fontSize: 10.5, pl: "56px", color: hasLocalCopy ? PALETTE.sage : PALETTE.alert }}>
                {hasLocalCopy ? "✓ Offline copy saved on the server — works with no internet" : "✗ No offline copy yet — save this item once while online to create one"}
              </Typography>
            </Stack>
          );
        })}

        <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="center" sx={{ pt: 1 }}>
          {confirmingDelete ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontSize: 12, color: PALETTE.alert, fontWeight: 600 }}>Delete permanently?</Typography>
              <Button size="small" onClick={() => { onDelete(item.id).catch(() => {}); onClose(); }} sx={{ color: "#fff", bgcolor: PALETTE.alert, "&:hover": { bgcolor: "#a53a0f" } }}>Yes, delete</Button>
              <Button size="small" onClick={() => setConfirmingDelete(false)} sx={{ color: PALETTE.inkDim }}>No</Button>
            </Stack>
          ) : (
            <Button size="small" onClick={() => setConfirmingDelete(true)} sx={{ color: PALETTE.alert }}>Delete product</Button>
          )}
          <Stack direction="row" spacing={1.5}>
            <Button onClick={onClose} sx={{ color: PALETTE.inkDim }}>Cancel</Button>
            <Button variant="contained" onClick={save} sx={{ bgcolor: PALETTE.garnet, "&:hover": { bgcolor: PALETTE.garnetLight } }}>Save changes</Button>
          </Stack>
        </Stack>
      </Stack>
    </Popover>
  );
}

function ProfitIntelligence({ menu, canEdit, onAddProduct, onUpdateProduct, onDeleteProduct }) {
  const [form, setForm] = useState({ name: "", cat: "Steaks", materialCost: "", labourCost: "", burdenCost: "", margin: "", imgUrl: "", isSpecial: false });
  const [saving, setSaving] = useState(false);
  const [editAnchor, setEditAnchor] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  const formCost = (parseFloat(form.materialCost) || 0) + (parseFloat(form.labourCost) || 0) + (parseFloat(form.burdenCost) || 0);

  const addProduct = async () => {
    if (!form.name || !formCost || !form.margin) return;
    setSaving(true);
    try {
      await onAddProduct({
        name: form.name, cat: form.cat, desc: "Chef's new creation",
        materialCost: parseFloat(form.materialCost) || 0, labourCost: parseFloat(form.labourCost) || 0, burdenCost: parseFloat(form.burdenCost) || 0,
        cost: +formCost.toFixed(2), margin: parseFloat(form.margin), isSpecial: form.isSpecial,
        imgUrl: form.imgUrl.trim() || null,
      });
      setForm({ name: "", cat: "Steaks", materialCost: "", labourCost: "", burdenCost: "", margin: "", imgUrl: "", isSpecial: false });
    } finally {
      setSaving(false);
    }
  };

  const openEditor = (e, item) => { setEditAnchor(e.currentTarget); setEditingItem(item); };
  const closeEditor = () => { setEditAnchor(null); setEditingItem(null); };

  return (
    <Stack spacing={3}>
      <SectionHead eyebrow="AI Profit Intelligence" title="Menu Costing & Margin Builder" />
      {canEdit && (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border }}>
          <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 1.5 }}>Add new product</Typography>
          <Grid container spacing={1.5} sx={{ width: '100%', margin: 2 }}>
            <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Item name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} /></Grid>
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small">
                <Select value={form.cat} onChange={(e)=>setForm({...form,cat:e.target.value})}>{CATS.map(c=><MenuItem key={c} value={c}>{c}</MenuItem>)}</Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Image URL (optional)" value={form.imgUrl} onChange={(e)=>setForm({...form,imgUrl:e.target.value})}
              InputProps={{ startAdornment: <InputAdornment position="start"><ImageIcon sx={{ fontSize: 15 }} /></InputAdornment> }} /></Grid>
            <Grid item xs={4}><TextField fullWidth size="small" type="number" label="Material cost $" value={form.materialCost} onChange={(e)=>setForm({...form,materialCost:e.target.value})} /></Grid>
            <Grid item xs={4}><TextField fullWidth size="small" type="number" label="Labour cost $" value={form.labourCost} onChange={(e)=>setForm({...form,labourCost:e.target.value})} /></Grid>
            <Grid item xs={4}><TextField fullWidth size="small" type="number" label="Burden cost $" value={form.burdenCost} onChange={(e)=>setForm({...form,burdenCost:e.target.value})} /></Grid>
            <Grid item xs={6} sm={2}><TextField fullWidth size="small" type="number" label="Margin %" value={form.margin} onChange={(e)=>setForm({...form,margin:e.target.value})}
              InputProps={{ endAdornment: <InputAdornment position="end"><Percent sx={{ fontSize: 14 }} /></InputAdornment> }} /></Grid>
           <FormControlLabel sx={{ mt: 1 }}
            control={<Switch size="small" checked={form.isSpecial} onChange={(e)=>setForm({...form, isSpecial: e.target.checked})} sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: PALETTE.brass }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: PALETTE.brass } }} />}
            label={<Typography sx={{ fontSize: 12.5 }}>Mark as Chef's Special / New Dish</Typography>}
          />
          </Grid>

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2,display:'flex',justifyContent:'space-between',alignItems:'center' }}>
            <Typography sx={{ fontSize: 12.5, color: PALETTE.inkDim }}>
              Total cost: <b>{fmtUSD2(formCost)}</b>{form.margin && <> · Final price preview: <b style={{ color: PALETTE.garnet }}>{fmtUSD2(formCost*(1+parseFloat(form.margin)/100))}</b></>}
            </Typography>
            <Button variant="contained" disabled={saving} startIcon={<Add />} onClick={addProduct} sx={{ bgcolor: PALETTE.garnet, "&:hover": { bgcolor: PALETTE.garnetLight } }}>{saving ? "Saving…" : "Add product"}</Button>
          </Stack>
        </Paper>
      )}
      <Paper variant="outlined" sx={{ borderRadius: 3, borderColor: PALETTE.border, overflowX: "auto" }}>
        {canEdit && <Typography sx={{ fontSize: 11, color: PALETTE.inkDim, p: 1.5, pb: 0 }}>Click any row to edit its name, category, material/labour/burden cost, margin, and all 3 gallery images at once — saved instantly for every visitor.</Typography>}
        <Table size="small">
          <TableHead><TableRow>{["Image","Item","Category","Making cost","Margin %","Final price","Profit / unit"].map(h=>
            <TableCell key={h} sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700, border: 0 }}>{h}</TableCell>)}
          </TableRow></TableHead>
          <TableBody>
            {menu.map((m) => (
              <TableRow key={m.id} onClick={canEdit ? (e) => openEditor(e, m) : undefined}
                sx={canEdit ? { cursor: "pointer", "&:hover": { bgcolor: alpha(PALETTE.brass, 0.05) } } : {}}>
                <TableCell sx={{ borderColor: PALETTE.border }}>
                  <Stack direction="row" spacing={0.5}>
                    {(m.img || []).slice(0, 3).map((src, i) => (
                      <SmartImg key={i} src={src} fallback={m.imgLocal?.[i]} alt="" sx={{ width: 28, height: 28, borderRadius: 1.5, objectFit: "cover" }} />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell sx={{ borderColor: PALETTE.border, fontSize: 13 }}>
                  <Stack direction="row" spacing={0.6} alignItems="center">
                    {m.name}
                    {m.isSpecial && <Chip size="small" label="Special" sx={{ height: 18, fontSize: 9, bgcolor: alpha(PALETTE.brass,0.15), color: PALETTE.brass, fontWeight: 700 }} />}
                  </Stack>
                </TableCell>
                <TableCell sx={{ borderColor: PALETTE.border, fontSize: 13, color: PALETTE.inkDim }}>{m.cat}</TableCell>
                <TableCell sx={{ borderColor: PALETTE.border, fontSize: 13, fontFamily: "JetBrains Mono, monospace" }}>{fmtUSD2(m.cost)}</TableCell>
                <TableCell sx={{ borderColor: PALETTE.border }}><Chip size="small" label={`${m.margin}%`} sx={{ bgcolor: alpha(PALETTE.brass,0.12), color: PALETTE.brass }} /></TableCell>
                <TableCell sx={{ borderColor: PALETTE.border, fontSize: 13, fontFamily: "JetBrains Mono, monospace", color: PALETTE.garnet, fontWeight: 600 }}>{fmtUSD2(m.price)}</TableCell>
                <TableCell sx={{ borderColor: PALETTE.border, fontSize: 13, fontFamily: "JetBrains Mono, monospace", color: PALETTE.sage }}>{fmtUSD2(m.price - m.cost)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      {editingItem && (
        <ProductEditPopover item={editingItem} anchorEl={editAnchor} onClose={closeEditor} onSave={onUpdateProduct} onDelete={onDeleteProduct} />
      )}
    </Stack>
  );
}

/* ================================ UNIFIED HUB ================================ */

function UnifiedHub({ menu, onNavigate, reservations }) {
  const [hubCatDetailsOpen, setHubCatDetailsOpen] = useState(false);
  const [hubMonthDetailsOpen, setHubMonthDetailsOpen] = useState(false);
  const [hubAreaDetailsOpen, setHubAreaDetailsOpen] = useState(false);
  const [hubTrendDetailsOpen, setHubTrendDetailsOpen] = useState(false);
  const [period, setPeriod] = useState("week");
  const itemSales = useMemo(() => itemSalesFor(menu), [menu]);
  const sliceMap = { day: 1, week: 7, month: 30 };
  const periodData = DAILY_SALES.slice(-sliceMap[period]);
  const totalRevenue = periodData.reduce((s,d)=>s+d.revenue,0);
  const totalProfit = periodData.reduce((s,d)=>s+d.profit,0);
  const weekData = periodData;
  const monthCompare = [
    { name: "3 mo ago", revenue: DAILY_SALES.slice(0,30).reduce((s,d)=>s+d.revenue,0) },
    { name: "2 mo ago", revenue: DAILY_SALES.slice(30,60).reduce((s,d)=>s+d.revenue,0) },
    { name: "Last mo", revenue: DAILY_SALES.slice(60,90).reduce((s,d)=>s+d.revenue,0) },
  ];
  const catPie = CATS.map((cat) => ({ name: cat, value: itemSales.filter(i=>i.cat===cat).reduce((s,i)=>s+i.revenue,0) }));

  return (
    <Stack spacing={3}>
      <Box sx={{display:'flex',alignItems:'start',justifyContent:'space-between'}}>
       <SectionHead eyebrow="Unified Business Intelligence" title="Everything, one hub"  />
       <ToggleButtonGroup exclusive value={period} onChange={(e,v)=>v&&setPeriod(v)} size="medium">
          {["day","week","month"].map((r) => <ToggleButton key={r} value={r} sx={{ textTransform: "capitalize", px: 2, "&.Mui-selected": { bgcolor: PALETTE.garnet, color: "#fff", "&:hover": { bgcolor: PALETTE.garnetLight } } }}>{r}</ToggleButton>)}
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
        <Grid item xs={6} md={3}>
          <DetailsTrigger title="Revenue Detail" subtitle={`Daily revenue — last ${period}`}
            columns={[{key:"label",label:"Date"},{key:"dow",label:"Day"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)}]} rows={periodData}
            onRowClick={(day) => ({
              title: `Revenue Breakdown — ${day.label}`, subtitle: `${fmtUSD2(day.revenue)} — items sold that day`,
              columns: [{key:"name",label:"Item"},{key:"cat",label:"Category"},{key:"qty",label:"Qty Sold"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)}],
              rows: itemsSoldForDay(day, itemSales),
            })}>
            <KpiCard icon={AttachMoney} label={`${period === "day" ? "Today's" : period === "week" ? "Weekly" : "Monthly"} Revenue`} value={fmtUSD(totalRevenue)} delta="+12.4% QoQ" />
          </DetailsTrigger>
        </Grid>
        <Grid item xs={6} md={3}>
          <DetailsTrigger title="Profit Detail" subtitle={`Daily profit — last ${period}`}
            columns={[{key:"label",label:"Date"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)},{key:"profit",label:"Profit",value:(r)=>fmtUSD2(r.profit)}]} rows={periodData}
            onRowClick={(day) => ({
              title: `Profit Breakdown — ${day.label}`, subtitle: `${fmtUSD2(day.profit)} profit — items sold that day`,
              columns: [{key:"name",label:"Item"},{key:"cat",label:"Category"},{key:"qty",label:"Qty Sold"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)}],
              rows: itemsSoldForDay(day, itemSales),
            })}>
            <KpiCard icon={TrendingUp} label={`${period === "day" ? "Today's" : period === "week" ? "Weekly" : "Monthly"} Profit`} value={fmtUSD(totalProfit)} delta={`${((totalProfit/totalRevenue)*100).toFixed(1)}% margin`} />
          </DetailsTrigger>
        </Grid>
        <Grid item xs={6} md={3}>
          <DetailsTrigger title="Low Stock Items" subtitle="Items at or below their reorder threshold"
            columns={[{key:"name",label:"Item"},{key:"qty",label:"On Hand"},{key:"threshold",label:"Threshold"},{key:"unit",label:"Unit"}]} rows={INVENTORY_SEED.filter(i=>i.qty<=i.threshold)}>
            <KpiCard icon={Inventory2} label="Low Stock Items" value={INVENTORY_SEED.filter(i=>i.qty<=i.threshold).length} positive={false} delta="Needs reorder" />
          </DetailsTrigger>
        </Grid>
        <Grid item xs={6} md={3}>
          <DetailsTrigger title="Reservations" subtitle="All booked tables"
            columns={[{key:"name",label:"Name"},{key:"phone",label:"Phone"},{key:"table",label:"Table"},{key:"partySize",label:"Party Size"},{key:"date",label:"Date"}]} rows={reservations || []}>
            <KpiCard icon={CalendarMonth} label="Reservations" value={(reservations || []).length} delta={(reservations||[]).length ? "Click to view all" : "None yet"} />
          </DetailsTrigger>
        </Grid>
      </Grid>
      <Grid container spacing={2.5} sx={{ width: '100%', margin: 0 }}>
        <Grid item sx={{width:'49%'}} xs={12} lg={8}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border, height: "100%" }}>
            <Stack direction="row" sx={{display:'flex',alignItems:'center',justifyContent:'space-between'}} justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600 }}>{period === "day" ? "Today's" : period === "week" ? "Weekly" : "Monthly"} trend — line</Typography>
              <IconButton size="small" onClick={()=>setHubTrendDetailsOpen(true)} title="Details & export" sx={{ border: `1px solid ${PALETTE.border}` }}><Download sx={{ fontSize: 13 }} /></IconButton>
            </Stack>
            <DetailsDialog open={hubTrendDetailsOpen} onClose={()=>setHubTrendDetailsOpen(false)} title={`${period === "day" ? "Today's" : period === "week" ? "Weekly" : "Monthly"} Trend Detail`} subtitle="Revenue and profit by day"
              columns={[{key:"label",label:"Date"},{key:"dow",label:"Day"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)},{key:"profit",label:"Profit",value:(r)=>fmtUSD2(r.profit)}]} rows={weekData}
              onRowClick={(day) => ({
                title: `Breakdown — ${day.label}`, subtitle: `Items sold that day`,
                columns: [{key:"name",label:"Item"},{key:"cat",label:"Category"},{key:"qty",label:"Qty Sold"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)}],
                rows: itemsSoldForDay(day, itemSales),
              })} />
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={weekData}>
                <CartesianGrid strokeDasharray="3 3" stroke={PALETTE.border} />
                <XAxis dataKey="label" tick={{ fill: PALETTE.inkDim, fontSize: 11 }} />
                <YAxis tick={{ fill: PALETTE.inkDim, fontSize: 11 }} tickFormatter={fmtUSD} width={55} />
                <Tooltip content={<ChartTooltip />} />
                <Line type="monotone" dataKey="revenue" stroke={PALETTE.brass} strokeWidth={2} name="Revenue" />
                <Line type="monotone" dataKey="profit" stroke={PALETTE.garnet} strokeWidth={2} name="Profit" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item sx={{width:'49%'}} xs={12} lg={4}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border, height: "100%" }}>
            <Stack direction="row" sx={{display:'flex',alignItems:'center',justifyContent:'space-between'}} justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600 }}>Category split — pie</Typography>
              <IconButton size="small" onClick={()=>setHubCatDetailsOpen(true)} title="Details & export" sx={{ border: `1px solid ${PALETTE.border}` }}><Download sx={{ fontSize: 13 }} /></IconButton>
            </Stack>
            <DetailsDialog open={hubCatDetailsOpen} onClose={()=>setHubCatDetailsOpen(false)} title="Category Split Detail" subtitle="90-day revenue by category"
              columns={[{key:"name",label:"Category"},{key:"value",label:"Revenue",value:(r)=>fmtUSD2(r.value)}]} rows={catPie} />
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={catPie} dataKey="value" nameKey="name" outerRadius={80} paddingAngle={2}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={{ stroke: PALETTE.inkDim, strokeWidth: 1 }}>
                  {catPie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}</Pie>
                <Tooltip content={<ChartTooltip />} />
                <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 12, color: PALETTE.inkDim }} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
      <Grid container spacing={2.5} sx={{ width: '100%', margin: 0 }}>
        <Grid item xs={12} sx={{width:'49%'}} md={6}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border, height: "100%" }}>
            <Stack direction="row" sx={{display:'flex',alignItems:'center',justifyContent:'space-between'}} justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600 }}>Monthly comparison — bar</Typography>
              <IconButton size="small" onClick={()=>setHubMonthDetailsOpen(true)} title="Details & export" sx={{ border: `1px solid ${PALETTE.border}` }}><Download sx={{ fontSize: 13 }} /></IconButton>
            </Stack>
            <DetailsDialog open={hubMonthDetailsOpen} onClose={()=>setHubMonthDetailsOpen(false)} title="Monthly Comparison Detail" subtitle="Revenue by 30-day period"
              columns={[{key:"name",label:"Period"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)}]} rows={monthCompare} />
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={monthCompare}>
                <CartesianGrid strokeDasharray="3 3" stroke={PALETTE.border} />
                <XAxis dataKey="name" tick={{ fill: PALETTE.inkDim, fontSize: 11 }} />
                <YAxis tick={{ fill: PALETTE.inkDim, fontSize: 11 }} tickFormatter={fmtUSD} width={55} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="revenue" fill={PALETTE.brass} radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} sx={{width:'49%'}} md={6}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border, height: "100%" }}>
            <Stack direction="row" sx={{display:'flex',alignItems:'center',justifyContent:'space-between'}} justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600 }}>90-day revenue vs profit — area</Typography>
              <IconButton size="small" onClick={()=>setHubAreaDetailsOpen(true)} title="Details & export" sx={{ border: `1px solid ${PALETTE.border}` }}><Download sx={{ fontSize: 13 }} /></IconButton>
            </Stack>
            <DetailsDialog open={hubAreaDetailsOpen} onClose={()=>setHubAreaDetailsOpen(false)} title="90-Day Revenue vs Profit Detail" subtitle="Full daily breakdown"
              columns={[{key:"label",label:"Date"},{key:"dow",label:"Day"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)},{key:"profit",label:"Profit",value:(r)=>fmtUSD2(r.profit)}]} rows={DAILY_SALES} />
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={DAILY_SALES.filter((_,i)=>i%3===0)}>
                <defs><linearGradient id="hubGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={PALETTE.garnet} stopOpacity={0.4} /><stop offset="95%" stopColor={PALETTE.garnet} stopOpacity={0.02} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke={PALETTE.border} />
                <XAxis dataKey="label" tick={{ fill: PALETTE.inkDim, fontSize: 9 }} minTickGap={30} />
                <YAxis tick={{ fill: PALETTE.inkDim, fontSize: 10 }} tickFormatter={fmtUSD} width={55} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="revenue" stroke={PALETTE.garnet} fill="url(#hubGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border }}>
        <Stack direction="row" spacing={1} alignItems="center"><AutoAwesome sx={{ fontSize: 16, color: PALETTE.brass }} /><Typography sx={{ fontWeight: 600 }}>AI Insight of the Week</Typography></Stack>
        <Typography sx={{ fontSize: 13.5, color: PALETTE.inkDim, mt: 1 }}>
          Weekend dinner service (Fri–Sat, 7–8:30PM) drives 41% of weekly revenue. Filet Mignon wastage is trending down 9% after last week's par-stock adjustment. Consider a $2 price test on Old Fashioned — current margin is your highest at 190%.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button size="small" endIcon={<ChevronRight sx={{ fontSize: 14 }} />} onClick={()=>onNavigate("sales")} sx={{ bgcolor: PALETTE.bg, color: PALETTE.garnet, border: `1px solid ${PALETTE.border}` }}>Open Sales</Button>
          <Button size="small" endIcon={<ChevronRight sx={{ fontSize: 14 }} />} onClick={()=>onNavigate("inventory")} sx={{ bgcolor: PALETTE.bg, color: PALETTE.garnet, border: `1px solid ${PALETTE.border}` }}>Open Inventory</Button>
        </Stack>
      </Paper>
    </Stack>
  );
}

/* ======================= CUSTOMER MEMORY + RESERVATIONS ======================= */

function TopCustomers({ customers }) {
  const [order, setOrder] = useState("desc");
  const [count, setCount] = useState(5);
  const [selected, setSelected] = useState(null);
  const sorted = useMemo(() => {
    const arr = [...customers].sort((a, b) => order === "desc" ? b.totalSpend - a.totalSpend : a.totalSpend - b.totalSpend);
    return count === "all" ? arr : arr.slice(0, count);
  }, [customers, order, count]);
  const maxSpend = Math.max(...customers.map((c) => c.totalSpend), 1);

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" rowGap={1} sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600 }}>
          Customers by spend — click a row for full detail
        </Typography>
        <Stack direction="row" spacing={1}>
          <ToggleButtonGroup exclusive size="small" value={count} onChange={(e,v)=>v!==null && setCount(v)}>
            <ToggleButton value={5} sx={{ fontSize: 11, px: 1.2, "&.Mui-selected": { bgcolor: alpha(PALETTE.brass,0.18), color: PALETTE.brass } }}>Top 5</ToggleButton>
            <ToggleButton value={10} sx={{ fontSize: 11, px: 1.2, "&.Mui-selected": { bgcolor: alpha(PALETTE.brass,0.18), color: PALETTE.brass } }}>Top 10</ToggleButton>
            <ToggleButton value="all" sx={{ fontSize: 11, px: 1.2, "&.Mui-selected": { bgcolor: alpha(PALETTE.brass,0.18), color: PALETTE.brass } }}>All</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup exclusive size="small" value={order} onChange={(e,v)=>v && setOrder(v)}>
            <ToggleButton value="desc" sx={{ fontSize: 11, px: 1.2, "&.Mui-selected": { bgcolor: alpha(PALETTE.garnet,0.15), color: PALETTE.garnet } }}>
              <TrendingDown sx={{ fontSize: 13, mr: 0.5, transform: "rotate(180deg)" }} />High → Low
            </ToggleButton>
            <ToggleButton value="asc" sx={{ fontSize: 11, px: 1.2, "&.Mui-selected": { bgcolor: alpha(PALETTE.garnet,0.15), color: PALETTE.garnet } }}>
              <TrendingUp sx={{ fontSize: 13, mr: 0.5, transform: "rotate(180deg)" }} />Low → High
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>
      <Stack spacing={1}>
        {sorted.map((c, i) => (
          <Stack key={c.id} direction="row" spacing={1.5} alignItems="center" onClick={()=>setSelected(c)}
            sx={{ p: 1, borderRadius: 2, bgcolor: PALETTE.bg, cursor: "pointer", transition: "background-color .15s", "&:hover": { bgcolor: alpha(PALETTE.brass, 0.1) } }}>
            <Typography sx={{ fontSize: 12, color: PALETTE.brass, fontWeight: 700, width: 22, textAlign: "center" }}>#{i + 1}</Typography>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Stack direction="row" justifyContent="space-between">
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{c.name}</Typography>
                  <Typography sx={{ fontSize: 11, color: PALETTE.inkDim, fontFamily: "JetBrains Mono, monospace" }}>{c.phone}</Typography>
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: PALETTE.garnet, fontFamily: "JetBrains Mono, monospace" }}>{fmtUSD2(c.totalSpend)}</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={(c.totalSpend / maxSpend) * 100}
                sx={{ height: 5, borderRadius: 3, mt: 0.5, bgcolor: PALETTE.border, "& .MuiLinearProgress-bar": { bgcolor: PALETTE.brass, borderRadius: 3 } }} />
            </Box>
           <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 100, justifyContent: "flex-end" }}>
              <Chip size="small" label={loyaltyTier(c.totalSpend).label} sx={{ height: 18, fontSize: 9.5, bgcolor: alpha(loyaltyTier(c.totalSpend).color,0.15), color: loyaltyTier(c.totalSpend).color, fontWeight: 700 }} />
              <Typography sx={{ fontSize: 11, color: PALETTE.inkDim }}>{c.visits} visits</Typography>
            </Stack>
          </Stack>
        ))}
      </Stack>
      <Dialog open={Boolean(selected)} onClose={()=>setSelected(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogContent sx={{ bgcolor: PALETTE.bg }}>
          {selected && <CustomerDetailCard customer={selected} />}
          <Button fullWidth onClick={()=>setSelected(null)} sx={{ mt: 2, color: PALETTE.inkDim }}>Close</Button>
        </DialogContent>
      </Dialog>
    </Paper>
  );
}

function CustomerDetailCard({ customer }) {
  const monthVisitDays = useMemo(() => {
    const now = new Date();
    return new Set(customer.orders.filter(o => o.date.getMonth() === now.getMonth() && o.date.getFullYear() === now.getFullYear()).map(o => o.date.getDate()));
  }, [customer]);
  const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth()+1, 0).getDate();
  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, borderColor: PALETTE.border }}>
      <Stack direction="row" justifyContent="space-between" flexWrap="wrap" rowGap={2}>
        <Box>
          <Stack direction="row" spacing={0.5} alignItems="center"><AutoAwesome sx={{ fontSize: 13, color: PALETTE.brass }} /><Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.brass, fontWeight: 700 }}>Welcome back</Typography></Stack>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>{customer.name}</Typography>
          <Typography sx={{ fontSize: 13, color: PALETTE.inkDim, mt: 0.5 }}>Regular for {customer.memberSince} months · {customer.visits} visits · lifetime spend {fmtUSD2(customer.totalSpend)} · {customer.phone}</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Chip icon={<Star sx={{ fontSize: 14 }} />} label={`Favorite: ${customer.favorite}`} sx={{ bgcolor: alpha(PALETTE.brass,0.12), color: PALETTE.brass, height: 34 }} />
          <Chip label={`${loyaltyTier(customer.totalSpend).label} Member`} sx={{ bgcolor: alpha(loyaltyTier(customer.totalSpend).color, 0.15), color: loyaltyTier(customer.totalSpend).color, height: 34, fontWeight: 700 }} />
          {customer.pointsTier && <Chip icon={<Star sx={{ fontSize: 13 }} />} label={`${customer.pointsTier} points this month`} sx={{ bgcolor: alpha(PALETTE.brass, 0.15), color: PALETTE.brass, height: 34, fontWeight: 700 }} />}
        </Stack>
      </Stack>
      <GoldRule />
      <Grid container spacing={3} sx={{ width: '100%', margin: 0 }}>
        <Grid item xs={12} md={6}>
          <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 1 }}>Previous orders</Typography>
          <Stack spacing={0.8} sx={{ maxHeight: 220, overflowY: "auto" }}>
            {customer.orders.slice(0,8).map((o, i) => (
              <Stack key={i} direction="row" justifyContent="space-between" sx={{ fontSize: 12, px: 1.2, py: 1, borderRadius: 2, bgcolor: PALETTE.bg }}>
                <Typography sx={{ fontSize: 12, color: PALETTE.inkDim }}>{o.date.toLocaleDateString("en-US",{month:"short",day:"numeric"})}</Typography>
                <Typography sx={{ fontSize: 12 }}>{o.items.map(it=>it.name).join(", ")}</Typography>
                <Typography sx={{ fontSize: 12, color: PALETTE.garnet }}>{fmtUSD2(o.total)}</Typography>
              </Stack>
            ))}
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 1 }}>Visit calendar — this month</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
            {Array.from({length: daysInMonth}).map((_, i) => {
              const day = i+1; const visited = monthVisitDays.has(day);
              return <Box key={day} sx={{ aspectRatio: "1", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 1, fontSize: 10, bgcolor: visited?PALETTE.brass:PALETTE.bg, color: visited?"#fff":PALETTE.inkDim, fontWeight: visited?700:400 }}>{day}</Box>;
            })}
          </Box>
          <Typography sx={{ fontSize: 11.5, color: PALETTE.inkDim, mt: 1 }}>Highlighted = day visited this month ({monthVisitDays.size} visits)</Typography>
        </Grid>
      </Grid>
    </Paper>
  );
}

function CustomerLookup({ customers }) {
  const [phone, setPhone] = useState("");
  const [found, setFound] = useState(null);
  const [searched, setSearched] = useState(false);
  const doSearch = () => { setFound(phone.trim() ? customers.find((c) => c.phone.replace(/\D/g,"").includes(phone.replace(/\D/g,""))) : null); setSearched(true); };

  return (
    <Stack spacing={3}>
      <SectionHead eyebrow="AI Customer Memory" title="Personalized Welcome" />
      <TopCustomers customers={customers} />
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border }}>
        <Stack direction="row" spacing={1.5}>
          <TextField fullWidth size="small" value={phone} onChange={(e)=>setPhone(e.target.value)} placeholder="Enter customer phone number"
            onKeyDown={(e)=>e.key==="Enter"&&doSearch()} InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ fontSize: 16 }} /></InputAdornment> }} />
          <Button variant="contained" startIcon={<Search sx={{ fontSize: 16 }} />} onClick={doSearch} sx={{ bgcolor: PALETTE.garnet, whiteSpace: "nowrap", "&:hover": { bgcolor: PALETTE.garnetLight } }}>Look up</Button>
        </Stack>
        <Typography sx={{ fontSize: 11.5, color: PALETTE.inkDim, mt: 1 }}>Try: {customers[3]?.phone}, {customers[9]?.phone}, or {customers[15]?.phone}</Typography>
      </Paper>
      {searched && !found && <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: PALETTE.border }}><Typography sx={{ fontSize: 13, color: PALETTE.inkDim }}>No customer found — this would be a new guest.</Typography></Paper>}
      {found && <CustomerDetailCard customer={found} />}
    </Stack>
  );
}

function nextDates(n) {
  const out = [];
  const today = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(today); d.setDate(d.getDate() + i);
    out.push({
      iso: d.toISOString().slice(0, 10),
      label: i === 0 ? "Today" : i === 1 ? "Tomorrow" : d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }),
    });
  }
  return out;
}

const TIME_SLOTS = (() => {
  const slots = [];
  const start = 17 * 60;
  const end = 21 * 60 + 30;
  const fmt = (mins) => {
    let h = Math.floor(mins / 60), m = mins % 60;
    const ap = h >= 12 ? "PM" : "AM";
    h = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${h}:${m.toString().padStart(2, "0")} ${ap}`;
  };
  for (let t = start; t + 30 <= end; t += 30) {
    slots.push({ id: `${t}`, label: `${fmt(t)} – ${fmt(t + 30)}` });
  }
  return slots;
})();

function ReservationAgent({ reservations, onBookTable }) {
  const todayISO = new Date().toISOString().slice(0, 10);
  const [step, setStep] = useState("date");
  const [draft, setDraft] = useState({ date: "", dateLabel: "", timeSlot: "", partySize: null, table: null, name: "", phone: "" });
  const [log, setLog] = useState([{ from: "ai", text: "Hi! I'm Lindey's reservation assistant. Let's get you booked — what date works for you?" }]);
  const [booking, setBooking] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" }); }, [log]);
  const say = (from, text) => setLog((l) => [...l, { from, text }]);

  const resetAll = () => {
    setDraft({ date: "", dateLabel: "", timeSlot: "", partySize: null, table: null, name: "", phone: "" });
    setStep("date");
    setLog([{ from: "ai", text: "Hi! I'm Lindey's reservation assistant. Let's get you booked — what date works for you?" }]);
    setBookingError("");
  };

  const pickDateValue = (iso) => {
    const d = new Date(iso + "T00:00:00");
    const today = new Date(); today.setHours(0,0,0,0);
    const diffDays = Math.round((d - today) / 86400000);
    const label = diffDays === 0 ? "Today" : diffDays === 1 ? "Tomorrow" : d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
    pickDate({ iso, label });
  };

  const pickDate = (d) => {
    say("user", d.label);
    setDraft((p) => ({ ...p, date: d.iso, dateLabel: d.label }));
    say("ai", "Got it. What time works best?");
    setStep("time");
  };
  const pickTime = (slot) => {
    say("user", slot.label);
    setDraft((p) => ({ ...p, timeSlot: slot.label }));
    say("ai", "How many guests?");
    setStep("party");
  };
  const pickParty = (n) => {
    say("user", `${n} guests`);
    setDraft((p) => ({ ...p, partySize: n }));
    say("ai", `Here's what's available for ${n} on ${draft.dateLabel} at ${draft.timeSlot} — pick a table:`);
    setStep("table");
  };
  const tableTaken = (tableId) => reservations.some((r) => r.date === draft.date && r.table === tableId && (r.timeSlot ? r.timeSlot === draft.timeSlot : true));
  const pickTable = (t) => {
    say("user", `Table ${t.id}`);
    setDraft((p) => ({ ...p, table: t.id }));
    say("ai", "Last step — who's the reservation under? Name and phone number, then confirm.");
    setStep("contact");
  };
  const [bookingError, setBookingError] = useState("");
  const confirmBooking = async () => {
    if (!draft.name.trim() || !draft.phone.trim()) return;
    setBooking(true);
    setBookingError("");
    try {
      say("user", `${draft.name} · ${draft.phone}`);
      await onBookTable({ date: draft.date, timeSlot: draft.timeSlot, partySize: draft.partySize, table: draft.table, name: draft.name.trim(), phone: draft.phone.trim() });
      say("ai", `Booked! Table ${draft.table} for ${draft.partySize} on ${draft.dateLabel} at ${draft.timeSlot}, under ${draft.name}. We'll see you then! 🥩🔥`);
      setStep("done");
    } catch (e) {
      setBookingError(e.message || "Couldn't complete the booking — please try again.");
    } finally {
      setBooking(false);
    }
  };
  const startOver = () => {
    setDraft({ date: "", dateLabel: "", timeSlot: "", partySize: null, table: null, name: "", phone: "" });
    setStep("date");
    say("ai", "Sure — let's book another table. What date works?");
  };

  const suitableTables = draft.partySize ? TABLES.filter((t) => t.seats >= draft.partySize) : [];
  const recentReservations = [...reservations].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  return (
    <Stack spacing={3}>
      <Box sx={{display:'flex',alignItems:'start',justifyContent:'space-between'}}>
        <SectionHead eyebrow="AI Table Reservation Agent" title="Guided Booking" />
        <Button size="small" onClick={resetAll} sx={{ color: PALETTE.inkDim, border: `1px solid ${PALETTE.border}` }}>Reset</Button>
      </Box>
      <Grid container spacing={2.5} sx={{ width: '1200px', margin: 0 }}>
        <Grid sx={{display:'flex',justifyContent:'space-between',width:'100%'}} item xs={12} md={12} lg={12}>
           <Grid item md={8} sx={{width:'70%'}} xs={12} >
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border, height: 560, display: "flex", flexDirection: "column" }}>
            <Stack spacing={1} sx={{ flexGrow: 1, overflowY: "auto", pr: 0.5 }}>
              {log.map((m, i) => (
                <Box key={i} sx={{ display: "flex", justifyContent: m.from==="user"?"flex-end":"flex-start" }}>
                  <Box sx={{ maxWidth: "85%", px: 1.6, py: 1, borderRadius: 3, fontSize: 13.5, bgcolor: m.from==="user"?PALETTE.garnet:PALETTE.bg, color: m.from==="user"?"#fff":PALETTE.ink, border: m.from==="ai"?`1px solid ${PALETTE.border}`:"none" }}>{m.text}</Box>
                </Box>
              ))}
              <div ref={endRef} />
            </Stack>
            <Divider sx={{ my: 1.5 }} />
            {step === "date" && (
              <Stack spacing={1}>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {nextDates(6).map((d) => (
                    <Chip key={d.iso} label={d.label} onClick={()=>pickDate(d)} clickable
                      sx={{ bgcolor: alpha(PALETTE.brass,0.12), color: PALETTE.brass, fontWeight: 600 }} />
                  ))}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontSize: 12, color: PALETTE.inkDim }}>Or pick any date:</Typography>
                  <TextField type="date" size="small" inputProps={{ min: todayISO }} onChange={(e)=> e.target.value && pickDateValue(e.target.value)} sx={{ width: 170 }} />
                </Stack>
              </Stack>
            )}
            {step === "time" && (
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ height: 140, overflowY: "auto" }}>
                {TIME_SLOTS.map((s) => (
                  <Chip key={s.id} label={s.label} onClick={()=>pickTime(s)} clickable
                    sx={{ bgcolor: alpha(PALETTE.brass,0.12), color: PALETTE.brass, fontWeight: 600 }} />
                ))}
              </Stack>
            )}
            {step === "party" && (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {[2,3,4,5,6,8,10].map((n) => (
                  <Chip key={n} label={`${n} guests`} onClick={()=>pickParty(n)} clickable
                    sx={{ bgcolor: alpha(PALETTE.brass,0.12), color: PALETTE.brass, fontWeight: 600 }} />
                ))}
              </Stack>
            )}
            {step === "table" && (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {suitableTables.map((t) => {
                  const taken = tableTaken(t.id);
                  return (
                    <Chip key={t.id} label={`${t.id} · seats ${t.seats}${taken ? " · booked" : ""}`} clickable={!taken} disabled={taken}
                      onClick={()=>!taken && pickTable(t)}
                      sx={{ bgcolor: taken ? PALETTE.border : alpha(PALETTE.sage,0.14), color: taken ? PALETTE.inkDim : PALETTE.sage, fontWeight: 600 }} />
                  );
                })}
              </Stack>
            )}
            {step === "contact" && (
              <Stack spacing={1}>
                <Stack direction="row" spacing={1}>
                  <TextField size="small" fullWidth label="Name" value={draft.name} onChange={(e)=>setDraft({...draft, name: e.target.value})} />
                  <TextField size="small" fullWidth label="Phone" value={draft.phone} onChange={(e)=>setDraft({...draft, phone: e.target.value})} />
                </Stack>
                {bookingError && <Typography sx={{ fontSize: 12, color: PALETTE.alert, fontWeight: 600 }}>{bookingError}</Typography>}
                <Button variant="contained" disabled={booking || !draft.name.trim() || !draft.phone.trim()} onClick={confirmBooking}
                  sx={{ bgcolor: PALETTE.garnet, "&:hover": { bgcolor: PALETTE.garnetLight } }}>{booking ? "Booking…" : "Confirm booking"}</Button>
              </Stack>
            )}
            {step === "done" && (
              <Button variant="outlined" onClick={startOver} sx={{ borderColor: PALETTE.border, color: PALETTE.ink }}>Book another table</Button>
            )}
          </Paper>
        </Grid>
  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border, mb: 2 }}>
            <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 1 }}>Tables at a glance — today</Typography>
            <Stack spacing={0.6}>
              {TABLES.map((t) => {
                const bookedFor = reservations.filter((r) => r.table === t.id && r.date === todayISO);
                const isBooked = bookedFor.length > 0;
                return (
                  <Stack key={t.id} direction="row" justifyContent="space-between" alignItems="center"
                    sx={{ px: 1.2, py: 0.7, borderRadius: 2, bgcolor: isBooked ? alpha(PALETTE.alert, 0.08) : alpha(PALETTE.sage, 0.08) }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{t.id}</Typography>
                      <Typography sx={{ fontSize: 11, color: PALETTE.inkDim }}>seats {t.seats}</Typography>
                    </Stack>
                    {isBooked ? (
                      <Typography sx={{ fontSize: 11, color: PALETTE.alert, fontWeight: 600 }}>{bookedFor[0].name} · {bookedFor[0].partySize}p</Typography>
                    ) : (
                      <Chip size="small" label="Available" sx={{ height: 18, fontSize: 9.5, bgcolor: alpha(PALETTE.sage,0.15), color: PALETTE.sage, fontWeight: 700 }} />
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </Paper>
        </Grid>
        <Paper variant="outlined" sx={{ width:'100%',p: 2.5, borderRadius: 3, borderColor: PALETTE.border, height: 520, overflowY: "auto" }}>
            <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 1 }}>Recent bookings ({recentReservations.length})</Typography>
            {recentReservations.length === 0 ? (
              <Typography sx={{ fontSize: 12.5, color: PALETTE.inkDim }}>No reservations yet — bookings made here appear immediately, for every staff member.</Typography>
            ) : (
              <Stack sx={{display:'flex',flexDirection:'row',flexWrap:'wrap',gap:"15px"}} spacing={0.8}>
                {recentReservations.map((r) => (
                  <Box key={r.id} sx={{ px: 1.2, py: 0.9, borderRadius: 2, bgcolor: PALETTE.bg, minWidth: 180 }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{r.name}</Typography>
                      <Typography sx={{ fontSize: 12, color: PALETTE.garnet, fontWeight: 700 }}>{r.table}</Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 11, color: PALETTE.inkDim, fontFamily: "JetBrains Mono, monospace" }}>{r.phone}</Typography>
 <Typography sx={{ fontSize: 12, color: PALETTE.garnet, fontWeight: 700, mt: 0.4 }}>
  🕐 {r.timeSlot ? r.timeSlot : "Time not set"}
</Typography>
<Stack direction="row" justifyContent="space-between" sx={{ mt: 0.3 }}>
  <Typography sx={{ fontSize: 11, color: PALETTE.inkDim }}>{r.date}</Typography>
  <Typography sx={{ fontSize: 11, color: PALETTE.inkDim }}>{r.partySize} guests</Typography>
</Stack>
 </Box>
                ))}
              </Stack>
            )}
          </Paper>
      </Grid>
    </Stack>
  );
}

/* ============================ REPORTS ============================ */

function Reports({ inventory, wastageLog }) {
  const [period, setPeriod] = useState("week");
  const [downloaded, setDownloaded] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [reportDetailsOpen, setReportDetailsOpen] = useState(false);
  const [wastageDetailsOpen, setWastageDetailsOpen] = useState(false);
  const reportRef = useRef(null);

  const data = period === "week" ? DAILY_SALES.slice(-7) : DAILY_SALES.slice(-30);
  const revenue = data.reduce((s,d)=>s+d.revenue,0);
  const profit = data.reduce((s,d)=>s+d.profit,0);
  const periodWastage = period === "week" ? wastageLog.slice(0, 7) : wastageLog;
  const wasteCost = periodWastage.reduce((s,w)=>s+w.cost,0);
  const lowStock = inventory.filter(i=>i.qty<=i.threshold);
  const wasteChartData = [...periodWastage].reverse();

  const downloadPDF = async () => {
    if (!reportRef.current) return;
    setGenerating(true);
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"), import("jspdf"),
      ]);
      const canvas = await html2canvas(reportRef.current, { scale: 2, backgroundColor: "#FFFFFF", useCORS: true });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgProps = pdf.getImageProperties(imgData);
      const imgHeight = (imgProps.height * pageWidth) / imgProps.width;
      let heightLeft = imgHeight, position = 0;
      pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`lindeys_${period}_report.pdf`);
      setDownloaded(true);
    } catch (err) {
      console.error("PDF generation failed — make sure 'jspdf' and 'html2canvas' are installed (npm install jspdf html2canvas).", err);
      alert("PDF export needs two packages installed in this project: npm install jspdf html2canvas");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Stack spacing={3}>
      <SectionHead eyebrow="AI Weekly & Monthly Reports" title="Business Report Builder" action={
        <Stack direction="row" spacing={1.5} alignItems="center">
          <ToggleButtonGroup exclusive value={period} onChange={(e,v)=>v&&setPeriod(v)} size="small">
            {["week","month"].map(p => <ToggleButton key={p} value={p} sx={{ textTransform: "capitalize", px: 2, "&.Mui-selected": { bgcolor: PALETTE.garnet, color: "#fff", "&:hover": { bgcolor: PALETTE.garnetLight } } }}>{p}</ToggleButton>)}
          </ToggleButtonGroup>
          <Button variant="outlined" startIcon={<Download sx={{ fontSize: 16 }} />} onClick={()=>setReportDetailsOpen(true)} sx={{ borderColor: PALETTE.border, color: PALETTE.ink }}>View & export CSV</Button>
          <Button variant="contained" disabled={generating} startIcon={<Download sx={{ fontSize: 16 }} />} onClick={downloadPDF} sx={{ bgcolor: PALETTE.garnet, "&:hover": { bgcolor: PALETTE.garnetLight } }}>
            {generating ? "Generating…" : `Download PDF`}
          </Button>
        </Stack>
      } />
      <DetailsDialog open={reportDetailsOpen} onClose={()=>setReportDetailsOpen(false)} title={`${period === "week" ? "Weekly" : "Monthly"} Report — Sales Data`} subtitle="Full underlying data behind this report"
        columns={[{key:"label",label:"Date"},{key:"dow",label:"Day"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)},{key:"profit",label:"Profit",value:(r)=>fmtUSD2(r.profit)},{key:"orders",label:"Orders"}]} rows={data} />

      <Box ref={reportRef} sx={{ bgcolor: "#fff", p: { xs: 1, sm: 2 }, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <LocalFireDepartment sx={{ color: PALETTE.garnet }} />
          <Typography sx={{ fontFamily: "Fraunces, serif", fontWeight: 700, fontSize: 20 }}>Lindey's Prime Steakhouse</Typography>
          <Typography sx={{ fontSize: 12, color: PALETTE.inkDim, ml: 1 }}>· {period === "week" ? "Weekly" : "Monthly"} Business Report · Generated {new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}</Typography>
        </Stack>

        <Grid container spacing={2} sx={{ width: '100%', margin: 0, mb: 2.5 }}>
          <Grid item xs={6} md={3}>
            <DetailsTrigger title="Revenue Detail" subtitle={`${period === "week" ? "Weekly" : "Monthly"} daily revenue`}
              columns={[{key:"label",label:"Date"},{key:"dow",label:"Day"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)},{key:"orders",label:"Orders"}]} rows={data}>
              <KpiCard icon={AttachMoney} label="Revenue" value={fmtUSD(revenue)} />
            </DetailsTrigger>
          </Grid>
          <Grid item xs={6} md={3}>
            <DetailsTrigger title="Profit Detail" subtitle={`${period === "week" ? "Weekly" : "Monthly"} daily profit`}
              columns={[{key:"label",label:"Date"},{key:"revenue",label:"Revenue",value:(r)=>fmtUSD2(r.revenue)},{key:"profit",label:"Profit",value:(r)=>fmtUSD2(r.profit)}]} rows={data}>
              <KpiCard icon={TrendingUp} label="Profit" value={fmtUSD(profit)} delta={`${((profit/revenue)*100).toFixed(1)}% margin`} />
            </DetailsTrigger>
          </Grid>
          <Grid item xs={6} md={3}>
            <DetailsTrigger title="Wastage Cost Detail" subtitle={`${period === "week" ? "Weekly" : "Monthly"} wastage entries`}
              columns={[{key:"date",label:"Date"},{key:"item",label:"Item"},{key:"prepared",label:"Prepared"},{key:"sold",label:"Sold"},{key:"wastedUnits",label:"Wasted"},{key:"cost",label:"Cost",value:(r)=>fmtUSD2(r.cost)}]} rows={periodWastage}>
              <KpiCard icon={WarningAmber} label="Wastage Cost" value={fmtUSD2(wasteCost)} positive={false} delta={`${period} total`} />
            </DetailsTrigger>
          </Grid>
          <Grid item xs={6} md={3}>
            <DetailsTrigger title="Low Stock Detail" subtitle="Items at or below their reorder threshold"
              columns={[{key:"name",label:"Item"},{key:"qty",label:"On Hand"},{key:"threshold",label:"Threshold"},{key:"par",label:"Par Level"},{key:"unit",label:"Unit"}]} rows={lowStock}>
              <KpiCard icon={Inventory2} label="Low Stock" value={lowStock.length} positive={false} />
            </DetailsTrigger>
          </Grid>
        </Grid>

        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border, mb: 2.5 }}>
          <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600 }}>{period === "week" ? "Weekly" : "Monthly"} revenue vs profit — or loss</Typography>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data}>
              <defs><linearGradient id="repGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={PALETTE.brass} stopOpacity={0.45}/><stop offset="95%" stopColor={PALETTE.brass} stopOpacity={0.02}/></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke={PALETTE.border} />
              <XAxis dataKey="label" tick={{ fill: PALETTE.inkDim, fontSize: 10 }} minTickGap={20} />
              <YAxis tick={{ fill: PALETTE.inkDim, fontSize: 10 }} tickFormatter={fmtUSD} width={55} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="revenue" stroke={PALETTE.brass} fill="url(#repGrad)" name="Revenue" />
              <Line type="monotone" dataKey="profit" stroke={PALETTE.garnet} strokeWidth={2} name="Profit" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border, mb: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600 }}>Prep · sold · wastage by day</Typography>
            <IconButton size="small" onClick={()=>setWastageDetailsOpen(true)} title="Details & export" sx={{ border: `1px solid ${PALETTE.border}` }}><Download sx={{ fontSize: 13 }} /></IconButton>
          </Stack>
          <DetailsDialog open={wastageDetailsOpen} onClose={()=>setWastageDetailsOpen(false)} title="Wastage Detail" subtitle={`${period === "week" ? "Weekly" : "Monthly"} prep vs sold vs wasted`}
            columns={[{key:"date",label:"Date"},{key:"item",label:"Item"},{key:"prepared",label:"Prepared"},{key:"sold",label:"Sold"},{key:"wastedUnits",label:"Wasted"},{key:"cost",label:"Cost",value:(r)=>fmtUSD2(r.cost)}]} rows={periodWastage} />
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={wasteChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={PALETTE.border} />
              <XAxis dataKey="date" tick={{ fill: PALETTE.inkDim, fontSize: 10 }} />
              <YAxis tick={{ fill: PALETTE.inkDim, fontSize: 10 }} width={30} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="prepared" name="Prepared" fill={PALETTE.brassLight} radius={[4,4,0,0]} />
              <Bar dataKey="sold" name="Sold" fill={PALETTE.sage} radius={[4,4,0,0]} />
              <Bar dataKey="wastedUnits" name="Wasted" fill={PALETTE.alert} radius={[4,4,0,0]} />
              <Legend wrapperStyle={{ fontSize: 11, color: PALETTE.inkDim }} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border, mb: 2.5 }}>
          <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 1 }}>Inventory snapshot</Typography>
          <Table size="small">
            <TableHead><TableRow>{["Item","On hand","Par","Status"].map(h=><TableCell key={h} sx={{ fontSize: 10.5, textTransform: "uppercase", color: PALETTE.inkDim, fontWeight: 700, border: 0 }}>{h}</TableCell>)}</TableRow></TableHead>
            <TableBody>
              {inventory.map((i) => (
                <TableRow key={i.id}>
                  <TableCell sx={{ borderColor: PALETTE.border, fontSize: 12.5 }}>{i.name}</TableCell>
                  <TableCell sx={{ borderColor: PALETTE.border, fontSize: 12.5 }}>{i.qty}{i.unit}</TableCell>
                  <TableCell sx={{ borderColor: PALETTE.border, fontSize: 12.5 }}>{i.par}{i.unit}</TableCell>
                  <TableCell sx={{ borderColor: PALETTE.border }}>
                    <Chip size="small" label={i.qty<=i.threshold ? "Reorder" : "OK"} sx={{ fontSize: 10, bgcolor: alpha(i.qty<=i.threshold?PALETTE.alert:PALETTE.sage,0.14), color: i.qty<=i.threshold?PALETTE.alert:PALETTE.sage }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, borderColor: PALETTE.border }}>
          <Stack direction="row" spacing={1} alignItems="center"><AutoAwesome sx={{ fontSize: 16, color: PALETTE.brass }} /><Typography sx={{ fontWeight: 600 }}>AI-generated summary</Typography></Stack>
          <Typography sx={{ fontSize: 13.5, color: PALETTE.inkDim, mt: 1 }}>
            This {period} generated {fmtUSD(revenue)} in revenue at a {((profit/revenue)*100).toFixed(1)}% profit margin — driven primarily by Prime Ribeye and Filet Mignon. Wastage cost {fmtUSD2(wasteCost)} across {periodWastage.length} logged prep entries, with {lowStock.length} item(s) currently below par and needing reorder. Recommended action: tighten weekday side-dish prep and re-test Old Fashioned pricing.
          </Typography>
        </Paper>
      </Box>

      {downloaded && <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: PALETTE.sage }}><CheckCircle sx={{ fontSize: 14 }} /><Typography sx={{ fontSize: 12 }}>lindeys_{period}_report.pdf downloaded.</Typography></Stack>}
    </Stack>
  );
}

/* ============================ OWNER CONTROLS ============================ */

function OwnerControls({ shopInfo, onUpdateShopInfo, menu, onAddProduct, onUpdateProduct, onDeleteProduct, banners, onSaveBanners, aboutCards, onSaveAboutCards }) {
  const [draftBanners, setDraftBanners] = useState(banners);
  const [draftAbout, setDraftAbout] = useState(aboutCards);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState("");
  useEffect(() => { setDraftBanners(banners); }, [banners]);
  useEffect(() => { setDraftAbout(aboutCards); }, [aboutCards]);

  const updateDraft = (idx, field, value) => setDraftBanners((prev) => prev.map((b, i) => i === idx ? { ...b, [field]: value } : b));
  const updateAboutDraft = (idx, field, value) => setDraftAbout((prev) => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  const dirtyBanners = JSON.stringify(draftBanners) !== JSON.stringify(banners);
  const dirtyAbout = JSON.stringify(draftAbout) !== JSON.stringify(aboutCards);

  const saveBanners = async () => {
    try { await onSaveBanners(draftBanners); setSaved(true); } catch (e) { setSaveError(e.message || String(e)); }
  };
  const saveAbout = async () => {
    try { await onSaveAboutCards(draftAbout); setSaved(true); } catch (e) { setSaveError(e.message || String(e)); }
  };

  return (
    <Stack spacing={3}>
      <SectionHead eyebrow="Owner Controls" title="Shop Front & Configuration" />
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, borderColor: PALETTE.border, position: "relative", overflow: "hidden" }}>
        <Box sx={{ position: "absolute", inset: 0, opacity: 0.06, background: `linear-gradient(120deg, ${PALETTE.garnet}, transparent)` }} />
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, mb: 0.5, position: "relative" }}>Live preview — reflects on public site</Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, position: "relative" }}>{shopInfo.name}</Typography>
        <Typography sx={{ color: PALETTE.garnet, mt: 0.5, position: "relative" }}>{shopInfo.tagline}</Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, borderColor: PALETTE.border }}>
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 1.5 }}>
          Shop identity — every field below is editable so this build can be reused for a different restaurant
        </Typography>
        <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Shop name / banner" value={shopInfo.name} onChange={(e)=>onUpdateShopInfo({ name: e.target.value })} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Tagline" value={shopInfo.tagline} onChange={(e)=>onUpdateShopInfo({ tagline: e.target.value })} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Address" value={shopInfo.address || ""} onChange={(e)=>onUpdateShopInfo({ address: e.target.value })} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Phone" value={shopInfo.phone || ""} onChange={(e)=>onUpdateShopInfo({ phone: e.target.value })} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Hours — weekdays" value={shopInfo.hoursWeekday || ""} onChange={(e)=>onUpdateShopInfo({ hoursWeekday: e.target.value })} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Hours — weekend" value={shopInfo.hoursWeekend || ""} onChange={(e)=>onUpdateShopInfo({ hoursWeekend: e.target.value })} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="number" label="Sales tax rate (%)" value={shopInfo.taxRate} onChange={(e)=>onUpdateShopInfo({ taxRate: e.target.value })} /></Grid>
          <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center" }}><Typography sx={{ fontSize: 12, color: PALETTE.inkDim }}>Saved automatically as you type — live for every visitor immediately.</Typography></Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, borderColor: PALETTE.border }}>
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 0.5 }}>Home page banners — auto-swiping carousel</Typography>
        <Typography sx={{ fontSize: 12, color: PALETTE.inkDim, mb: 2 }}>Edit any banner's image, title, or subtitle below, then press Save — this updates the live site for every visitor immediately. Add as many as you like, or remove one.</Typography>
        <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
          {draftBanners.map((b, i) => (
            <Grid item xs={12} md={4} key={b.id ?? i}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, borderColor: PALETTE.border, height: "100%", position: "relative" }}>
                <IconButton size="small" onClick={()=>setDraftBanners((prev)=>prev.filter((_,idx)=>idx!==i))}
                  sx={{ position: "absolute", top: 6, right: 6, bgcolor: alpha(PALETTE.alert,0.1), "&:hover": { bgcolor: alpha(PALETTE.alert,0.2) }, zIndex: 1 }}
                  title="Remove this banner"><Close sx={{ fontSize: 15, color: PALETTE.alert }} /></IconButton>
                <SmartImg src={b.img} fallback={b.imgLocal} alt="" sx={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 2, mb: 1.2 }} />
                <Stack spacing={1}>
                  <TextField size="small" fullWidth label="Image URL" value={b.img} onChange={(e)=>updateDraft(i, "img", e.target.value)} />
                  <TextField size="small" fullWidth label="Title" value={b.title} onChange={(e)=>updateDraft(i, "title", e.target.value)} />
                  <TextField size="small" fullWidth label="Subtitle" value={b.subtitle} onChange={(e)=>updateDraft(i, "subtitle", e.target.value)} />
                </Stack>
              </Paper>
            </Grid>
          ))}
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" onClick={()=>setDraftBanners((prev)=>[...prev, { img: "", title: "New banner", subtitle: "" }])}
              sx={{ height: "100%", minHeight: 220, borderRadius: 3, borderColor: PALETTE.border, borderStyle: "dashed", display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", cursor: "pointer", color: PALETTE.inkDim, "&:hover": { borderColor: PALETTE.brass, color: PALETTE.brass } }}>
              <Add sx={{ fontSize: 28, mb: 0.5 }} />
              <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>Add a banner</Typography>
            </Paper>
          </Grid>
        </Grid>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
          <Button variant="contained" disabled={!dirtyBanners} onClick={saveBanners} sx={{ bgcolor: PALETTE.garnet, "&:hover": { bgcolor: PALETTE.garnetLight } }}>Save banners</Button>
          {dirtyBanners && <Typography sx={{ fontSize: 12, color: PALETTE.brass }}>Unsaved changes</Typography>}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, borderColor: PALETTE.border }}>
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 0.5 }}>Home page "About" cards</Typography>
        <Typography sx={{ fontSize: 12, color: PALETTE.inkDim, mb: 2 }}>The cards under the hero banner (dining room / kitchen / cellar, or whatever fits a new shop) — image, title, and description, all editable. Add as many as you like, or remove one.</Typography>
        <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
          {draftAbout.map((c, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, borderColor: PALETTE.border, height: "100%", position: "relative" }}>
                <IconButton size="small" onClick={()=>setDraftAbout((prev)=>prev.filter((_,idx)=>idx!==i))}
                  sx={{ position: "absolute", top: 6, right: 6, bgcolor: alpha(PALETTE.alert,0.1), "&:hover": { bgcolor: alpha(PALETTE.alert,0.2) }, zIndex: 1 }}
                  title="Remove this card"><Close sx={{ fontSize: 15, color: PALETTE.alert }} /></IconButton>
                <SmartImg src={c.img} fallback={c.imgLocal} alt="" sx={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 2, mb: 1.2 }} />
                <Stack spacing={1}>
                  <TextField size="small" fullWidth label="Image URL" value={c.img} onChange={(e)=>updateAboutDraft(i, "img", e.target.value)} />
                  <TextField size="small" fullWidth label="Title" value={c.title} onChange={(e)=>updateAboutDraft(i, "title", e.target.value)} />
                  <TextField size="small" fullWidth multiline minRows={2} label="Description" value={c.desc} onChange={(e)=>updateAboutDraft(i, "desc", e.target.value)} />
                </Stack>
              </Paper>
            </Grid>
          ))}
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" onClick={()=>setDraftAbout((prev)=>[...prev, { img: "", title: "New card", desc: "" }])}
              sx={{ height: "100%", minHeight: 200, borderRadius: 3, borderColor: PALETTE.border, borderStyle: "dashed", display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", cursor: "pointer", color: PALETTE.inkDim, "&:hover": { borderColor: PALETTE.brass, color: PALETTE.brass } }}>
              <Add sx={{ fontSize: 28, mb: 0.5 }} />
              <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>Add a card</Typography>
            </Paper>
          </Grid>
        </Grid>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
          <Button variant="contained" disabled={!dirtyAbout} onClick={saveAbout} sx={{ bgcolor: PALETTE.garnet, "&:hover": { bgcolor: PALETTE.garnetLight } }}>Save about cards</Button>
          {dirtyAbout && <Typography sx={{ fontSize: 12, color: PALETTE.brass }}>Unsaved changes</Typography>}
        </Stack>
      </Paper>

      <ProfitIntelligence menu={menu} canEdit={true} onAddProduct={onAddProduct} onUpdateProduct={onUpdateProduct} onDeleteProduct={onDeleteProduct} />
      <Snackbar open={saved} autoHideDuration={2500} onClose={()=>setSaved(false)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="success" variant="filled" onClose={()=>setSaved(false)} sx={{ bgcolor: PALETTE.sage }}>Saved — live for every visitor now.</Alert>
      </Snackbar>
      <Snackbar open={Boolean(saveError)} autoHideDuration={5000} onClose={()=>setSaveError("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="error" variant="filled" onClose={()=>setSaveError("")}>{saveError}</Alert>
      </Snackbar>
    </Stack>
  );
}

/* ============================ STAFF & CUSTOMER DIRECTORY ============================ */

function TableAssignments({ tableAssignments, onAssignTable, onUnassignTable, orderQueue, reservations, customers = [] }) {
  const todayISO = new Date().toISOString().slice(0, 10);
  const [detailTable, setDetailTable] = useState(null);
  return (
    <Stack spacing={3}>
      <SectionHead eyebrow="Table Management" title="Assign Servants to Tables" />
      <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
        {TABLES.map((t) => {
          const assignment = tableAssignments.find((a) => a.table === t.id);
          const activeOrder = orderQueue.find((o) => o.table === t.id && o.status !== "served");
          const reservedToday = reservations.find((r) => r.table === t.id && r.date === todayISO);
          const hasDetail = Boolean(activeOrder || reservedToday);
          return (
            <Grid item xs={12} sm={6} md={4} key={t.id}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: activeOrder ? PALETTE.brass : PALETTE.border, height: "100%" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{t.id}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: PALETTE.inkDim }}>seats {t.seats}</Typography>
                </Stack>
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <Select displayEmpty value={assignment?.servantId || ""} renderValue={(v) => v ? SERVANTS.find(s=>s.id===v)?.name : "Unassigned"}
                    onChange={(e) => {
                      if (!e.target.value) onUnassignTable(t.id);
                      else { const s = SERVANTS.find(x => x.id === e.target.value); onAssignTable(t.id, s.id, s.name); }
                    }}>
                    <MenuItem value="">Unassigned</MenuItem>
                    {SERVANTS.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                  </Select>
                </FormControl>
                {activeOrder && (
                  <Chip size="small" label={`Active order: ${activeOrder.status}`} sx={{ bgcolor: alpha(STATUS_COLOR[activeOrder.status], 0.15), color: STATUS_COLOR[activeOrder.status], fontWeight: 700, mb: 0.5 }} />
                )}
                {reservedToday && <Typography sx={{ fontSize: 11, color: PALETTE.brass, mt: 0.5 }}>Reserved today: {reservedToday.name} · {reservedToday.partySize}p</Typography>}
                {!activeOrder && !reservedToday && <Typography sx={{ fontSize: 11.5, color: PALETTE.sage, fontWeight: 600 }}>Available</Typography>}
                {hasDetail && (
                  <Button size="small" fullWidth onClick={()=>setDetailTable({ t, activeOrder, reservedToday })} sx={{ mt: 1, fontSize: 11, color: PALETTE.garnet, border: `1px solid ${PALETTE.border}` }}>View guest details</Button>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>
      <Dialog open={Boolean(detailTable)} onClose={()=>setDetailTable(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogContent>
          {detailTable && (() => {
            const { t, activeOrder, reservedToday } = detailTable;
            const guestName = activeOrder?.name || reservedToday?.name || "";
            const guestPhone = activeOrder?.phone || reservedToday?.phone || "";
            const matchedCustomer = guestPhone ? customers.find((c) => c.phone.replace(/\D/g,"") === guestPhone.replace(/\D/g,"")) : null;
            return (
              <Stack spacing={1.5}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{t.id} — Guest Details</Typography>
                {guestName && <Typography sx={{ fontSize: 14 }}><b>Name:</b> {guestName}</Typography>}
                {guestPhone && <Typography sx={{ fontSize: 14 }}><b>Phone:</b> {guestPhone}</Typography>}
                {activeOrder && (
                  <>
                    <Divider />
                    <Typography sx={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700 }}>Ordered items</Typography>
                    <Stack spacing={0.4}>{activeOrder.items.map((it,i) => <Typography key={i} sx={{ fontSize: 13 }}>• {it}</Typography>)}</Stack>
                    <Chip size="small" label={`Status: ${activeOrder.status}`} sx={{ bgcolor: alpha(STATUS_COLOR[activeOrder.status], 0.15), color: STATUS_COLOR[activeOrder.status], fontWeight: 700, alignSelf: "flex-start" }} />
                  </>
                )}
                {reservedToday && (
                  <>
                    <Divider />
                    <Typography sx={{ fontSize: 13 }}><b>Reservation:</b> {reservedToday.partySize} guests, {reservedToday.date}</Typography>
                  </>
                )}
                {matchedCustomer && (
                  <>
                    <Divider />
                    <Typography sx={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.brass, fontWeight: 700 }}>Known regular</Typography>
                    <Typography sx={{ fontSize: 13 }}>Favorite: {matchedCustomer.favorite} · {matchedCustomer.visits} visits · {fmtUSD2(matchedCustomer.totalSpend)} lifetime</Typography>
                  </>
                )}
                <Button fullWidth onClick={()=>setDetailTable(null)} sx={{ mt: 1, color: PALETTE.inkDim }}>Close</Button>
              </Stack>
            );
          })()}
        </DialogContent>
      </Dialog>
    </Stack>
  );
}

function MyTables({ user, tableAssignments, orderQueue, onAdvanceOrder, reservations }) {
  const myTables = tableAssignments.filter((a) => a.servantId === user.id);
  const todayISO = new Date().toISOString().slice(0, 10);
  const myTableIds = myTables.map((a) => a.table);
  const readyToServe = orderQueue.filter((o) => myTableIds.includes(o.table) && o.status === "ready");
  return (
    <Stack spacing={3}>
      <SectionHead eyebrow={`Servant Terminal — ${user.name}`} title="My Tables" />
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: readyToServe.length ? PALETTE.sage : PALETTE.border, bgcolor: readyToServe.length ? alpha(PALETTE.sage, 0.08) : "transparent" }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: readyToServe.length ? 1 : 0 }}>
          <CheckCircle sx={{ fontSize: 18, color: readyToServe.length ? PALETTE.sage : PALETTE.inkDim }} />
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: readyToServe.length ? PALETTE.sage : PALETTE.inkDim }}>
            {readyToServe.length > 0 ? `Ready to serve now (${readyToServe.length})!` : "Nothing ready to pick up right now."}
          </Typography>
        </Stack>
        {readyToServe.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {readyToServe.map((o) => (
              <Chip key={o.id} label={`Table ${o.table} — ${o.items.join(", ")}`} onClick={()=>onAdvanceOrder(o.id)} clickable
                icon={<CheckCircle sx={{ fontSize: 14 }} />}
                sx={{ bgcolor: PALETTE.sage, color: "#fff", fontWeight: 700 }} />
            ))}
          </Stack>
        )}
      </Paper>
      {myTables.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, borderColor: PALETTE.border }}>
          <Typography sx={{ fontSize: 13.5, color: PALETTE.inkDim }}>No tables assigned to you yet — ask the Owner or Admin to assign one from Table Assignments.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2.5} sx={{ width: '100%', margin: 0 }}>
          {myTables.map((a) => {
            const table = TABLES.find((t) => t.id === a.table);
            const activeOrders = orderQueue.filter((o) => o.table === a.table && o.status !== "served");
            const servedOrders = orderQueue.filter((o) => o.table === a.table && o.status === "served").slice(-3);
            const reservedToday = reservations.find((r) => r.table === a.table && r.date === todayISO);
            return (
              <Grid item xs={12} md={6} key={a.table}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: activeOrders.length ? PALETTE.brass : PALETTE.border, height: "100%" }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{a.table}</Typography>
                    <Typography sx={{ fontSize: 12, color: PALETTE.inkDim }}>seats {table?.seats}</Typography>
                  </Stack>
                  {reservedToday && (
                    <Typography sx={{ fontSize: 12, color: PALETTE.brass, mb: 1 }}>Reserved for {reservedToday.name} · {reservedToday.partySize} guests · {reservedToday.phone}</Typography>
                  )}
                  {activeOrders.length === 0 ? (
                    <Typography sx={{ fontSize: 12.5, color: PALETTE.sage, fontWeight: 600 }}>No active orders — table is clear.</Typography>
                  ) : (
                    <Stack spacing={1}>
                      {activeOrders.map((o) => (
                        <Box key={o.id} sx={{ p: 1.2, borderRadius: 2, bgcolor: PALETTE.bg }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Chip size="small" label={o.status} sx={{ bgcolor: alpha(STATUS_COLOR[o.status], 0.15), color: STATUS_COLOR[o.status], textTransform: "capitalize", fontWeight: 700 }} />
                            <Button size="small" onClick={()=>onAdvanceOrder(o.id)} variant="contained" sx={{ bgcolor: PALETTE.garnet, fontSize: 11, "&:hover": { bgcolor: PALETTE.garnetLight } }}>Advance</Button>
                          </Stack>
                          <Typography sx={{ fontSize: 12, color: PALETTE.inkDim, mt: 0.5 }}>{o.items.join(", ")}</Typography>
                          {(o.name || o.phone) && <Typography sx={{ fontSize: 11, color: PALETTE.brass, mt: 0.3 }}>{o.name || "Guest"}{o.phone ? ` · ${o.phone}` : ""}</Typography>}
                        </Box>
                      ))}
                    </Stack>
                  )}
                  {servedOrders.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography sx={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 0.5 }}>Recently served</Typography>
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {servedOrders.map((o) => <Chip key={o.id} size="small" icon={<CheckCircle sx={{ fontSize: 12 }} />} label={o.items.join(", ")} sx={{ bgcolor: alpha(PALETTE.sage,0.12), color: PALETTE.sage, fontSize: 10.5 }} />)}
                      </Stack>
                    </Box>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Stack>
  );
}

function StaffDirectory({ customers }) {
  return (
    <Stack spacing={3}>
      <SectionHead eyebrow="Access Control" title="Staff & Login Directory" />
      <Paper variant="outlined" sx={{ borderRadius: 3, borderColor: PALETTE.border, overflowX: "auto" }}>
        <Table size="small">
          <TableHead><TableRow>{["Name","Role","Title","Phone (login)"].map(h=><TableCell key={h} sx={{ fontSize: 11, textTransform: "uppercase", color: PALETTE.inkDim, fontWeight: 700, border: 0 }}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {[...STAFF, { ...WAITER, title: "Waiter Terminal" }].map(s => (
              <TableRow key={s.id}>
                <TableCell sx={{ borderColor: PALETTE.border, fontSize: 13 }}>{s.name}</TableCell>
                <TableCell sx={{ borderColor: PALETTE.border }}><Chip size="small" label={s.role} sx={{ bgcolor: alpha(PALETTE.brass,0.12), color: PALETTE.brass }} /></TableCell>
                <TableCell sx={{ borderColor: PALETTE.border, fontSize: 13, color: PALETTE.inkDim }}>{s.title}</TableCell>
                <TableCell sx={{ borderColor: PALETTE.border, fontSize: 13, color: PALETTE.garnet, fontFamily: "JetBrains Mono, monospace" }}>{s.phone}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <SectionHead eyebrow="35 Registered Guests" title="Customer Login-Free Directory" />
      <Paper variant="outlined" sx={{ borderRadius: 3, borderColor: PALETTE.border, overflowX: "auto" }}>
        <Table size="small">
         <TableHead><TableRow>{["Name","Phone","Visits","Tier","Favorite","Lifetime spend"].map(h=><TableCell key={h} sx={{ fontSize: 11, textTransform: "uppercase", color: PALETTE.inkDim, fontWeight: 700, border: 0 }}>{h}</TableCell>)}</TableRow></TableHead>
<TableBody>
  {customers.map(c => (
    <TableRow key={c.id}>
      <TableCell sx={{ borderColor: PALETTE.border, fontSize: 13 }}>{c.name}</TableCell>
      <TableCell sx={{ borderColor: PALETTE.border, fontSize: 13, color: PALETTE.inkDim, fontFamily: "JetBrains Mono, monospace" }}>{c.phone}</TableCell>
      <TableCell sx={{ borderColor: PALETTE.border, fontSize: 13 }}>{c.visits}</TableCell>
      <TableCell sx={{ borderColor: PALETTE.border }}>
        <Chip size="small" label={loyaltyTier(c.totalSpend).label} sx={{ height: 20, fontSize: 10, bgcolor: alpha(loyaltyTier(c.totalSpend).color,0.15), color: loyaltyTier(c.totalSpend).color, fontWeight: 700 }} />
      </TableCell>
      <TableCell sx={{ borderColor: PALETTE.border, fontSize: 13, color: PALETTE.garnet }}>{c.favorite}</TableCell>
      <TableCell sx={{ borderColor: PALETTE.border, fontSize: 13, color: PALETTE.sage, fontFamily: "JetBrains Mono, monospace" }}>{fmtUSD2(c.totalSpend)}</TableCell>
    </TableRow>
  ))}
</TableBody>
        </Table>
      </Paper>
    </Stack>
  );
}

/* ============================ KITCHEN QUEUE ============================ */

function OrderQueueView({ queue, onAdvanceOrder, title = "Live Order Queue", eyebrow = "Kitchen Terminal" }) {
  const advance = (id) => onAdvanceOrder(id);
  const active = queue.filter((o) => o.status !== "served");
  const withUrgencyList = active;
  const overdueList = withUrgencyList.filter((o) => o.urgency === "overdue").sort((a,b) => a.remaining - b.remaining);
  const hurryList = withUrgencyList.filter((o) => o.urgency === "hurry").sort((a,b) => a.remaining - b.remaining);
  const recentlyCompleted = queue.filter((o) => o.status === "served").slice(-5).reverse();

  return (
    <Stack spacing={3}>
      <Box sx={{display:'flex',alignItems:'start',justifyContent:'space-between'}}>
      <SectionHead eyebrow={eyebrow} title={title} />
      <Typography sx={{ fontSize: 11.5, color: PALETTE.inkDim }}>Target: out within {KITCHEN_SLA_MINUTES} min</Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: PALETTE.border }}>
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 700, mb: 1.5 }}>Urgency alerts</Typography>
        <Stack spacing={1.5}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
              <WarningAmber sx={{ fontSize: 16, color: overdueList.length ? PALETTE.alert : PALETTE.inkDim }} />
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: overdueList.length ? PALETTE.ink : PALETTE.inkDim }}>Overdue orders ({overdueList.length})</Typography>
            </Stack>
            {overdueList.length > 0 ? (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {overdueList.map((o) => (
                  <Chip key={o.id} size="small" label={`Table ${o.table} — ${Math.abs(o.remaining)}m over`} sx={{ bgcolor: alpha(PALETTE.alert, 0.15), color: PALETTE.alert, fontWeight: 700 }} />
                ))}
              </Stack>
            ) : <Typography sx={{ fontSize: 12, color: PALETTE.inkDim, pl: 3 }}>Nothing overdue.</Typography>}
          </Box>
          <Divider />
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
              <AccessTime sx={{ fontSize: 16, color: hurryList.length ? PALETTE.brass : PALETTE.inkDim }} />
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: hurryList.length ? PALETTE.ink : PALETTE.inkDim }}>Hurry — due within 5 minutes ({hurryList.length})</Typography>
            </Stack>
            {hurryList.length > 0 ? (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {hurryList.map((o) => (
                  <Chip key={o.id} size="small" label={`Table ${o.table} — ${o.remaining}m left`} sx={{ bgcolor: alpha(PALETTE.brass, 0.18), color: PALETTE.brass, fontWeight: 700 }} />
                ))}
              </Stack>
            ) : <Typography sx={{ fontSize: 12, color: PALETTE.inkDim, pl: 3 }}>Nothing due imminently.</Typography>}
          </Box>
        </Stack>
      </Paper>

      <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
        {withUrgencyList.map((o) => (
          <Grid item xs={12} sm={6} md={3} key={o.id}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: o.urgency === "normal" ? PALETTE.border : URGENCY_COLOR[o.urgency], borderWidth: o.urgency === "normal" ? 1 : 2, height: "100%" }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{o.orderType === "online" ? "Online Order" : `Table ${o.table}`}</Typography>
                <Chip size="small" label={o.status} sx={{ bgcolor: alpha(STATUS_COLOR[o.status], 0.15), color: STATUS_COLOR[o.status], textTransform: "capitalize", fontSize: 10 }} />
              </Stack>
              {(o.name || o.phone) && <Typography sx={{ fontSize: 11, color: PALETTE.brass, mb: 1 }}>{o.name || "Guest"}{o.phone ? ` · ${o.phone}` : ""}</Typography>}
              <Stack spacing={0.2} sx={{ mb: 1.5 }}>{o.items.map((it,i) => <Typography key={i} sx={{ fontSize: 12, color: PALETTE.inkDim }}>• {it}</Typography>)}</Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={0.4} alignItems="center">
                  <AccessTime sx={{ fontSize: 13, color: URGENCY_COLOR[o.urgency] }} />
                  <Typography sx={{ fontSize: 11.5, color: URGENCY_COLOR[o.urgency], fontWeight: o.urgency !== "normal" ? 700 : 400 }}>
                    {o.urgency === "overdue" ? `Overdue ${Math.abs(o.remaining)}m` : o.urgency === "hurry" ? `${o.remaining}m left` : `${o.minAgo}m ago`}
                  </Typography>
                </Stack>
                <Button size="small" onClick={()=>advance(o.id)} variant="contained" sx={{ bgcolor: PALETTE.garnet, "&:hover": { bgcolor: PALETTE.garnetLight }, fontSize: 11.5 }}>Advance</Button>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>
      {withUrgencyList.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, borderColor: PALETTE.border, textAlign: "center" }}>
          <Typography sx={{ fontSize: 13, color: PALETTE.inkDim }}>No active orders right now.</Typography>
        </Paper>
      )}
      {recentlyCompleted.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: PALETTE.border }}>
          <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 1 }}>Recently completed — earlier today</Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {recentlyCompleted.map((o) => (
              <Chip key={o.id} size="small" icon={<CheckCircle sx={{ fontSize: 12 }} />} label={`Table ${o.table} — ${o.items.length} item(s)`} sx={{ bgcolor: alpha(PALETTE.sage, 0.12), color: PALETTE.sage }} />
            ))}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

function KitchenQueue({ menu, queue, onAdvanceOrder, inventory, ledger, wastageLog, barcodes, onLogMovement, onLogWastage, onCreateBarcode, onScanBarcode, onSetExpiryDate }) {
  return (
    <Stack spacing={3}>
      <OrderQueueView queue={queue} onAdvanceOrder={onAdvanceOrder} />
      <InventoryHub items={inventory} ledger={ledger} wastageLog={wastageLog} menu={menu} barcodes={barcodes} onLogMovement={onLogMovement} onLogWastage={onLogWastage} onCreateBarcode={onCreateBarcode} onScanBarcode={onScanBarcode} onSetExpiryDate={onSetExpiryDate} />
    </Stack>
  );
}

/* =============================== ORDER TAKING (Waiter) =============================== */

function OrderTaking({ menu, customers, queue, onSendOrder, reservations, onBookTable }) {
  const [cart, setCart] = useState([]);
  const [placed, setPlaced] = useState(false);
  const [tableId, setTableId] = useState("");
  const [myOrderIds, setMyOrderIds] = useState([]);
  const [sending, setSending] = useState(false);
  const add = (m) => setCart((c) => [...c, m]);
  const remove = (idx) => setCart((c) => c.filter((_,i)=>i!==idx));
  const total = cart.reduce((s,m)=>s+m.price,0);

  useEffect(() => {
    if (myOrderIds.length === 0 && queue.length > 0) {
      setMyOrderIds(queue.slice(-5).map((o) => o.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue.length]);

  const sendToKitchen = async () => {
    if (cart.length === 0 || !tableId) return;
    setSending(true);
    try {
      const order = await onSendOrder({ table: tableId, items: cart.map((m) => m.name) });
      setMyOrderIds((ids) => [order.id, ...ids]);
      setPlaced(true);
      setCart([]);
    } finally {
      setSending(false);
    }
  };

  const myOrders = queue.filter((o) => myOrderIds.includes(o.id));

  return (
    <Stack spacing={3}>
      <SectionHead eyebrow="Waiter Terminal" title="Take New Order" />
      <Grid container spacing={2.5} sx={{ width: '100%', margin: 0 }}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border, height: "100%" }}>
            <Grid container spacing={1.5} sx={{ maxHeight: 440, overflowY: "auto", m: 0, width: '100%' }}>
              {menu.map((m) => (
                <Grid item xs={12} sm={6} key={m.id}>
                  <Stack direction="row" spacing={1.5} alignItems="center" onClick={()=>add(m)}
                    sx={{ p: 1, borderRadius: 2, border: `1px solid ${PALETTE.border}`, cursor: "pointer", "&:hover": { borderColor: PALETTE.brass, bgcolor: alpha(PALETTE.brass,0.05) } }}>
                    <SmartImg src={m.img?.[0]} fallback={m.imgLocal?.[0]} alt="" sx={{ width: 42, height: 42, borderRadius: 2, objectFit: "cover" }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{m.name}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: PALETTE.inkDim }}>{m.cat}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 13.5, color: PALETTE.garnet, fontWeight: 600, fontFamily: "JetBrains Mono, monospace" }}>{fmtUSD2(m.price)}</Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
        <Grid item sx={{width:'100%',display:'flex',justifyContent:'space-between'}} xs={12} md={4}>
          <Paper variant="outlined" sx={{ width:'20%',p: 2.5, borderRadius: 3, borderColor: PALETTE.border, height: "100%" }}>
            <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 1 }}>Current order</Typography>
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }} required error={placed === false && cart.length > 0 && !tableId}>
              <Select displayEmpty value={tableId} onChange={(e) => setTableId(e.target.value)}
                renderValue={(v) => v ? `Table ${v}` : "Select table number…"}>
                {TABLES.map((t) => {
                  const todayISO = new Date().toISOString().slice(0, 10);
                  const reservedBy = reservations.find((r) => r.table === t.id && r.date === todayISO);
                  return <MenuItem key={t.id} value={t.id}>{t.id} (seats {t.seats}){reservedBy ? ` — reserved: ${reservedBy.name}` : ""}</MenuItem>;
                })}
              </Select>
            </FormControl>
            <Stack spacing={0.6} sx={{ maxHeight: 260, overflowY: "auto" }}>
              {cart.length === 0 && <Typography sx={{ fontSize: 12.5, color: PALETTE.inkDim }}>No items yet — tap a menu item to add.</Typography>}
              {cart.map((m, i) => (
                <Stack key={i} direction="row" justifyContent="space-between" alignItems="center" sx={{ fontSize: 12.5, px: 1, py: 0.8, borderRadius: 2, bgcolor: PALETTE.bg }}>
                  <Typography sx={{ fontSize: 12.5 }}>{m.name}</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontSize: 12.5, color: PALETTE.garnet }}>{fmtUSD2(m.price)}</Typography>
                    <IconButton size="small" onClick={()=>remove(i)}><Close sx={{ fontSize: 13 }} /></IconButton>
                  </Stack>
                </Stack>
              ))}
            </Stack>
            <GoldRule />
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: 13, color: PALETTE.inkDim }}>Total</Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{fmtUSD2(total)}</Typography>
            </Stack>
            <Button fullWidth disabled={cart.length===0 || !tableId || sending} onClick={sendToKitchen} variant="contained"
              sx={{ bgcolor: (cart.length && tableId) ? PALETTE.garnet : PALETTE.border, "&:hover": { bgcolor: PALETTE.garnetLight } }}>{sending ? "Sending…" : "Send to kitchen"}</Button>
            {cart.length > 0 && !tableId && <Typography sx={{ fontSize: 11.5, color: PALETTE.alert, mt: 0.75 }}>Select a table number before sending the order.</Typography>}
            {placed && <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1, color: PALETTE.sage }}><CheckCircle sx={{ fontSize: 14 }} /><Typography sx={{ fontSize: 12 }}>Order sent to kitchen queue.</Typography></Stack>}
          </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, borderColor: PALETTE.border,width:'75%' }}>
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: PALETTE.inkDim, fontWeight: 600, mb: 0.5 }}>Your sent orders — live status</Typography>
        <Typography sx={{ fontSize: 11.5, color: PALETTE.inkDim, mb: 1.5 }}>Example orders shown below span earlier today (completed), right now (in progress), and just placed — new orders you send join this same list.</Typography>
        {myOrders.length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: PALETTE.inkDim }}>Nothing sent yet this session — orders you send appear here with live kitchen status.</Typography>
        ) : (
          <Stack spacing={1}>
            {myOrders.map((o) => (
              <Stack key={o.id} direction="row" alignItems="center" spacing={1.5} sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: PALETTE.bg, border: o.urgency !== "normal" && o.status !== "served" ? `1px solid ${URGENCY_COLOR[o.urgency]}` : "none" }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, minWidth: 90 }}>{o.orderType === "online" ? "Online Order" : `Table ${o.table}`}</Typography>
                <Typography sx={{ fontSize: 12, color: PALETTE.inkDim, flexGrow: 1 }}>{o.items.join(", ")}</Typography>
                <Chip size="small" label={o.status} sx={{ bgcolor: alpha(STATUS_COLOR[o.status], 0.15), color: STATUS_COLOR[o.status], textTransform: "capitalize", fontSize: 10 }} />
                {o.status !== "served" && (
                  <Chip size="small" icon={<AccessTime sx={{ fontSize: 12 }} />}
                    label={o.urgency === "overdue" ? `Overdue ${Math.abs(o.remaining)}m` : o.urgency === "hurry" ? `${o.remaining}m left` : `${o.minAgo}m ago`}
                    sx={{ bgcolor: alpha(URGENCY_COLOR[o.urgency], 0.15), color: URGENCY_COLOR[o.urgency], fontWeight: o.urgency !== "normal" ? 700 : 500, fontSize: 10 }} />
                )}
                {o.status === "served" && <Chip size="small" icon={<CheckCircle sx={{ fontSize: 12 }} />} label="Completed" sx={{ bgcolor: alpha(PALETTE.sage,0.15), color: PALETTE.sage, fontSize: 10, fontWeight: 700 }} />}
              </Stack>
            ))}
          </Stack>
        )}
      </Paper>
        </Grid>
      </Grid>


      <CustomerLookup customers={customers} />
      <ReservationAgent reservations={reservations} onBookTable={onBookTable} />
    </Stack>
  );
}

/* =================================== APP SHELL =================================== */

function DashboardTopBar({ user,onStaffLogin, onLogout, onViewShop, tabs, active, setActive, serverOnline }) {
  return (
    <AppBar position="sticky" elevation={0} sx={{ bgcolor: alpha(PALETTE.bg, 0.92), backdropFilter: "blur(10px)", borderBottom: `1px solid ${PALETTE.border}`, color: PALETTE.ink }}>
      <Toolbar sx={{ gap: 1 }}>
        <LocalFireDepartment sx={{ color: PALETTE.garnet }} />
        <Typography sx={{ fontFamily: "Fraunces, serif", fontWeight: 700, fontSize: 18 }}>Lindey's</Typography>
        <Chip size="small" label={user.role} sx={{ bgcolor: alpha(PALETTE.brass,0.15), color: PALETTE.brass, ml: 1 }} />
        <Box sx={{ flexGrow: 1 }} />
        <Tabs value={active} onChange={(e,v)=>setActive(v)} variant="scrollable" scrollButtons="auto"
          TabIndicatorProps={{ style: { backgroundColor: PALETTE.garnet, borderRadius: 3, height: 3 } }}
          sx={{ display: { xs: "none", md: "flex" }, minHeight: 0, width:'1200px' }}>
          {tabs.map((t) => <Tab key={t.key} value={t.key} icon={<t.icon sx={{ fontSize: 16 }} />} iconPosition="start" label={t.label}
            sx={{ textTransform: "none", fontWeight: 600, fontSize: 13, minHeight: 0, color: active===t.key?PALETTE.garnet:PALETTE.inkDim }} />)}
        </Tabs>
        <Box sx={{ flexGrow: 1, display: { md: "none" } }} />
        <Chip size="small" label={serverOnline ? "Live" : "Offline"}

          sx={{ mr: 1, fontSize: 11, fontWeight: 700, bgcolor: alpha(serverOnline ? PALETTE.sage : PALETTE.alert, 0.12), color: serverOnline ? PALETTE.sage : PALETTE.alert }} />
         <MTooltip title="Staff Sign In">
            <IconButton onClick={onStaffLogin} sx={{ background:'#c3baba',border: `1px solid ${PALETTE.border}`, color: PALETTE.ink }}>
              <AdminPanelSettings sx={{ fontSize: 20 }} />
            </IconButton>
          </MTooltip>
        <IconButton onClick={onViewShop} title="View public shop page" sx={{ color: PALETTE.inkDim }}><StorefrontIcon sx={{ fontSize: 19 }} /></IconButton>
        <IconButton onClick={onLogout} sx={{ color: PALETTE.inkDim }}><Logout sx={{ fontSize: 18 }} /></IconButton>
      </Toolbar>
      <Tabs value={active} onChange={(e,v)=>setActive(v)} variant="scrollable" scrollButtons="auto"
        TabIndicatorProps={{ style: { backgroundColor: PALETTE.garnet } }}
        sx={{ display: { xs: "flex", md: "none" }, minHeight: 0, px: 1, pb: 1 }}>
        {tabs.map((t) => <Tab key={t.key} value={t.key} icon={<t.icon sx={{ fontSize: 15 }} />} iconPosition="start" label={t.label}
          sx={{ textTransform: "none", fontWeight: 600, fontSize: 12, minHeight: 0, color: active===t.key?PALETTE.garnet:PALETTE.inkDim }} />)}
      </Tabs>
    </AppBar>
  );
}

const TABS_BY_ROLE = {
  Owner: [
    { key: "hub", label: "Unified Hub", icon: BarChartIcon },
    { key: "sales", label: "Sales", icon: ShowChart },
    { key: "loyalty", label: "Loyalty ", icon: Star },
    { key: "orders", label: "Orders", icon: SoupKitchen },
    { key: "inventory", label: "Inventory", icon: Inventory2 },
    { key: "profit", label: "Profit Intel", icon: AttachMoney },
    { key: "owner", label: "Shop Setup", icon: Settings },
    { key: "tables", label: "Table Assignments", icon: Groups },
    { key: "customers", label: "Customer AI", icon: Groups },
    { key: "reservations", label: "Reservations", icon: CalendarMonth },
    { key: "reports", label: "Reports", icon: Download },
    { key: "staff", label: "Directory", icon: AdminPanelSettings },
  ],
  Admin: [
    { key: "hub", label: "Unified Hub", icon: BarChartIcon },
    { key: "sales", label: "Sales", icon: ShowChart },
    { key: "loyalty", label: "Loyalty ", icon: Star },
    { key: "orders", label: "Orders", icon: SoupKitchen },
    { key: "inventory", label: "Inventory", icon: Inventory2 },
    { key: "profit", label: "Profit Intel", icon: AttachMoney },
    { key: "tables", label: "Table Assignments", icon: Groups },
    { key: "customers", label: "Customer AI", icon: Groups },
    { key: "reservations", label: "Reservations", icon: CalendarMonth },
    { key: "reports", label: "Reports", icon: Download },
    { key: "staff", label: "Directory", icon: AdminPanelSettings },
  ],
  Kitchen: [
    { key: "kitchen", label: "Order Queue", icon: SoupKitchen },
    { key: "inventory", label: "Inventory", icon: Inventory2 },
  ],
  User: [
    { key: "order", label: "Take Order", icon: RestaurantMenu },
    { key: "customers", label: "Customer AI", icon: Groups },
    { key: "reservations", label: "Reservations", icon: CalendarMonth },
  ],
  Servant: [
    { key: "myTables", label: "My Tables", icon: Groups },
  ],
};

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error("App crashed:", error, info); }
  render() {
    if (this.state.error) {
      return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#FAF8F4", p: 4, fontFamily: "monospace" }}>
          <Typography variant="h5" sx={{ color: "#C1440E", fontWeight: 700, mb: 2 }}>Something crashed while rendering.</Typography>
          <Typography sx={{ fontSize: 13, color: "#241F1A", mb: 1 }}>{String(this.state.error?.message || this.state.error)}</Typography>
          <Box component="pre" sx={{ fontSize: 11, color: "#756E63", whiteSpace: "pre-wrap", bgcolor: "#fff", p: 2, borderRadius: 2, border: "1px solid #E9E2D6" }}>
            {this.state.error?.stack}
          </Box>
          <Button variant="contained" sx={{ mt: 2, bgcolor: "#7E1F31" }} onClick={() => window.location.reload()}>Reload</Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

function downloadCSV(filename, columns, rows) {
  const escape = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = columns.map((c) => escape(c.label)).join(",");
  const lines = rows.map((r) => columns.map((c) => escape(typeof c.value === "function" ? c.value(r) : r[c.key])).join(","));
  const csv = [header, ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function DetailsDialog({ open, onClose, title, subtitle, columns, rows, onRowClick }) {
  const [drillDown, setDrillDown] = useState(null);
  const handleRowClick = (row) => {
    if (!onRowClick) return;
    const detail = onRowClick(row);
    if (detail) setDrillDown(detail);
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 4, maxHeight: "85vh" } }}>
      <DialogContent>
        <Stack  direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ display:'flex',justifyContent:'space-between',mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
            {subtitle && <Typography sx={{ fontSize: 12.5, color: PALETTE.inkDim, mt: 0.3 }}>{subtitle}</Typography>}
            <Typography sx={{ fontSize: 11.5, color: PALETTE.inkDim, mt: 0.3 }}>{rows.length} row{rows.length === 1 ? "" : "s"}{onRowClick ? " — click a row for its own detail" : ""}</Typography>
          </Box>
          <Box direction="row" sx={{gap:5}} spacing={1}>
            <Button size="small"  startIcon={<Download sx={{ fontSize: 15 }} />} onClick={() => downloadCSV(title, columns, rows)}
              sx={{ mr:2,bgcolor: PALETTE.garnet, color: "#fff", "&:hover": { bgcolor: PALETTE.garnetLight } }} variant="contained">Export CSV</Button>
            <IconButton size="small" onClick={onClose}><Close sx={{ fontSize: 18 }} /></IconButton>
          </Box>
        </Stack>
        <Box sx={{ maxHeight: "60vh", overflow: "auto", border: `1px solid ${PALETTE.border}`, borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>{columns.map((c) => <TableCell key={c.key} sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: PALETTE.inkDim, fontWeight: 700, bgcolor: PALETTE.bg }}>{c.label}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={columns.length} sx={{ textAlign: "center", color: PALETTE.inkDim, fontSize: 13, py: 4 }}>No data yet.</TableCell></TableRow>
              ) : rows.map((r, i) => (
                <TableRow key={i} hover onClick={onRowClick ? () => handleRowClick(r) : undefined} sx={onRowClick ? { cursor: "pointer" } : {}}>
                  {columns.map((c) => <TableCell key={c.key} sx={{ fontSize: 12.5 }}>{typeof c.value === "function" ? c.value(r) : r[c.key]}</TableCell>)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>
      {drillDown && (
        <DetailsDialog open={Boolean(drillDown)} onClose={()=>setDrillDown(null)} title={drillDown.title} subtitle={drillDown.subtitle} columns={drillDown.columns} rows={drillDown.rows} />
      )}
    </Dialog>
  );
}

function DetailsTrigger({ title, subtitle, columns, rows, onRowClick, children }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Box onClick={() => setOpen(true)} sx={{ cursor: "pointer", borderRadius: 3, transition: "box-shadow .15s", "&:hover": { boxShadow: "0 4px 18px rgba(0,0,0,0.08)" } }} title="Click for full details & export">
        {children}
      </Box>
      <DetailsDialog open={open} onClose={() => setOpen(false)} title={title} subtitle={subtitle} columns={columns} rows={rows} onRowClick={onRowClick} />
    </>
  );
}

function ScrollNav() {
  const [scrollable, setScrollable] = useState(false);
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(false);
  useEffect(() => {
    const check = () => {
      const scrollTop = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      setScrollable(maxScroll > 60);
      setAtTop(scrollTop < 40);
      setAtBottom(scrollTop > maxScroll - 40);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    const t = setInterval(check, 500);
    return () => { window.removeEventListener("scroll", check); window.removeEventListener("resize", check); clearInterval(t); };
  }, []);

  if (!scrollable) return null;
  const goDown = () => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
  const goUp = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <Stack spacing={1} sx={{ position: "fixed", bottom: 90, right: 24, zIndex: 1250 }}>
      <Fab size="small" onClick={goUp} disabled={atTop} title="Scroll to top"
        sx={{ bgcolor: PALETTE.ink, color: "#fff", boxShadow: "0 6px 20px rgba(0,0,0,0.25)", opacity: atTop ? 0.35 : 1, "&:hover": { bgcolor: PALETTE.garnet } }}>
        <ChevronRight sx={{ transform: "rotate(-90deg)" }} />
      </Fab>
      <Fab size="small" onClick={goDown} disabled={atBottom} title="Scroll to bottom"
        sx={{ bgcolor: PALETTE.ink, color: "#fff", boxShadow: "0 6px 20px rgba(0,0,0,0.25)", opacity: atBottom ? 0.35 : 1, "&:hover": { bgcolor: PALETTE.garnet } }}>
        <ChevronRight sx={{ transform: "rotate(90deg)" }} />
      </Fab>
    </Stack>
  );
}

export default function AppRoot() {
  return (
    <ErrorBoundary>
      <App />
      <ScrollNav />
    </ErrorBoundary>
  );
}

function App() {
  const [view, setViewRaw] = useState("store");
  const setView = (v) => { setViewRaw(v); };
  const [user, setUser] = useState(null);
  const [active, setActiveRaw] = useState("hub");
  const setActive = (v) => { setActiveRaw(v); };
  const [serverOnline, setServerOnline] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);

  const [shopInfo, setShopInfo] = useState(appData.shopInfo);
  const [banners, setBanners] = useState(appData.banners);
  const [aboutCards, setAboutCards] = useState(appData.aboutCards);
  const [reservations, setReservations] = useState(appData.reservations || []);
  const [tableAssignments, setTableAssignments] = useState(appData.tableAssignments || []);
  const [menu, setMenu] = useState(() => appData.menu.map((m) => ({ ...m, price: +(m.cost * (1 + m.margin / 100)).toFixed(2) })));
  const [inventory, setInventory] = useState(appData.inventory);

  const [ledger, setLedger] = useState(STOCK_LEDGER_SEED);
  const [wastageLog, setWastageLog] = useState(WASTAGE_SEED);
  const [orderQueue, setOrderQueue] = useState([]);
  const [barcodes, setBarcodes] = useState(BARCODE_SEED);
  const [reserveOpen, setReserveOpen] = useState(false);
  const customers = useMemo(() => genCustomers(35, menu), [menu]);
   const [assignments, setAssignments] = useState({});
  // const [comboSuggestions, setComboSuggestions] = useState([]);
  // ... inside App component, after const customers = useMemo(...)

const [comboSuggestions, setComboSuggestions] = useState(() => {
  // Generate 11 dummy suggestions using the first 11 customers
  return customers.slice(0, 11).map((c, idx) => ({
    id: `dummy-${idx}`,
    text: `Combo ${idx + 1}: ${c.favorite} + Truffle Fries + Old Fashioned`,
     name: c.name,
    phone: c.phone,
    date: new Date(Date.now() - idx * 86400000 * 2).toISOString().slice(0, 10),
    // we store the customer id to later retrieve full details
    customerId: c.id,
  }));
});
const [loyaltyRules, setLoyaltyRules] = useState(DEFAULT_LOYALTY_RULES);
  const [savedOffers, setSavedOffers] = useState([]);
  const [activeOffers, setActiveOffers] = useState([]);
  
  const saveOffer = (offer) => setSavedOffers((s) => [offer, ...s]);
const applyOffer = (offer) => {
  setActiveOffers((prev) => {
    if (prev.some((o) => o.id === offer.id)) return prev; // already active
    return [...prev, {
      id: offer.id,
      name: offer.name,
      blocks: offer.blocks.map((b) => ({ ...b })), // each block keeps its own editable value
    }];
  });
};

const updateActiveOfferBlockValue = (offerId, blockId, newValue) => {
  setActiveOffers((prev) => prev.map((o) => o.id !== offerId ? o : {
    ...o, blocks: o.blocks.map((b) => b.id !== blockId ? b : { ...b, value: newValue }),
  }));
};

const removeActiveOffer = (offerId) => setActiveOffers((prev) => prev.filter((o) => o.id !== offerId));

const deleteOffer = (id) => setSavedOffers((s) => s.filter((o) => o.id !== id));
  // NEW — combo suggestions, shared between the storefront submission boxes and the Sales tab card
  const seededSuggestionsRef = useRef(false);
  useEffect(() => {
    if (seededSuggestionsRef.current || customers.length === 0) return;
    seededSuggestionsRef.current = true;
    const seedFrom = (idx, text, daysAgo) => {
      const c = customers[idx];
      return { id: `seed-${idx}`, text, name: c?.name || "", phone: c?.phone || "", date: daysAgoLabel(daysAgo) };
    };
    setComboSuggestions([
      seedFrom(2, "Ribeye + Old Fashioned + Cheesecake", 9),
      seedFrom(7, "Filet Mignon + Truffle Mash + Crème Brûlée for two", 6),
      seedFrom(15, "Wagyu Sliders + Espresso Martini happy-hour combo", 4),
      { id: "seed-guest-1", text: "Kids combo: mini burger + fries + milkshake", name: "", phone: "612-555-9999", date: daysAgoLabel(2) },
      { id: "seed-guest-2", text: "Vegetarian tasting combo with three sides", name: "", phone: "", date: daysAgoLabel(1) },
    ]);
    apiGet("/combo-suggestions").then((data) => {
      if (Array.isArray(data) && data.length) {
        setComboSuggestions((prev) => [...data.map((d, i) => ({ id: `srv-${i}`, text: d.text, phone: d.phone || "", name: "", date: "recently" })), ...prev]);
      }
    }).catch(() => { /* server route optional */ });
  }, [customers]);
  const onSuggestCombo = (entry) => {
    const matched = customers.find((c) => entry.phone && c.phone.replace(/\D/g,"").includes(entry.phone.replace(/\D/g,"")));
    setComboSuggestions((s) => [{ ...entry, name: matched?.name || "", date: new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"}) }, ...s]);
  };
  // NEW: derive the customer-facing menu (capped at 16/category) and permanent combos
  const { shownMenu, combos } = useMemo(() => buildComboOverflow(menu), [menu]);
  useEffect(() => {
    (async () => {
      try {
        const suggestions = await apiGet("/combo-suggestions");
        if (Array.isArray(suggestions)) setComboSuggestions(suggestions);
      } catch {
        // Route may not exist on the server yet — fine, the tile just shows an empty state.
      }
    })();
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const d = await apiGet("/data");
        setShopInfo(d.shopInfo);
        setBanners(d.banners);
        setAboutCards(d.aboutCards);
        setMenu(d.menu.map((m) => ({ ...m, price: +(m.cost * (1 + m.margin / 100)).toFixed(2) })));
        setInventory(d.inventory);
        setOrderQueue((d.orders || []).map(withUrgency));
        if (d.ledger?.length) setLedger(d.ledger);
        if (d.wastageLog?.length) setWastageLog(d.wastageLog);
        setReservations(d.reservations || []);
        setTableAssignments(d.tableAssignments || []);
        setServerOnline(true);
      } catch (err) {
        console.warn("data-server unreachable — showing the bundled data.json instead. Edits won't be saved anywhere until the server is reachable. Check DATA_SERVER_URL near the top of this file.", err);
        setServerOnline(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!serverOnline) return;
    const t = setInterval(async () => {
      try {
        const { orders } = await apiGet("/orders");
        setOrderQueue(orders.map(withUrgency));
      } catch { /* transient network hiccup — try again next tick */ }
    }, 6000);
    return () => clearInterval(t);
  }, [serverOnline]);

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet"; link.href = FONT_URL;
    document.head.appendChild(link);
    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js";
    script.async = true;
    document.body.appendChild(script);
    return () => { document.head.removeChild(link); document.body.removeChild(script); };
  }, []);

  const handleLogin = (u) => { setUser(u); setActive(TABS_BY_ROLE[u.role][0].key); setView("dashboard"); };
  const handleLogout = () => { setUser(null); setView("store"); };

  const flashSaved = () => { setSavedFlash(true); setSaveError(""); setTimeout(() => setSavedFlash(false), 2000); };

  const onUpdateShopInfo = async (partial) => {
    const next = { ...shopInfo, ...partial };
    setShopInfo(next);
    try { await apiPost("/save/shop-info", partial); setSaveError(""); setServerOnline(true); }
    catch (e) { setSaveError(e.message); setServerOnline(false); }
  };

  const onSaveBanners = async (draftBanners) => {
    setBanners(draftBanners);
    await apiPost("/save/banners", { banners: draftBanners.map((b) => ({ img: b.img, title: b.title, subtitle: b.subtitle })) });
    flashSaved();
  };

  const onSaveAboutCards = async (draftCards) => {
    setAboutCards(draftCards);
    await apiPost("/save/about-cards", { aboutCards: draftCards.map((c) => ({ img: c.img, title: c.title, desc: c.desc })) });
    flashSaved();
  };

  const onAddProduct = async ({ name, cat, desc, cost, margin, materialCost, labourCost, burdenCost, imgUrl, isSpecial }) => {
    const catPool = IMAGE_POOL_BY_CAT[cat] || [];
    const startIdx = imgUrl ? 0 : (name.length * 3) % Math.max(1, catPool.length - 3);
    const img = imgUrl ? [imgUrl, imgUrl, imgUrl] : catPool.slice(startIdx, startIdx + 3);
    const price = +(cost * (1 + margin / 100)).toFixed(2);
    const tempId = `temp-${Date.now()}`;
    setMenu((m) => [...m, { id: tempId, name, cat, desc, cost, margin, materialCost, labourCost, burdenCost, isSpecial, price, img }]);
    try {
      const res = await apiPost("/save/add-product", { name, cat, desc, cost, margin, materialCost, labourCost, burdenCost, img, isSpecial });
      setMenu((m) => m.map((it) => it.id === tempId ? { ...res.item, price } : it));
      flashSaved();
    } catch (e) {
      setMenu((m) => m.filter((it) => it.id !== tempId));
      setSaveError(e.message || String(e));
      throw e;
    }
  };

  const onUpdateProduct = async (id, fields) => {
    const previous = menu;
    const current = menu.find((m) => m.id === id);
    const merged = { ...current, ...fields };
    merged.price = +(merged.cost * (1 + merged.margin / 100)).toFixed(2);
    setMenu((m) => m.map((item) => item.id === id ? merged : item));
    try {
      await apiPost("/save/product", { id, name: merged.name, cat: merged.cat, desc: merged.desc, cost: merged.cost, margin: merged.margin, materialCost: merged.materialCost, labourCost: merged.labourCost, burdenCost: merged.burdenCost, img: merged.img, isSpecial: merged.isSpecial });
      flashSaved();
    } catch (e) {
      setMenu(previous);
      setSaveError(e.message || String(e));
      throw e;
    }
  };

  const onDeleteProduct = async (id) => {
    const previous = menu;
    setMenu((m) => m.filter((item) => item.id !== id));
    try {
      await apiPost("/save/delete-product", { id });
      flashSaved();
    } catch (e) {
      setMenu(previous);
      setSaveError(e.message || String(e));
      throw e;
    }
  };

  const onLogMovement = async ({ itemId, type, qty, note, date }) => {
    const item = inventory.find((i) => i.id === itemId);
    const newQty = Math.max(0, (item?.qty || 0) + (type === "received" ? qty : -qty));
    const dateFields = (date && type === "received") ? { receivedDate: date } : {};
    setInventory((inv) => inv.map((i) => i.id === itemId ? { ...i, qty: newQty, ...dateFields } : i));
    try {
      const res = await apiPost("/save/stock-movement", { itemId, itemName: item?.name, type, qty, note });
      setLedger((l) => [res.entry, ...l]);
      if (Object.keys(dateFields).length) await apiPost("/save/inventory", { id: itemId, qty: newQty, ...dateFields });
      setServerOnline(true);
    } catch (e) {
      setSaveError(e.message || String(e));
      setServerOnline(false);
    }
  };

  const onSetExpiryDate = async (itemId, outDate) => {
    setInventory((inv) => inv.map((i) => i.id === itemId ? { ...i, outDate } : i));
    try {
      const item = inventory.find((i) => i.id === itemId);
      await apiPost("/save/inventory", { id: itemId, qty: item?.qty ?? 0, outDate });
      setServerOnline(true);
    } catch (e) {
      setSaveError(e.message || String(e));
      setServerOnline(false);
    }
  };

  const onLogWastage = async ({ itemName, unitCost, prepared, sold }) => {
    const wastedUnits = Math.max(0, prepared - sold);
    const now = new Date();
    const optimistic = { id: `local-${Date.now()}`, item: itemName, prepared, sold, wastedUnits, cost: +(wastedUnits * unitCost).toFixed(1), date: now.toLocaleDateString("en-US",{month:"short",day:"numeric"}) };
    setWastageLog((w) => [optimistic, ...w]);
    try {
      const res = await apiPost("/save/wastage", { itemName, unitCost, prepared, sold });
      setWastageLog((w) => w.map((e) => e.id === optimistic.id ? res.entry : e));
      setServerOnline(true);
    } catch (e) {
      setSaveError(e.message || String(e));
      setServerOnline(false);
    }
  };

  const onCreateBarcode = async ({ itemId, qty }) => {
    const item = inventory.find((i) => i.id === itemId);
    const prefix = (item?.name.match(/[A-Za-z]+/g) || ["ITM"])[0].slice(0, 3).toUpperCase();
    const code = `${prefix}-${qty}${(item?.unit || "").toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
    setBarcodes((b) => [{ id: `local-${Date.now()}`, code, itemId, itemName: item?.name, unit: item?.unit, qty, createdAt: new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"}) }, ...b]);
  };

  const onScanBarcode = async (code) => {
    const label = barcodes.find((b) => b.code.toLowerCase() === code.toLowerCase());
    if (!label) return { ok: false, message: `No label found for code "${code}".` };
    const now = new Date();
    const timeStr = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const item = inventory.find((i) => i.id === label.itemId);
    const newQty = Math.max(0, (item?.qty || 0) - label.qty);
    setInventory((inv) => inv.map((i) => i.id === label.itemId ? { ...i, qty: newQty } : i));
    setLedger((l) => [{ id: `local-${Date.now()}`, itemName: label.itemName, type: "used", qty: label.qty, note: `Scanned barcode ${label.code} — taken from cold store`, date: now.toLocaleDateString("en-US",{month:"short",day:"numeric"}), time: timeStr }, ...l]);
    try { await apiPost("/save/inventory", { id: label.itemId, qty: newQty }); setServerOnline(true); } catch { setServerOnline(false); }
    return { ok: true, message: `Logged: ${label.itemName} — ${label.qty}${label.unit} taken at ${timeStr}.` };
  };

  const onSendOrder = async ({ table, items, orderType, phone, name }) => {
    const res = await apiPost("/save/new-order", { table, items, orderType: orderType || "dinein", phone, name });
    const order = withUrgency(res.order);
    setOrderQueue((q) => [...q, order]);
    return order;
  };

const onBookTable = async ({ date, timeSlot, partySize, table, name, phone }) => {
  const res = await apiPost("/save/new-reservation", { date, timeSlot, partySize, table, name, phone });
  setReservations((r) => [res.reservation, ...r]);
  return res.reservation;
};

  const onAssignTable = async (table, servantId, servantName) => {
    const previous = tableAssignments;
    setTableAssignments((a) => [...a.filter((x) => x.table !== table), { table, servantId, servantName, assignedAt: Date.now() }]);
    try {
      await apiPost("/save/assign-table", { table, servantId, servantName });
      flashSaved();
    } catch (e) {
      setTableAssignments(previous);
      setSaveError(e.message || String(e));
    }
  };

  const onUnassignTable = async (table) => {
    const previous = tableAssignments;
    setTableAssignments((a) => a.filter((x) => x.table !== table));
    try {
      await apiPost("/save/unassign-table", { table });
      flashSaved();
    } catch (e) {
      setTableAssignments(previous);
      setSaveError(e.message || String(e));
    }
  };

  const onAdvanceOrder = async (id) => {
    const res = await apiPost("/save/advance-order", { id });
    const order = withUrgency(res.order);
    setOrderQueue((q) => q.map((o) => o.id === id ? order : o));
  };

  if (view === "store") {
    return (
      <ThemeProvider theme={theme}><CssBaseline />
        <Storefront shopInfo={shopInfo} menu={shownMenu} combos={combos} banners={banners} aboutCards={aboutCards} orderQueue={orderQueue} onSendOrder={onSendOrder} reservations={reservations} onStaffLogin={() => setView("login")} onOpenReserve={() => setReserveOpen(true)} customersForLoyalty={customers} serverOnline={serverOnline} loyaltyRules={loyaltyRules} activeOffers={activeOffers}  />
        <Dialog open={reserveOpen} onClose={(e, reason) => { if (reason !== "backdropClick") setReserveOpen(false); }} maxWidth="100px"  PaperProps={{ sx: { borderRadius: 4, position: "relative" } }}>
          <IconButton onClick={()=>setReserveOpen(false)} sx={{ position: "absolute", top: 10, right: 10, zIndex: 2, bgcolor: alpha(PALETTE.ink, 0.06) }}><Close sx={{ fontSize: 18 }} /></IconButton>
          <DialogContent sx={{ bgcolor: PALETTE.bg }}><ReservationAgent reservations={reservations} onBookTable={onBookTable} /></DialogContent>
        </Dialog>
      </ThemeProvider>
    );
  }
  if (view === "login") {
    return <ThemeProvider theme={theme}><CssBaseline /><LoginScreen onLogin={handleLogin} onBackToShop={() => setView("store")} /></ThemeProvider>;
  }

  const tabs = TABS_BY_ROLE[user.role];
  const render = () => {
    switch (active) {
      case "hub": return <UnifiedHub menu={menu} onNavigate={setActive} reservations={reservations} />;
      case "sales": return <SalesAnalytics menu={menu} customers={customers} onNavigate={setActive}  comboSuggestions={comboSuggestions} />;
      case "loyalty": return (
  <LoyaltyOffersHub
    customers={customers}
    loyaltyRules={loyaltyRules}
    onChangeLoyaltyRules={setLoyaltyRules}
    savedOffers={savedOffers}
    onSaveOffer={saveOffer}
    onApplyOffer={applyOffer}
    onDeleteOffer={deleteOffer}
    activeOffers={activeOffers}
    onUpdateActiveOfferBlockValue={updateActiveOfferBlockValue}
    onRemoveActiveOffer={removeActiveOffer}
  />
);
      case "inventory": return <InventoryHub items={inventory} ledger={ledger} wastageLog={wastageLog} menu={menu} barcodes={barcodes} onLogMovement={onLogMovement} onLogWastage={onLogWastage} onCreateBarcode={onCreateBarcode} onScanBarcode={onScanBarcode} onSetExpiryDate={onSetExpiryDate} />;
      case "profit": return <ProfitIntelligence menu={menu} canEdit={user.role === "Owner"} onAddProduct={onAddProduct} onUpdateProduct={onUpdateProduct} onDeleteProduct={onDeleteProduct} />;
      case "owner": return <OwnerControls shopInfo={shopInfo} onUpdateShopInfo={onUpdateShopInfo} menu={menu} onAddProduct={onAddProduct} onUpdateProduct={onUpdateProduct} onDeleteProduct={onDeleteProduct} banners={banners} onSaveBanners={onSaveBanners} aboutCards={aboutCards} onSaveAboutCards={onSaveAboutCards} />;
      case "customers": return <CustomerLookup customers={customers} />;
      case "reservations": return <ReservationAgent reservations={reservations} onBookTable={onBookTable} />;
      case "tables": return <TableAssignments tableAssignments={tableAssignments} onAssignTable={onAssignTable} onUnassignTable={onUnassignTable} orderQueue={orderQueue} reservations={reservations} customers={customers} />;
      case "myTables": return <MyTables user={user} tableAssignments={tableAssignments} orderQueue={orderQueue} onAdvanceOrder={onAdvanceOrder} reservations={reservations} />;
      case "reports": return <Reports inventory={inventory} wastageLog={wastageLog} />;
      case "staff": return <StaffDirectory customers={customers} />;
      case "orders": return <OrderQueueView queue={orderQueue} onAdvanceOrder={onAdvanceOrder} eyebrow="Orders Overview" title="All Orders — Live Status" />;
      case "kitchen": return <KitchenQueue menu={menu} queue={orderQueue} onAdvanceOrder={onAdvanceOrder} inventory={inventory} ledger={ledger} wastageLog={wastageLog} barcodes={barcodes} onLogMovement={onLogMovement} onLogWastage={onLogWastage} onCreateBarcode={onCreateBarcode} onScanBarcode={onScanBarcode} onSetExpiryDate={onSetExpiryDate} />;
      case "order": return <OrderTaking menu={menu} customers={customers} queue={orderQueue} onSendOrder={onSendOrder} reservations={reservations} onBookTable={onBookTable} />;
      default: return null;
    }
  };

  return (
    <ThemeProvider theme={theme}><CssBaseline />
      <Box sx={{ minHeight: "100vh", bgcolor: PALETTE.bg }}>
        <DashboardTopBar  user={user} onLogout={handleLogout} onViewShop={()=>setView("store")} tabs={tabs} active={active}
          setActive={setActive} serverOnline={serverOnline} />
        {!serverOnline && !loading && (
          <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1400, bgcolor: alpha(PALETTE.alert, 0.95), py: 1, textAlign: "center" }}>
            <Typography sx={{ fontSize: 12.5, color: "#fff", fontWeight: 600 }}>
              Can't reach data-server.cjs — showing local demo data only, and nothing will actually save for other visitors. Check it's running and DATA_SERVER_URL near the top of this file matches its address.
            </Typography>
          </Box>
        )}
        <Snackbar open={Boolean(saveError)} autoHideDuration={6000} onClose={()=>setSaveError("")} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
          <Alert severity="error" variant="filled" onClose={()=>setSaveError("")}>{saveError}</Alert>
        </Snackbar>
        <Container maxWidth="xl" sx={{ py: 4 }}>{render()}</Container>
        <Box sx={{ textAlign: "center", py: 3 }}><Typography sx={{ fontSize: 11, color: PALETTE.inkDim }}>Lindey's Prime Steakhouse · AI Operations Hub — Prototype Demo · Minnesota</Typography></Box>
        {/* <FloatingAIConcierge onOpenReserve={() => setActive("reservations")} menu={menu} customers={customers} inventory={inventory} wastageLog={wastageLog} ledger={ledger} /> */}
       <FloatingAIConcierge
  onOpenReserve={() => setActive("reservations")}
  menu={menu}
  customers={customers}
  inventory={inventory}
  wastageLog={wastageLog}
  ledger={ledger}
  loyaltyRules={loyaltyRules}   // <-- add this line
/>
        <Snackbar open={savedFlash} autoHideDuration={2000} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert severity="success" variant="filled" sx={{ bgcolor: PALETTE.sage }}>Saved — live for every visitor.</Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}